import { extractMarkdownLinkSpans, notesToMarkdown, stripMarkdown } from "@littlebird/markdown-utils";
import { format } from "date-fns/format";
import { parseISO } from "date-fns/parseISO";
import { z } from "zod";
import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { RRule, rrulestr } from "rrule";
import { startOfDay } from "date-fns/startOfDay";
import { subDays } from "date-fns/subDays";
import { subMonths } from "date-fns/subMonths";
import { subWeeks } from "date-fns/subWeeks";
//#region src/action-card/action-card-url.ts
const ACTION_CARD_PATHNAME = /^\/action-cards\/(\d+)$/;
const APP_ORIGIN$1 = "https://app.littlebird.ai";
const APP_HOST$1 = new URL(APP_ORIGIN$1).host;
/** The agent writes these relative, so a bare path resolves against our own origin. */
function parseActionCardUrl(href) {
	try {
		const url = new URL(href, APP_ORIGIN$1);
		if (url.protocol !== "https:" || url.host !== APP_HOST$1) return { kind: "none" };
		const match = url.pathname.match(ACTION_CARD_PATHNAME);
		if (!match) return { kind: "none" };
		return {
			kind: "actionCard",
			localId: Number(match[1])
		};
	} catch {
		return { kind: "none" };
	}
}
/** Reads the same link tokens the renderer does, so a url it won't render can't hide a card. */
function findLinkedActionCardIds(markdown) {
	const localIds = /* @__PURE__ */ new Set();
	for (const { href } of extractMarkdownLinkSpans(markdown)) {
		const parsed = parseActionCardUrl(href);
		if (parsed.kind === "actionCard") localIds.add(parsed.localId);
	}
	return localIds;
}
//#endregion
//#region src/analytics-events.ts
let AnalyticsEvents = /* @__PURE__ */ function(AnalyticsEvents) {
	AnalyticsEvents["BOOTUP_SCREEN_OPENED"] = "bootup_screen_opened";
	AnalyticsEvents["BOOTUP_NAVIGATE_TO_LOGIN"] = "bootup_navigate_to_login";
	AnalyticsEvents["LOGIN_SCREEN_OPENED"] = "login_screen_opened";
	AnalyticsEvents["LOGIN_EMAIL_ENTERED"] = "login_email_entered";
	AnalyticsEvents["LOGIN_EMAIL_SUBMITTED"] = "login_email_submitted";
	AnalyticsEvents["AUTH0_CODE_SENT"] = "auth0_code_sent";
	AnalyticsEvents["LOGIN_EMAIL_ERROR"] = "login_email_error";
	AnalyticsEvents["LOGIN_CODE_SCREEN_VIEWED"] = "login_code_screen_viewed";
	AnalyticsEvents["LOGIN_CODE_ENTERED"] = "login_code_entered";
	AnalyticsEvents["LOGIN_CODE_SUBMITTED"] = "login_code_submitted";
	AnalyticsEvents["AUTH0_CODE_VALID"] = "auth0_code_valid";
	AnalyticsEvents["USER_LOGGED_IN"] = "user_logged_in";
	AnalyticsEvents["USER_AUTHENTICATED"] = "user_authenticated";
	AnalyticsEvents["ACCEPTED_TERMS_AND_CONDITIONS"] = "accepted_terms_and_conditions";
	AnalyticsEvents["RESEND_LOGIN_CODE"] = "resend_login_code";
	AnalyticsEvents["LOGIN_SNIPER_LINK_CLICKED"] = "login_sniper_link_clicked";
	AnalyticsEvents["ONBOARDING_STARTED"] = "onboarding_started";
	AnalyticsEvents["ONBOARDING_COMPLETED"] = "onboarding_completed";
	AnalyticsEvents["ONBOARDING_SCREEN_1_COMPLETED"] = "onboarding_screen_1_completed";
	AnalyticsEvents["ONBOARDING_SCREEN_2_COMPLETED"] = "onboarding_screen_2_completed";
	AnalyticsEvents["ONBOARDING_SCREEN_3_COMPLETED"] = "onboarding_screen_3_completed";
	AnalyticsEvents["ONBOARDING_SCREEN_SOURCE_COMPLETED"] = "onboarding_screen_source_completed";
	AnalyticsEvents["ONBOARDING_SCREEN_4_COMPLETED"] = "onboarding_screen_4_completed";
	AnalyticsEvents["ONBOARDING_SCREEN_DATA_SHARING_COMPLETED"] = "onboarding_screen_data_sharing_completed";
	AnalyticsEvents["ONBOARDING_SCREEN_5_COMPLETED"] = "onboarding_screen_5_completed";
	AnalyticsEvents["PERSONALIZATION_SCREEN_VIEWED"] = "personalization_screen_viewed";
	AnalyticsEvents["NAME_ENTERED"] = "name_entered";
	AnalyticsEvents["PERSONALIZATION_COMPLETED"] = "personalization_completed";
	AnalyticsEvents["USER_PROFILE_UPDATED"] = "user_profile_updated";
	AnalyticsEvents["PERMISSIONS_SCREEN_VIEWED"] = "permissions_screen_viewed";
	AnalyticsEvents["PERMISSIONS_REQUESTED"] = "permissions_requested";
	AnalyticsEvents["PERMISSIONS_GRANTED"] = "permissions_granted";
	AnalyticsEvents["CONTEXT_COLLECTION_STARTED"] = "context_collection_started";
	AnalyticsEvents["CONTEXT_COLLECTION_RESUMED"] = "context_collection_resumed";
	AnalyticsEvents["CONTEXT_COLLECTION_STOPPED"] = "context_collection_stopped";
	AnalyticsEvents["CONTEXT_COLLECTION_PAUSED"] = "context_collection_paused";
	AnalyticsEvents["CONTEXT_COLLECTION_DISABLED"] = "context_collection_disabled";
	AnalyticsEvents["CONTEXT_COLLECTION_START_FAILED"] = "context_collection_start_failed";
	AnalyticsEvents["CONTEXT_COLLECTION_STOPPED_FAILED"] = "context_collection_stopped_failed";
	AnalyticsEvents["ONBOARDING_CONTEXT_COLLECTION_TIMER_EXPIRED"] = "onboarding_context_collection_timer_expired";
	AnalyticsEvents["ONBOARDING_USER_NAVIGATED_AWAY_DURING_DEMO"] = "onboarding_user_navigated_away_during_demo";
	AnalyticsEvents["ONBOARDING_DEMO_SKIP_CLICKED"] = "onboarding_demo_skip_clicked";
	AnalyticsEvents["ONBOARDING_DEMO_SKIP_CONFIRMED"] = "onboarding_demo_skip_confirmed";
	AnalyticsEvents["ONBOARDING_DEMO_SKIP_CANCELLED"] = "onboarding_demo_skip_cancelled";
	AnalyticsEvents["WEB_ONBOARDING_STARTED"] = "web_onboarding_started";
	AnalyticsEvents["WEB_ONBOARDING_CHOICE_CLICKED"] = "web_onboarding_choice_clicked";
	AnalyticsEvents["WEB_ONBOARDING_COMPLETED"] = "web_onboarding_completed";
	AnalyticsEvents["ONBOARDING_START_VIEWED"] = "onboarding_start_viewed";
	AnalyticsEvents["ONBOARDING_USER_DETAILS_VIEWED"] = "onboarding_user_details_viewed";
	AnalyticsEvents["ONBOARDING_ACCESSIBILITY_VIEWED"] = "onboarding_accessibility_viewed";
	AnalyticsEvents["ONBOARDING_APP_CONTROLS_VIEWED"] = "onboarding_app_controls_viewed";
	AnalyticsEvents["ONBOARDING_APP_PICKER_VIEWED"] = "onboarding_app_picker_viewed";
	AnalyticsEvents["ONBOARDING_MEETING_NOTES_VIEWED"] = "onboarding_meeting_notes_viewed";
	AnalyticsEvents["ONBOARDING_INTEGRATIONS_VIEWED"] = "onboarding_integrations_viewed";
	AnalyticsEvents["ONBOARDING_HUMMINGBIRD_VIEWED"] = "onboarding_hummingbird_viewed";
	AnalyticsEvents["ONBOARDING_DATA_SHARING_VIEWED"] = "onboarding_data_sharing_viewed";
	AnalyticsEvents["ONBOARDING_CONTEXT_LEARNING_VIEWED"] = "onboarding_context_learning_viewed";
	AnalyticsEvents["ONBOARDING_DONE_VIEWED"] = "onboarding_done_viewed";
	AnalyticsEvents["ONBOARDING_START_COMPLETED"] = "onboarding_start_completed";
	AnalyticsEvents["ONBOARDING_USER_DETAILS_COMPLETED"] = "onboarding_user_details_completed";
	AnalyticsEvents["ONBOARDING_ACCESSIBILITY_COMPLETED"] = "onboarding_accessibility_completed";
	AnalyticsEvents["ONBOARDING_APP_CONTROLS_COMPLETED"] = "onboarding_app_controls_completed";
	AnalyticsEvents["ONBOARDING_APP_PICKER_COMPLETED"] = "onboarding_app_picker_completed";
	AnalyticsEvents["ONBOARDING_MEETING_NOTES_COMPLETED"] = "onboarding_meeting_notes_completed";
	AnalyticsEvents["ONBOARDING_INTEGRATIONS_COMPLETED"] = "onboarding_integrations_completed";
	AnalyticsEvents["ONBOARDING_HUMMINGBIRD_COMPLETED"] = "onboarding_hummingbird_completed";
	AnalyticsEvents["ONBOARDING_DATA_SHARING_STEP_COMPLETED"] = "onboarding_data_sharing_step_completed";
	AnalyticsEvents["ONBOARDING_CONTEXT_LEARNING_COMPLETED"] = "onboarding_context_learning_completed";
	AnalyticsEvents["ONBOARDING_MEETING_NOTES_SKIPPED"] = "onboarding_meeting_notes_skipped";
	AnalyticsEvents["ONBOARDING_INTEGRATIONS_SKIPPED"] = "onboarding_integrations_skipped";
	AnalyticsEvents["ONBOARDING_HUMMINGBIRD_SKIPPED"] = "onboarding_hummingbird_skipped";
	AnalyticsEvents["ONBOARDING_ACCESSIBILITY_PERMISSION_REQUESTED"] = "onboarding_accessibility_permission_requested";
	AnalyticsEvents["ONBOARDING_ACCESSIBILITY_PERMISSION_GRANTED"] = "onboarding_accessibility_permission_granted";
	AnalyticsEvents["ONBOARDING_MIC_PERMISSION_REQUESTED"] = "onboarding_mic_permission_requested";
	AnalyticsEvents["ONBOARDING_MIC_PERMISSION_GRANTED"] = "onboarding_mic_permission_granted";
	AnalyticsEvents["ACCESSIBILITY_PERMISSION_REQUESTED"] = "accessibility_permission_requested";
	AnalyticsEvents["ACCESSIBILITY_PERMISSION_GRANTED"] = "accessibility_permission_granted";
	AnalyticsEvents["ONBOARDING_GOOGLE_CALENDAR_CONNECT_CLICKED"] = "onboarding_google_calendar_connect_clicked";
	AnalyticsEvents["ONBOARDING_GOOGLE_CALENDAR_CONNECTED"] = "onboarding_google_calendar_connected";
	AnalyticsEvents["ONBOARDING_GMAIL_CONNECT_CLICKED"] = "onboarding_gmail_connect_clicked";
	AnalyticsEvents["ONBOARDING_GMAIL_CONNECTED"] = "onboarding_gmail_connected";
	AnalyticsEvents["GOOGLE_CALENDAR_CONNECTED"] = "google_calendar_connected";
	AnalyticsEvents["GMAIL_CONNECTED"] = "gmail_connected";
	AnalyticsEvents["GOOGLE_CONSENT_OFFERED"] = "google_consent_offered";
	AnalyticsEvents["GOOGLE_CONSENT_STARTED"] = "google_consent_started";
	AnalyticsEvents["GOOGLE_CONSENT_RESULT"] = "google_consent_result";
	AnalyticsEvents["EMAIL_EDIT_VIA_INTEGRATION"] = "email_edit_via_integration";
	AnalyticsEvents["EMAIL_SENT_VIA_INTEGRATION"] = "email_sent_via_integration";
	AnalyticsEvents["ONBOARDING_HUMMINGBIRD_ENABLED"] = "onboarding_hummingbird_enabled";
	AnalyticsEvents["ONBOARDING_HUMMINGBIRD_DISABLED"] = "onboarding_hummingbird_disabled";
	AnalyticsEvents["ONBOARDING_HUMMINGBIRD_SHORTCUT_CHANGED"] = "onboarding_hummingbird_shortcut_changed";
	AnalyticsEvents["ONBOARDING_DATA_SHARING_TOGGLED"] = "onboarding_data_sharing_toggled";
	AnalyticsEvents["ONBOARDING_CONTEXT_LEARNING_TIMER_EXPIRED"] = "onboarding_context_learning_timer_expired";
	AnalyticsEvents["ONBOARDING_USER_NAVIGATED_AWAY"] = "onboarding_user_navigated_away";
	AnalyticsEvents["ONBOARDING_CONTEXT_LEARNING_SKIP_CLICKED"] = "onboarding_context_learning_skip_clicked";
	AnalyticsEvents["ONBOARDING_CONTEXT_LEARNING_SKIP_CONFIRMED"] = "onboarding_context_learning_skip_confirmed";
	AnalyticsEvents["ONBOARDING_CONTEXT_LEARNING_SKIP_CANCELLED"] = "onboarding_context_learning_skip_cancelled";
	AnalyticsEvents["ONBOARDING_CONTEXT_FIRST_RUN_STARTED"] = "onboarding_context_first_run_started";
	AnalyticsEvents["ONBOARDING_CONTEXT_FIRST_RUN_SUMMARY_READY"] = "onboarding_context_first_run_summary_ready";
	AnalyticsEvents["ONBOARDING_CONTEXT_FIRST_RUN_SUMMARY_VIEWED"] = "onboarding_context_first_run_summary_viewed";
	AnalyticsEvents["ONBOARDING_CONTEXT_FIRST_RUN_DISMISSED"] = "onboarding_context_first_run_dismissed";
	AnalyticsEvents["ONBOARDING_CONTEXT_FIRST_RUN_GAVE_UP"] = "onboarding_context_first_run_gave_up";
	AnalyticsEvents["UPDATE_CHECK_STARTED"] = "update_check_started";
	AnalyticsEvents["UPDATE_AVAILABLE"] = "update_available";
	AnalyticsEvents["UPDATE_DOWNLOAD_STARTED"] = "update_download_started";
	AnalyticsEvents["UPDATE_DOWNLOADED"] = "update_downloaded";
	AnalyticsEvents["UPDATE_INSTALL_STARTED"] = "update_install_started";
	AnalyticsEvents["UPDATE_INSTALLED"] = "update_installed";
	AnalyticsEvents["UPDATE_DISMISSED"] = "update_dismissed";
	AnalyticsEvents["UPDATE_ERROR"] = "update_error";
	AnalyticsEvents["INSTALL_REPAIR_PROMPT_SHOWN"] = "install_repair_prompt_shown";
	AnalyticsEvents["INSTALL_REPAIR_ACCEPTED"] = "install_repair_accepted";
	AnalyticsEvents["INSTALL_REPAIR_DISMISSED"] = "install_repair_dismissed";
	AnalyticsEvents["INSTALL_REPAIR_DOWNLOAD_CLICKED"] = "install_repair_download_clicked";
	AnalyticsEvents["CHAT_OPENED"] = "chat_opened";
	AnalyticsEvents["USER_CHAT_EVENT"] = "user_chat_event";
	AnalyticsEvents["MESSAGE_INPUT_STARTED"] = "message_input_started";
	AnalyticsEvents["APP_MENTION_ADDED"] = "app_mention_added";
	AnalyticsEvents["SKILL_MENTION_ADDED"] = "skill_mention_added";
	AnalyticsEvents["MESSAGE_SENT"] = "message_sent";
	AnalyticsEvents["MESSAGE_COMPLETED"] = "message_completed";
	AnalyticsEvents["MESSAGE_ERROR"] = "message_error";
	AnalyticsEvents["MESSAGE_RATED"] = "message_rated";
	AnalyticsEvents["MESSAGE_FEEDBACK"] = "message_feedback";
	AnalyticsEvents["RECOMMENDATION_REGENERATED"] = "recommendation_generated";
	AnalyticsEvents["RECOMMENDATION_SHOWN"] = "recommendation_shown";
	AnalyticsEvents["RECOMMENDATION_CLICKED"] = "recommendation_clicked";
	AnalyticsEvents["RECOMMENDATION_SET_AS_ACTIVE_APP_INPUT"] = "recommendation_set_as_active_app_input";
	AnalyticsEvents["LITTLEBIRD_CHAT_RESPONSE"] = "littlebird_chat_response";
	AnalyticsEvents["CHAT_STOP_GENERATION"] = "chat_stop_generation";
	AnalyticsEvents["CHAT_STREAM_INTERRUPTED"] = "chat_stream_interrupted";
	AnalyticsEvents["CHAT_PROMPT_LIBRARY_OPENED"] = "chat_prompt_library_opened";
	AnalyticsEvents["CHAT_PROMPT_LIBRARY_PROMPT_SELECTED"] = "chat_prompt_library_prompt_selected";
	AnalyticsEvents["CHAT_COPYABLE_PARSE_FAILURE"] = "chat_copyable_parse_failure";
	AnalyticsEvents["CHAT_COMPOSER_ACTION_PRESS_STARTED"] = "chat_composer_action_press_started";
	AnalyticsEvents["CHAT_SEND_TAP_IGNORED"] = "chat_send_tap_ignored";
	AnalyticsEvents["CHAT_STOP_TAP_NO_THREAD"] = "chat_stop_tap_no_thread";
	AnalyticsEvents["CHAT_MODEL_CHANGED"] = "chat_model_changed";
	AnalyticsEvents["CHAT_TITLE_RENAMED"] = "chat_title_renamed";
	AnalyticsEvents["CHAT_STARRED"] = "chat_starred";
	AnalyticsEvents["CHAT_ARCHIVED"] = "chat_archived";
	AnalyticsEvents["CHAT_UNARCHIVED"] = "chat_unarchived";
	AnalyticsEvents["CHAT_SHARED"] = "chat_shared";
	AnalyticsEvents["CHAT_UNSHARED"] = "chat_unshared";
	AnalyticsEvents["CHAT_DELETED"] = "chat_deleted";
	AnalyticsEvents["MESSAGE_LB_COPY_CLICKED"] = "message_lb_copy_clicked";
	AnalyticsEvents["MESSAGE_LB_COPY_IMAGE_CLICKED"] = "message_lb_copy_image_clicked";
	AnalyticsEvents["MESSAGE_USER_COPY_CLICKED"] = "message_user_copy_clicked";
	AnalyticsEvents["MESSAGE_USER_EDITED"] = "message_user_edited";
	AnalyticsEvents["MESSAGE_USER_VERSION_SWITCHED"] = "message_user_version_switched";
	AnalyticsEvents["MESSAGE_LB_SPEAK"] = "message_lb_speak";
	AnalyticsEvents["MESSAGE_FILE_ATTACHED"] = "message_file_attached";
	AnalyticsEvents["MESSAGE_USER_VOICE"] = "message_user_voice";
	AnalyticsEvents["VOICE_MODE_STARTED"] = "voice_mode_started";
	AnalyticsEvents["VOICE_MODE_STOPPED"] = "voice_mode_stopped";
	AnalyticsEvents["CHAT_PAGE_VIEWED"] = "chat_page_viewed";
	AnalyticsEvents["CHAT_HOME_VIEWED"] = "chat_home_viewed";
	AnalyticsEvents["ALL_CHATS_CHAT_OPENED"] = "all_chats_chat_opened";
	AnalyticsEvents["ALL_CHATS_FILTER_CHANGED"] = "all_chats_filter_changed";
	AnalyticsEvents["ALL_CHATS_SEE_ALL_CLICKED"] = "all_chats_see_all_clicked";
	AnalyticsEvents["CONTEXTUAL_CHAT"] = "contextual_chat";
	AnalyticsEvents["GENERIC_CHAT"] = "generic_chat";
	AnalyticsEvents["WEB_SEARCH_CHAT"] = "web_search_chat";
	AnalyticsEvents["MEETING_DETECTED"] = "meeting_detected";
	AnalyticsEvents["MEETING_NAME_UPDATE"] = "meeting_name_update";
	AnalyticsEvents["MEETING_RECORDING_STARTED"] = "meeting_recording_started";
	AnalyticsEvents["MEETING_RECORDING_STOPPED"] = "meeting_recording_stopped";
	AnalyticsEvents["MEETING_RESUME_NOTIFICATION_SHOWN"] = "meeting_resume_notification_shown";
	AnalyticsEvents["MEETING_RESUME_NOTIFICATION_RESOLVED"] = "meeting_resume_notification_resolved";
	AnalyticsEvents["MEETING_TRANSCRIPTION_REQUESTED"] = "meeting_transcription_requested";
	AnalyticsEvents["MEETING_TRANSCRIPTION_STARTED"] = "meeting_transcription_started";
	AnalyticsEvents["MEETING_TRANSCRIPTION_COMPLETED"] = "meeting_transcription_completed";
	AnalyticsEvents["MEETING_SUMMARY_STARTED"] = "meeting_summary_started";
	AnalyticsEvents["MEETING_SUMMARY_COMPLETED"] = "meeting_summary_completed";
	AnalyticsEvents["MEETING_SUMMARY_VIEWED"] = "meeting_summary_viewed";
	AnalyticsEvents["MEETING_SUMMARY_EXPORTED"] = "meeting_summary_exported";
	AnalyticsEvents["MEETING_VIEWED"] = "meeting_viewed";
	AnalyticsEvents["MEETING_NOTE_VIEWED"] = "meeting_note_viewed";
	AnalyticsEvents["MEETING_NOTE_EDITED"] = "meeting_note_edited";
	AnalyticsEvents["MEETING_TRANSCRIPT_VIEWED"] = "meeting_transcript_viewed";
	AnalyticsEvents["MEETING_ASSIST_VIEWED"] = "meeting_assist_viewed";
	AnalyticsEvents["MEETING_ASSIST_RATED"] = "meeting_assist_rated";
	AnalyticsEvents["MEETING_ASSIST_POPUP_SHOWN"] = "meeting_assist_popup_shown";
	AnalyticsEvents["MEETING_ASSIST_POPUP_RESOLVED"] = "meeting_assist_popup_resolved";
	AnalyticsEvents["MEETING_ASSIST_SETTING_CHANGED"] = "meeting_assist_setting_changed";
	AnalyticsEvents["MEETING_INTENT_PROMPT_SHOWN"] = "meeting_intent_prompt_shown";
	AnalyticsEvents["MEETING_INTENT_PROMPT_RESOLVED"] = "meeting_intent_prompt_resolved";
	AnalyticsEvents["APP_CONTEXT_COLLECTED"] = "app_context_collected";
	AnalyticsEvents["BROWSER_CONTEXT_COLLECTED"] = "browser_context_collected";
	AnalyticsEvents["CONTEXT_BATCH_SENT"] = "context_batch_sent";
	AnalyticsEvents["CONTEXT_BATCH_RECEIVED"] = "context_batch_received";
	AnalyticsEvents["CONTEXT_PROCESSING_STARTED"] = "context_processing_started";
	AnalyticsEvents["CONTEXT_EMBEDDINGS_GENERATED"] = "context_embeddings_generated";
	AnalyticsEvents["CONTEXT_PROCESSING_COMPLETED"] = "context_processing_completed";
	AnalyticsEvents["CONTEXT_COLLECTION_ERROR"] = "context_collection_error";
	AnalyticsEvents["CONTEXT_PROCESSING_ERROR"] = "context_processing_error";
	AnalyticsEvents["REPORT_TEMPLATE_UPDATED"] = "report_template_updated";
	AnalyticsEvents["REPORT_TEMPLATE_CREATED"] = "report_template_created";
	AnalyticsEvents["REPORT_TEMPLATE_INITIATED"] = "report_template_initiated";
	AnalyticsEvents["REPORT_TO_CHAT"] = "report_to_chat";
	AnalyticsEvents["REPORT_REVEAL_CHAT"] = "report_reveal_chat";
	AnalyticsEvents["ROUTINE_EVENT"] = "routine_event";
	AnalyticsEvents["ROUTINE_LIBRARY_EVENT"] = "routine_library_event";
	AnalyticsEvents["ROUTINE_NEW_BUTTON_CLICKED"] = "routine_new_button_clicked";
	AnalyticsEvents["ROUTINE_LIST_VIEWED"] = "routine_list_viewed";
	AnalyticsEvents["ROUTINE_DELETED"] = "routine_deleted";
	AnalyticsEvents["ROUTINE_PAGE_VIEWED"] = "routine_page_viewed";
	AnalyticsEvents["ROUTINE_REPORT_VIEWED"] = "routine_report_viewed";
	AnalyticsEvents["ROUTINE_REPORT_RATED"] = "routine_report_rated";
	AnalyticsEvents["ROUTINE_STARRED"] = "routine_starred";
	AnalyticsEvents["USER_TASK_EVENT"] = "user_task_event";
	AnalyticsEvents["TASK_STATUS_UPDATED"] = "task_status_updated";
	AnalyticsEvents["TASK_ADDED"] = "task_added";
	AnalyticsEvents["TASK_COMPLETED"] = "task_completed";
	AnalyticsEvents["TASK_TAGS_UPDATED"] = "task_tags_updated";
	AnalyticsEvents["SKIPPED_TASKS_ONBOARDING"] = "skipped_tasks_onboarding";
	AnalyticsEvents["TASK_DELETED"] = "task_deleted";
	AnalyticsEvents["TASK_REORDERED"] = "task_reordered";
	AnalyticsEvents["TASK_DUE_DATE_CHANGED"] = "task_due_date_changed";
	AnalyticsEvents["TASK_DUE_TIME_CHANGED"] = "task_due_time_changed";
	AnalyticsEvents["TASK_REMINDER_CHANGED"] = "task_reminder_changed";
	AnalyticsEvents["TASK_TAB_COMPLETED"] = "task_tab_completed";
	AnalyticsEvents["TASK_TAB_ARCHIVE"] = "task_tab_archive";
	AnalyticsEvents["TASK_TAB_ALL"] = "task_tab_all";
	AnalyticsEvents["JOURNAL_COMPLETED"] = "journal_completed";
	AnalyticsEvents["JOURNAL_OPENED"] = "journal_opened";
	AnalyticsEvents["JOURNAL_DELETED"] = "journal_deleted";
	AnalyticsEvents["JOURNAL_ONBOARDING_SKIPPED"] = "journal_onboarding_skipped";
	AnalyticsEvents["JOURNAL_ONBOARDING_COMPLETED"] = "journal_onboarding_completed";
	AnalyticsEvents["JOURNAL_LIST_VIEWED"] = "journal_list_viewed";
	AnalyticsEvents["PUSH_NOTIFICATION_CLICKED"] = "push_notification_clicked";
	AnalyticsEvents["FORCE_REDIRECT"] = "force_redirect";
	AnalyticsEvents["ERROR_OCCURRED"] = "error_occurred";
	AnalyticsEvents["PAGE_VIEWED"] = "$pageview";
	AnalyticsEvents["MAIN_WINDOW_FOCUSED"] = "main_window_focused";
	AnalyticsEvents["MAIN_WINDOW_BLURRED"] = "main_window_blurred";
	AnalyticsEvents["INVITE_EMAILS_SENT"] = "invite_emails_sent";
	AnalyticsEvents["REFERRAL_INITIATED"] = "referral_initiated";
	AnalyticsEvents["REFERRAL_EMAIL_INVITATION"] = "referral_email_invitation";
	AnalyticsEvents["REFERRAL_SHARE_LINK"] = "referral_share_link";
	AnalyticsEvents["REFERRAL_INVITE_SENT"] = "referral_invite_sent";
	AnalyticsEvents["REFERRAL_INVITE_COPIED"] = "referral_invite_copied";
	AnalyticsEvents["REFERRAL_CODE_COPIED"] = "referral_code_copied";
	AnalyticsEvents["REFERRAL_MENU_CLICKED"] = "referral_menu_clicked";
	AnalyticsEvents["REFERRAL_CODE_APPLIED"] = "referral_code_applied";
	AnalyticsEvents["REFERRAL_SHARE_BUTTON_CLICKED"] = "referral_share_button_clicked";
	AnalyticsEvents["REFERRAL_SHARE_BUTTON_FINISHED"] = "referral_share_button_finished";
	AnalyticsEvents["REFERRAL_CTA_VIEWED"] = "referral_cta_viewed";
	AnalyticsEvents["REFERRAL_CTA_CLICKED"] = "referral_cta_clicked";
	AnalyticsEvents["REFERRAL_CTA_DISMISSED"] = "referral_cta_dismissed";
	AnalyticsEvents["AI_WRAPPED_SIDEBAR_CLICKED"] = "ai_wrapped_sidebar_clicked";
	AnalyticsEvents["AI_WRAPPED_SIDEBAR_DISMISSED"] = "ai_wrapped_sidebar_dismissed";
	AnalyticsEvents["REFERRAL_ACTION_COMPLETED"] = "referral_action_completed";
	AnalyticsEvents["REFERRAL_PAGE_VIEWED"] = "referral_page_viewed";
	AnalyticsEvents["REFERRAL_CODE_ENTERED_ONBOARDING"] = "referral_code_entered_onboarding";
	AnalyticsEvents["REFERRAL_ONBOARDING_STEP_VIEWED"] = "referral_onboarding_step_viewed";
	AnalyticsEvents["REFERRAL_ONBOARDING_STEP_SKIPPED"] = "referral_onboarding_step_skipped";
	AnalyticsEvents["TEAM_CREATION_STARTED"] = "team_creation_started";
	AnalyticsEvents["TEAM_CHECKOUT_SUBMITTED"] = "team_checkout_submitted";
	AnalyticsEvents["TEAM_MEMBER_INVITED"] = "team_member_invited";
	AnalyticsEvents["TEAM_INVITE_LINK_COPIED"] = "team_invite_link_copied";
	AnalyticsEvents["TEAM_INVITE_ACCEPTED"] = "team_invite_accepted";
	AnalyticsEvents["TEAM_MEMBER_ROLE_CHANGED"] = "team_member_role_changed";
	AnalyticsEvents["TEAM_MEMBER_TIER_CHANGED"] = "team_member_tier_changed";
	AnalyticsEvents["TEAM_MEMBER_REMOVED"] = "team_member_removed";
	AnalyticsEvents["TEAM_BILLING_PORTAL_OPENED"] = "team_billing_portal_opened";
	AnalyticsEvents["TEAM_DELETED"] = "team_deleted";
	AnalyticsEvents["TEAM_DOMAIN_ADDED"] = "team_domain_added";
	AnalyticsEvents["TEAM_DOMAIN_REMOVED"] = "team_domain_removed";
	AnalyticsEvents["TEAM_DOMAIN_JOIN_ACCEPTED"] = "team_domain_join_accepted";
	AnalyticsEvents["RETENTION_DISCOUNT_APPLIED"] = "retention_discount_applied";
	AnalyticsEvents["CHAT_SETTING_PROMPT_UPDATED"] = "chat_setting_prompt_updated";
	AnalyticsEvents["TASK_SETTING_PROMPT_UPDATED"] = "task_setting_prompt_updated";
	AnalyticsEvents["JOURNAL_SETTING_PROMPT_UPDATED"] = "journal_setting_prompt_updated";
	AnalyticsEvents["MEETING_SETTING_PROMPT_UPDATED"] = "meeting_setting_prompt_updated";
	AnalyticsEvents["TODO_FILTER_RESET_TO_DEFAULTS"] = "todo_filter_reset_to_defaults";
	AnalyticsEvents["UPGRADE_PLAN_MENU_CLICKED"] = "upgrade_plan_menu_clicked";
	AnalyticsEvents["SUBSCRIPTION_FREEMIUM_MODAL_SHOWN"] = "subscription_freemium_modal_shown";
	AnalyticsEvents["SUBSCRIPTION_PRO_TRIAL_MODAL_SHOWN"] = "subscription_pro_trial_modal_shown";
	AnalyticsEvents["SUBSCRIPTION_BETA_MODAL_SHOWN"] = "subscription_beta_modal_shown";
	AnalyticsEvents["SUBSCRIPTION_UPGRADE_MODAL_SHOWN"] = "subscription_upgrade_modal_shown";
	AnalyticsEvents["SUBSCRIPTION_DOWNGRADE_MODAL_SHOWN"] = "subscription_downgrade_modal_shown";
	AnalyticsEvents["TESTIMONIALS_VIEWED"] = "testimonials_viewed";
	AnalyticsEvents["SUBSCRIPTION_PAGE_TRIAL_STARTED"] = "subscription_page_trial_started";
	AnalyticsEvents["CHECKOUT_RETURN_LANDED"] = "checkout_return_landed";
	AnalyticsEvents["LINK_FALLBACK_TO_WEB"] = "link_fallback_to_web";
	AnalyticsEvents["EXPERIMENT_USER_ASSIGNED"] = "experiment_user_assigned";
	AnalyticsEvents["CHAT_LIMIT_HIT"] = "chat_limit_hit";
	AnalyticsEvents["RESEARCH_LIMIT_HIT"] = "research_limit_hit";
	AnalyticsEvents["ROUTINE_LIMIT_HIT"] = "routine_limit_hit";
	AnalyticsEvents["MEETING_LIMIT_HIT"] = "meeting_limit_hit";
	AnalyticsEvents["CREDIT_PAYWALL_VIEWED"] = "credit_paywall_viewed";
	AnalyticsEvents["CREDIT_PURCHASE_STARTED"] = "credit_purchase_started";
	AnalyticsEvents["UPGRADE_BUTTON_CLICKED"] = "upgrade_button_clicked";
	AnalyticsEvents["USAGE_PACE_WARNING_SHOWN"] = "usage_pace_warning_shown";
	AnalyticsEvents["USAGE_WARNING_SHOWN"] = "usage_warning_shown";
	AnalyticsEvents["USAGE_WARNING_DISMISSED"] = "usage_warning_dismissed";
	AnalyticsEvents["MEETING_TITLE_RENAMED"] = "meeting_title_renamed";
	AnalyticsEvents["MEETING_RATED"] = "meeting_rated";
	AnalyticsEvents["MEETING_SUMMARY_COPIED"] = "meeting_summary_copied";
	AnalyticsEvents["MEETING_SEND_TO_ATTENDEES"] = "meeting_send_to_attendees";
	AnalyticsEvents["MEETING_NOTES_UNSUPPORTED_SHOWN"] = "meeting_notes_unsupported_shown";
	AnalyticsEvents["MEETING_SHARED"] = "meeting_shared";
	AnalyticsEvents["MEETING_UNSHARED"] = "meeting_unshared";
	AnalyticsEvents["MEETING_SUMMARISE"] = "meeting_summarise";
	AnalyticsEvents["MEETING_SUMMARY_GENERATE_SHOWN"] = "meeting_summary_generate_shown";
	AnalyticsEvents["MEETING_SUMMARY_GENERATE_CLICKED"] = "meeting_summary_generate_clicked";
	AnalyticsEvents["MEETING_DELETED"] = "meeting_deleted";
	AnalyticsEvents["MEETING_AUTO_RECORDING_BANNER_VIEWED"] = "meeting_auto_recording_banner_viewed";
	AnalyticsEvents["MEETING_AUTO_RECORDING_BANNER_CTA_CLICKED"] = "meeting_auto_recording_banner_cta_clicked";
	AnalyticsEvents["MEETING_AUTO_RECORDING_BANNER_DISMISSED"] = "meeting_auto_recording_banner_dismissed";
	AnalyticsEvents["MEETING_AUTO_STOP_SUMMARY_SETTING_CHANGED"] = "meeting_auto_stop_summary_setting_changed";
	AnalyticsEvents["MEETING_AUTO_STOP_SUMMARY_HANDLED"] = "meeting_auto_stop_summary_handled";
	AnalyticsEvents["MEETING_PREP_CLICKED"] = "meeting_prep_clicked";
	AnalyticsEvents["MEETING_PREP_COMPLETED"] = "meeting_prep_completed";
	AnalyticsEvents["MEETING_PREP_FAILED"] = "meeting_prep_failed";
	AnalyticsEvents["MEETING_PREP_REGENERATED"] = "meeting_prep_regenerated";
	AnalyticsEvents["MEETING_PREP_RATED"] = "meeting_prep_rated";
	AnalyticsEvents["MEETING_PREP_VIEWED_FROM_MENU"] = "meeting_prep_viewed_from_menu";
	AnalyticsEvents["CHAT_DOCKED"] = "chat_docked";
	AnalyticsEvents["CHAT_UNDOCKED"] = "chat_undocked";
	AnalyticsEvents["IMAGE_GENERATION_PROMPT_SUBMITTED"] = "image_generation_prompt_submitted";
	AnalyticsEvents["IMAGE_GENERATION_COMPLETED_CLIENT"] = "image_generation_completed_client";
	AnalyticsEvents["IMAGE_GENERATION_FAILED_CLIENT"] = "image_generation_failed_client";
	AnalyticsEvents["IMAGE_GENERATION_RESULT_VIEWED"] = "image_generation_result_viewed";
	AnalyticsEvents["IMAGE_GENERATION_RESULT_DOWNLOADED"] = "image_generation_result_downloaded";
	AnalyticsEvents["IMAGE_GENERATION_RESULT_SAVED"] = "image_generation_result_saved";
	AnalyticsEvents["IMAGE_GENERATION_RESULT_SHARED"] = "image_generation_result_shared";
	AnalyticsEvents["SPRITE_TAB_VIEWED"] = "sprite_tab_viewed";
	AnalyticsEvents["SPRITE_OPENED"] = "sprite_opened";
	AnalyticsEvents["SPRITE_CHAT_OPENED"] = "sprite_chat_opened";
	AnalyticsEvents["SPRITE_DELETED"] = "sprite_deleted";
	AnalyticsEvents["SPRITE_VISIBILITY_CHANGED"] = "sprite_visibility_changed";
	AnalyticsEvents["PROFILE_MENU_CLICKED"] = "profile_menu_clicked";
	AnalyticsEvents["HUMMINGBIRD_OPENED"] = "hummingbird_opened";
	AnalyticsEvents["HUMMINGBIRD_OPENED_FROM_MAIN_WINDOW"] = "hummingbird_opened_from_main_window";
	AnalyticsEvents["HUMMINGBIRD_SETTING_TOGGLED"] = "hummingbird_setting_toggled";
	AnalyticsEvents["HUMMINGBIRD_MEETING_CONTEXT_TOGGLED"] = "hummingbird_meeting_context_toggled";
	AnalyticsEvents["HUMMINGBIRD_VOICE_HOLD_RELEASED"] = "hummingbird_voice_hold_released";
	AnalyticsEvents["HUMMINGBIRD_VOICE_SHORTCUT_TOGGLED"] = "hummingbird_voice_shortcut_toggled";
	AnalyticsEvents["HUMMINGBIRD_VOICE_AUTO_SEND_TOGGLED"] = "hummingbird_voice_auto_send_toggled";
	AnalyticsEvents["HUMMINGBIRD_APP_CONTEXT_TOGGLED"] = "hummingbird_app_context_toggled";
	AnalyticsEvents["HUMMINGBIRD_FRONT_APP_CONTEXT_AUTO_INCLUDED"] = "hummingbird_front_app_context_auto_included";
	AnalyticsEvents["HUMMINGBIRD_FRONT_APP_CONTEXT_REMOVED"] = "hummingbird_front_app_context_removed";
	AnalyticsEvents["HUMMINGBIRD_FRONT_APP_CONTEXT_SETTING_TOGGLED"] = "hummingbird_front_app_context_setting_toggled";
	AnalyticsEvents["HUMMINGBIRD_TEXT_SELECTION_TOGGLED"] = "hummingbird_text_selection_toggled";
	AnalyticsEvents["HUMMINGBIRD_EXPANDED_TO_MAIN_WINDOW"] = "hummingbird_expanded_to_main_window";
	AnalyticsEvents["HUMMINGBIRD_TAB_SUGGESTION_USED"] = "hummingbird_tab_suggestion_used";
	AnalyticsEvents["HUMMINGBIRD_NEW_CHAT_CREATED"] = "hummingbird_new_chat_created";
	AnalyticsEvents["HUMMINGBIRD_SCREENSHOT_CAPTURED"] = "hummingbird_screenshot_captured";
	AnalyticsEvents["HUMMINGBIRD_ACTIONS_MENU_OPENED"] = "hummingbird_actions_menu_opened";
	AnalyticsEvents["HUMMINGBIRD_RECENT_CHATS_MENU_OPENED"] = "hummingbird_recent_chats_menu_opened";
	AnalyticsEvents["HUMMINGBIRD_COPY_RESPONSE"] = "hummingbird_copy_response";
	AnalyticsEvents["HUMMINGBIRD_PASTE_RESPONSE"] = "hummingbird_paste_response";
	AnalyticsEvents["HUMMINGBIRD_CONTEXT_CAPTURE_FAILED"] = "hummingbird_context_capture_failed";
	AnalyticsEvents["HUMMINGBIRD_SCREENSHOT_CAPTURE_FAILED"] = "hummingbird_screenshot_capture_failed";
	AnalyticsEvents["HUMMINGBIRD_SCREENSHOT_FALLBACK_HINT_SHOWN"] = "hummingbird_screenshot_fallback_hint_shown";
	AnalyticsEvents["HUMMINGBIRD_SCREENSHOT_FALLBACK_CAPTURED"] = "hummingbird_screenshot_fallback_captured";
	AnalyticsEvents["HUMMINGBIRD_SCREENSHOT_FALLBACK_SETTING_TOGGLED"] = "hummingbird_screenshot_fallback_setting_toggled";
	AnalyticsEvents["HUMMINGBIRD_CHAT_SELECTED"] = "hummingbird_chat_selected";
	AnalyticsEvents["HUMMINGBIRD_CHAT_REMOVED"] = "hummingbird_chat_removed";
	AnalyticsEvents["HUMMINGBIRD_REDOCKED"] = "hummingbird_redocked";
	AnalyticsEvents["HUMMINGBIRD_APP_MENTION_ADDED"] = "hummingbird_app_mention_added";
	AnalyticsEvents["HUMMINGBIRD_SKILL_MENTION_ADDED"] = "hummingbird_skill_mention_added";
	AnalyticsEvents["CONTEXT_CHAT_OPENED"] = "context_chat_opened";
	AnalyticsEvents["CONTEXT_CHAT_CLOSED"] = "context_chat_closed";
	AnalyticsEvents["CONTEXT_CHAT_EXPANDED_TO_MAIN"] = "context_chat_expanded_to_main";
	AnalyticsEvents["WHATS_NEW_SHOWN"] = "whats_new_shown";
	AnalyticsEvents["WHATS_NEW_PRIMARY_CTA_CLICKED"] = "whats_new_primary_cta_clicked";
	AnalyticsEvents["WHATS_NEW_SECONDARY_CTA_CLICKED"] = "whats_new_secondary_cta_clicked";
	AnalyticsEvents["WHATS_NEW_DISMISSED"] = "whats_new_dismissed";
	AnalyticsEvents["WELCOME_ITEM_CLICKED"] = "welcome_item_clicked";
	AnalyticsEvents["PROJECT_CREATED"] = "project_created";
	AnalyticsEvents["PROJECT_DELETED"] = "project_deleted";
	AnalyticsEvents["PROJECT_LEFT"] = "project_left";
	AnalyticsEvents["PROJECT_STARRED"] = "project_starred";
	AnalyticsEvents["PROJECT_ICON_CHANGED"] = "project_icon_changed";
	AnalyticsEvents["ASSIGNED_TO_PROJECT"] = "assigned_to_project";
	AnalyticsEvents["REMOVED_FROM_PROJECT"] = "removed_from_project";
	AnalyticsEvents["PROJECT_SUGGESTION_SHOWN"] = "project_suggestion_shown";
	AnalyticsEvents["PROJECT_SUGGESTION_ACCEPTED"] = "project_suggestion_accepted";
	AnalyticsEvents["PROJECT_SUGGESTION_DISMISSED"] = "project_suggestion_dismissed";
	AnalyticsEvents["PROJECT_FILES_TAB_CLICKED"] = "project_files_tab_clicked";
	AnalyticsEvents["PROJECT_INSTRUCTIONS_TAB_CLICKED"] = "project_instructions_tab_clicked";
	AnalyticsEvents["PROJECT_FILE_UPLOAD_CLICKED"] = "project_file_upload_clicked";
	AnalyticsEvents["PROJECT_FILE_DELETE_CLICKED"] = "project_file_delete_clicked";
	AnalyticsEvents["PROJECT_SHARE_COMPLETED"] = "project_share_completed";
	/**
	* @deprecated To be removed in future versions
	*/
	AnalyticsEvents["USER_ONBOARDING_EVENT"] = "user_onboarding_event";
	/**
	* @deprecated To be removed in future versions
	*/
	AnalyticsEvents["USER_ACTION_EVENT"] = "user_action_event";
	AnalyticsEvents["WIDGETS_COLLAPSE_TOGGLED"] = "widgets_collapse_toggled";
	AnalyticsEvents["WIDGETS_SCHEDULE_PREP_CLICKED"] = "widgets_schedule_prep_clicked";
	AnalyticsEvents["WIDGETS_SCHEDULE_JOIN_CLICKED"] = "widgets_schedule_join_clicked";
	AnalyticsEvents["MEETINGS_SIDEBAR_JOIN_CLICKED"] = "meetings_sidebar_join_clicked";
	AnalyticsEvents["WIDGETS_SCHEDULE_VIEW_ALL_CLICKED"] = "widgets_schedule_view_all_clicked";
	AnalyticsEvents["WIDGETS_SCHEDULE_CONNECT_CALENDAR_CLICKED"] = "widgets_schedule_connect_calendar_clicked";
	AnalyticsEvents["WIDGETS_ROUTINES_ROUTINE_CLICKED"] = "widgets_routines_routine_clicked";
	AnalyticsEvents["WIDGETS_ROUTINES_CREATE_CLICKED"] = "widgets_routines_create_clicked";
	AnalyticsEvents["WIDGETS_ROUTINES_MANAGE_CLICKED"] = "widgets_routines_manage_clicked";
	AnalyticsEvents["WIDGETS_YESTERDAY_MEETING_CLICKED"] = "widgets_yesterday_meeting_clicked";
	AnalyticsEvents["WIDGETS_YESTERDAY_EXTRACT_ACTIONS_CLICKED"] = "widgets_yesterday_extract_actions_clicked";
	AnalyticsEvents["WIDGETS_YESTERDAY_CONNECT_CALENDAR_CLICKED"] = "widgets_yesterday_connect_calendar_clicked";
	AnalyticsEvents["WIDGETS_PICKUP_RESUMED"] = "widgets_pickup_resumed";
	AnalyticsEvents["WIDGETS_CONNECT_MORE_CLICKED"] = "widgets_connect_more_clicked";
	AnalyticsEvents["WIDGETS_CONNECT_INTEGRATION_CLICKED"] = "widgets_connect_integration_clicked";
	AnalyticsEvents["WIDGETS_CREDIT_TOPUP_CLICKED"] = "widgets_credit_topup_clicked";
	AnalyticsEvents["WIDGETS_CREDIT_MANAGE_CLICKED"] = "widgets_credit_manage_clicked";
	AnalyticsEvents["HOME_VIEWED"] = "unified_home_viewed";
	AnalyticsEvents["HOME_SETTINGS_OPENED"] = "unified_home_config_opened";
	AnalyticsEvents["HOME_SETTINGS_RESET"] = "unified_home_config_reset";
	AnalyticsEvents["HOME_SECTION_VISIBILITY_TOGGLED"] = "unified_home_section_visibility_toggled";
	AnalyticsEvents["HOME_SECTION_COLLAPSE_TOGGLED"] = "unified_home_section_collapse_toggled";
	AnalyticsEvents["HOME_SECTION_REORDERED"] = "unified_home_section_reordered";
	AnalyticsEvents["HOME_PINNED_ROUTINE_TOGGLED"] = "unified_home_pinned_routine_toggled";
	AnalyticsEvents["HOME_ACTION_CARDS_SHOW_ALL_TOGGLED"] = "unified_home_action_cards_show_all_toggled";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_SHOWN"] = "unified_home_integration_suggestion_shown";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_CONNECT_CLICKED"] = "unified_home_integration_suggestion_connect_clicked";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_CONNECTED"] = "unified_home_integration_suggestion_connected";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_DISMISSED"] = "unified_home_integration_suggestion_dismissed";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_REMOVED"] = "unified_home_integration_suggestion_removed";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_TRY_CLICKED"] = "unified_home_integration_suggestion_try_clicked";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_CTA_CLICKED"] = "unified_home_integration_suggestion_cta_clicked";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_FEEDBACK_SHOWN"] = "unified_home_integration_suggestion_feedback_shown";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_FEEDBACK_SUBMITTED"] = "unified_home_integration_suggestion_feedback_submitted";
	AnalyticsEvents["HOME_INTEGRATION_SUGGESTION_FEEDBACK_SKIPPED"] = "unified_home_integration_suggestion_feedback_skipped";
	AnalyticsEvents["INTEGRATIONS_SUGGESTION_DISMISSED"] = "integrations_suggestion_dismissed";
	AnalyticsEvents["INTEGRATION_LINK_OPENED"] = "integration_link_opened";
	AnalyticsEvents["INTEGRATION_LINK_CONNECT_STARTED"] = "integration_link_connect_started";
	AnalyticsEvents["INTEGRATION_LINK_CONNECT_DISMISSED"] = "integration_link_connect_dismissed";
	AnalyticsEvents["FEED_VIEWED"] = "feed_viewed";
	AnalyticsEvents["FEED_DENSITY_CHANGED"] = "feed_density_changed";
	AnalyticsEvents["FEED_LAYOUT_CHANGED"] = "feed_layout_changed";
	AnalyticsEvents["ACTION_CARD_SHOWN"] = "action_card_shown";
	AnalyticsEvents["ACTION_CARD_VIEWED"] = "action_card_viewed";
	AnalyticsEvents["ACTION_CARD_OPENED"] = "action_card_opened";
	AnalyticsEvents["ACTION_CARD_EXPANDED"] = "action_card_expanded";
	AnalyticsEvents["ACTION_CARD_SOURCE_OPENED"] = "action_card_source_opened";
	AnalyticsEvents["ACTION_CARD_EDIT_STARTED"] = "action_card_edit_started";
	AnalyticsEvents["ACTION_CARD_EDIT_SAVED"] = "action_card_edit_saved";
	AnalyticsEvents["ACTION_CARD_CONFIRM_SUBMITTED"] = "action_card_confirm_submitted";
	AnalyticsEvents["ACTION_CARD_DISMISS_SUBMITTED"] = "action_card_dismiss_submitted";
	AnalyticsEvents["ACTION_CARD_DISMISS_UNDONE"] = "action_card_dismiss_undone";
	AnalyticsEvents["ACTION_CARD_REVIEW_OPENED"] = "action_card_review_opened";
	AnalyticsEvents["ACTION_CARD_REVIEW_CLOSED"] = "action_card_review_closed";
	AnalyticsEvents["ACTION_CARD_REVIEW_SKIPPED"] = "action_card_review_skipped";
	AnalyticsEvents["ACTION_CARD_NEW_CHAT_STARTED"] = "action_card_new_chat_started";
	AnalyticsEvents["ACTION_CARD_CHAT_CLICKED"] = "action_card_chat_clicked";
	AnalyticsEvents["ACTION_CARD_CHAT_MESSAGE_SUBMITTED"] = "action_card_chat_message_submitted";
	AnalyticsEvents["ACTION_CARD_ACTION"] = "action_card_action";
	AnalyticsEvents["ACTION_CARD_COMPLETED"] = "action_card_completed";
	AnalyticsEvents["ACTION_CARD_ACKNOWLEDGED"] = "action_card_acknowledged";
	AnalyticsEvents["ACTION_CARD_COMMAND_FAILED"] = "action_card_command_failed";
	AnalyticsEvents["ACTION_CARD_FOLLOW_UP_ATTACHED"] = "action_card_follow_up_attached";
	AnalyticsEvents["ACTION_CARD_FOLLOW_UP_THREAD_OPENED"] = "action_card_follow_up_thread_opened";
	AnalyticsEvents["CARD_WATCH_SUGGESTION_SHOWN"] = "card_watch_suggestion_shown";
	AnalyticsEvents["CARD_WATCH_SUGGESTION_ACCEPTED"] = "card_watch_suggestion_accepted";
	AnalyticsEvents["CARD_WATCH_SUGGESTION_KEPT"] = "card_watch_suggestion_kept";
	AnalyticsEvents["CARD_WATCH_RESOLVED_RESTORED"] = "card_watch_resolved_restored";
	AnalyticsEvents["ON_DEVICE_INTEGRATIONS_VIEWED"] = "on_device_integrations_viewed";
	AnalyticsEvents["ON_DEVICE_INTEGRATION_CONNECT_STARTED"] = "on_device_integration_connect_started";
	AnalyticsEvents["CUSTOM_MCP_SERVER_ADDED"] = "custom_mcp_server_added";
	AnalyticsEvents["CUSTOM_MCP_SERVER_CONFIG_PASTED"] = "custom_mcp_server_config_pasted";
	AnalyticsEvents["CUSTOM_MCP_SERVER_CONFIG_REJECTED"] = "custom_mcp_server_config_rejected";
	AnalyticsEvents["CHANNELS_BETA_FEEDBACK_CLICKED"] = "channels_beta_feedback_clicked";
	AnalyticsEvents["DESKTOP_BIRD_GAME_OPENED"] = "desktop_bird_game_opened";
	AnalyticsEvents["DESKTOP_BIRD_GAME_STARTED"] = "desktop_bird_game_started";
	AnalyticsEvents["DESKTOP_BIRD_GAME_OVER"] = "desktop_bird_game_over";
	AnalyticsEvents["CHANGELOG_SIDEBAR_CLICKED"] = "changelog_sidebar_clicked";
	AnalyticsEvents["CHANGELOG_SIDEBAR_DISMISSED"] = "changelog_sidebar_dismissed";
	AnalyticsEvents["CHANGELOG_MENU_CLICKED"] = "changelog_menu_clicked";
	AnalyticsEvents["PEOPLE_CONNECTION_REQUESTED"] = "people_connection_requested";
	AnalyticsEvents["PEOPLE_INVITE_SENT"] = "people_invite_sent";
	return AnalyticsEvents;
}({});
const INTERNAL_DOMAINS = [
	"genos.dev",
	"studiomgmt.co",
	"victorare.mu",
	"littlebird.ai"
];
/**
* Checks if email belongs to an internal domain or is a test account
*/
function isInternalEmail(email) {
	return INTERNAL_DOMAINS.some((domain) => email.endsWith(`@${domain}`) || email.startsWith("test"));
}
//#endregion
//#region src/backend-name.ts
const BACKEND_LABELS = {
	python: "Python",
	typescript: "TypeScript"
};
/**
* A payload's `backend`. An older server leaves it out, and a newer one can
* send a name this build does not know; both read as null, which shows no label
*/
const parseBackendName = (value) => value === "python" || value === "typescript" ? value : null;
/** How staff see the backend's name */
const backendLabelOf = (backend) => BACKEND_LABELS[backend];
//#endregion
//#region src/date-utils.ts
const formatPaywallSubtitle = (nextAllowedTime, errorMessage) => {
	try {
		const resetTime = parseISO(nextAllowedTime);
		return `${errorMessage} Try again after ${`${format(resetTime, "MMM d")} at ${format(resetTime, "h:mm a")}`}.`;
	} catch (error) {
		console.warn("Failed to parse next_allowed_time:", error);
		return errorMessage;
	}
};
const formatImageGenQuotaResetCountdown = (nextAllowedTime) => {
	if (!nextAllowedTime) return "Resets soon.";
	const target = Date.parse(nextAllowedTime);
	if (Number.isNaN(target)) return "Resets soon.";
	const diffMs = target - Date.now();
	if (diffMs <= 0) return "Resets shortly.";
	const minutes = Math.ceil(diffMs / 6e4);
	if (minutes < 1) return "Resets in less than a minute.";
	if (minutes < 60) return `Resets in ${minutes} minute${minutes === 1 ? "" : "s"}.`;
	const hours = Math.ceil(minutes / 60);
	return `Resets in ${hours} hour${hours === 1 ? "" : "s"}.`;
};
function formatRelativeDate(date, nowMs = Date.now()) {
	const d = date instanceof Date ? date : new Date(date);
	const diff = nowMs - d.getTime();
	const minutes = Math.floor(diff / 6e4);
	if (minutes < 1) return "Just now";
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days === 1) return "Yesterday";
	if (days < 7) return `${days}d ago`;
	const isSameYear = d.getFullYear() === new Date(nowMs).getFullYear();
	return d.toLocaleDateString(void 0, {
		month: "short",
		day: "numeric",
		year: isSameYear ? void 0 : "numeric"
	});
}
const addedText = (withZone, withoutZone) => {
	let start = 0;
	while (start < withoutZone.length && withZone[start] === withoutZone[start]) start += 1;
	let end = 0;
	while (end < withoutZone.length - start && withZone[withZone.length - 1 - end] === withoutZone[withoutZone.length - 1 - end]) end += 1;
	return withZone.slice(start, withZone.length - end).replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
};
const zoneFormatters = /* @__PURE__ */ new Map();
let zoneFormattersCtor;
const zoneFormattersFor = (iana) => {
	if (zoneFormattersCtor !== Intl.DateTimeFormat) {
		zoneFormatters.clear();
		zoneFormattersCtor = Intl.DateTimeFormat;
	}
	let formatters = zoneFormatters.get(iana);
	if (!formatters) {
		formatters = {
			withZone: new Intl.DateTimeFormat(void 0, {
				timeZone: iana,
				timeZoneName: "long"
			}),
			withoutZone: new Intl.DateTimeFormat(void 0, { timeZone: iana })
		};
		zoneFormatters.set(iana, formatters);
	}
	return formatters;
};
const formatTimezone = (iana, when = /* @__PURE__ */ new Date()) => {
	try {
		const { withZone, withoutZone } = zoneFormattersFor(iana);
		return addedText(withZone.format(when), withoutZone.format(when)) || iana;
	} catch {
		return iana;
	}
};
//#endregion
//#region src/cron.ts
/**
* calls a given function periodically with a given interval
* Usage -
* const cron = new PeriodicCron(1000, () => {
*  console.log("Hello World");
* });
* cron.start();
* cron.stop();
*/
var PeriodicCron = class {
	intervalMs;
	fn;
	timer;
	stopped = false;
	constructor(intervalMs, fn) {
		this.intervalMs = intervalMs;
		this.fn = fn;
	}
	start() {
		if (this.timer) return;
		this.stopped = false;
		const runFn = () => {
			Promise.resolve(this.fn()).catch((error) => {
				console.error(error, `PeriodicCron: failed to run cron function - ${error?.message}`, {
					context: "PeriodicCron:start",
					metadata: {
						intervalMs: this.intervalMs,
						fnName: this.fn.name
					}
				});
			}).finally(() => {
				if (!this.stopped) this.timer = setTimeout(runFn, this.withJitter(this.intervalMs));
			});
		};
		this.timer = setTimeout(runFn, this.withJitter(this.intervalMs));
	}
	stop() {
		this.stopped = true;
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = void 0;
		}
	}
	withJitter(timeMs) {
		return timeMs + Math.random() * 2e3;
	}
};
//#endregion
//#region src/connection-card/connection-card-url.ts
const COMPACT_REF_PREFIX = "v2.";
const COMPACT_REF_PARTS = 6;
const COMPACT_REF_SIGNATURE_LENGTH = 16;
/** Only the signed reference is read; anything else in the link is ignored, never trusted. */
function parseConnectionCardUrl(href) {
	try {
		const url = new URL(href);
		if (url.protocol !== "littlebird:" || url.hostname !== "connections" || url.pathname !== "/oauth") return { kind: "none" };
		const flowRef = url.searchParams.get("flow_ref");
		return flowRef ? {
			kind: "connectionCard",
			flowRef
		} : { kind: "none" };
	} catch {
		return { kind: "none" };
	}
}
/**
* True while a compact `v2.` reference is still streaming in. Such prefixes
* can never resolve, so callers should hold off fetching. Refs outside the
* compact grammar are never reported partial — the backend validates them.
*/
function isPartialConnectionFlowRef(flowRef) {
	if (COMPACT_REF_PREFIX.startsWith(flowRef)) return true;
	if (!flowRef.startsWith(COMPACT_REF_PREFIX)) return false;
	const parts = flowRef.split(".");
	return parts.length < COMPACT_REF_PARTS || parts[5].length < COMPACT_REF_SIGNATURE_LENGTH;
}
//#endregion
//#region src/copyable-email/copyable-actions.ts
const EMAIL_LABEL_RE = /(^|[^a-z0-9])(e-?mail|gmail|mail)([^a-z0-9]|$)/i;
const SUBJECT_RE = /^subject:\s*(.*)$/i;
const BOUNDARY_LINE_BREAKS_RE = /^(?:\r\n|\r|\n)+|(?:\r\n|\r|\n)+$/g;
/**
* Strip leading/trailing line breaks (not spaces/tabs) so an editor shows a
* clean body while preserving first-line indentation and trailing whitespace.
*/
function trimBoundaryNewlines(value) {
	return value.replace(BOUNDARY_LINE_BREAKS_RE, "");
}
/**
* True when a copyable block should use the email UI (subject row, mailto,
* edit). Matches on label (Email / Gmail / …) or on content that starts with a
* `Subject:` line — models often use descriptive labels like "Graceful Decline".
*/
function isCopyableEmailBlock(label, content) {
	if (label && EMAIL_LABEL_RE.test(label)) return true;
	return parseEmailDraft(content).subjectLine.length > 0;
}
const DEFAULT_SUBJECT_SEPARATOR = "\n\n";
const SUBJECT_LINE_PARTS_RE = /^(\s*Subject:\s*)(.*?)(\s*)$/i;
/**
* Splices `newSubject` into `subjectLine` while keeping the line's original
* whitespace prefix (any indent before `Subject:` plus the spaces after the
* colon) and trailing whitespace. Falls back to the canonical
* `Subject: ${newSubject}` if the source line doesn't match the expected
* shape. Lets a subject edit avoid rewriting the surrounding whitespace
* bytes that the user didn't touch.
*/
function replaceSubjectValue(subjectLine, newSubject) {
	const match = SUBJECT_LINE_PARTS_RE.exec(subjectLine);
	if (!match) return `Subject: ${newSubject}`;
	return `${match[1]}${newSubject}${match[3]}`;
}
/**
* Splits `content` into lines while recording each line's source positions.
* Recognizes `\n`, `\r\n`, and bare `\r` as line terminators so callers can
* slice the original bytes (CRLF and all) instead of working off a normalized
* `\n`-only copy.
*/
function splitLinesWithPositions(content) {
	const lines = [];
	const positions = [];
	let lineStart = 0;
	let i = 0;
	while (i < content.length) {
		const ch = content[i];
		if (ch === "\n" || ch === "\r") {
			const terminatorLen = ch === "\r" && content[i + 1] === "\n" ? 2 : 1;
			lines.push(content.slice(lineStart, i));
			positions.push({
				start: lineStart,
				end: i
			});
			i += terminatorLen;
			lineStart = i;
		} else i++;
	}
	lines.push(content.slice(lineStart));
	positions.push({
		start: lineStart,
		end: content.length
	});
	return {
		lines,
		positions
	};
}
function parseEmailDraft(content) {
	const { lines, positions } = splitLinesWithPositions(content);
	const firstContentLineIndex = lines.findIndex((line) => line.trim());
	if (firstContentLineIndex === -1) return {
		subject: "",
		body: content,
		subjectSeparator: "",
		preSubject: "",
		subjectLine: ""
	};
	const subjectMatch = SUBJECT_RE.exec(lines[firstContentLineIndex].trim());
	if (!subjectMatch) return {
		subject: "",
		body: content,
		subjectSeparator: "",
		preSubject: "",
		subjectLine: ""
	};
	let bodyStartIndex = firstContentLineIndex + 1;
	while (bodyStartIndex < lines.length && lines[bodyStartIndex].trim() === "") bodyStartIndex++;
	const subjectLineStartPos = positions[firstContentLineIndex].start;
	const subjectLineEndPos = positions[firstContentLineIndex].end;
	const bodyLineStartPos = bodyStartIndex >= lines.length ? content.length : positions[bodyStartIndex].start;
	const body = content.slice(bodyLineStartPos);
	return {
		subject: subjectMatch[1].trim(),
		body,
		subjectSeparator: content.slice(subjectLineEndPos, bodyLineStartPos),
		preSubject: firstContentLineIndex > 0 ? content.slice(0, subjectLineStartPos) : "",
		subjectLine: content.slice(subjectLineStartPos, subjectLineEndPos)
	};
}
/**
* Re-serialize an email draft. If `subjectLine` is supplied, it's emitted
* verbatim - callers pass the source `subjectLine` from `parseEmailDraft`
* when the user didn't edit the subject, so byte-exact whitespace around
* `Subject:` and the value round-trips. Otherwise the canonical
* `Subject: ${subject}` line is rebuilt.
*/
function formatEmailDraftContent(subject, body, subjectSeparator = DEFAULT_SUBJECT_SEPARATOR, preSubject = "", subjectLine) {
	if (!subject) return `${preSubject}${body}`;
	return `${preSubject}${subjectLine ?? `Subject: ${subject}`}${body !== "" && subjectSeparator === "" && !/^[\r\n]/.test(body) ? DEFAULT_SUBJECT_SEPARATOR : subjectSeparator}${body}`;
}
/**
* Re-serialize a parsed draft after the user edits its subject and/or body,
* keeping the source bytes the user didn't touch byte-for-byte. This owns the
* "did the subject change?" policy — splice the new value into the original
* `Subject:` line when it changed, reuse the source line verbatim when it
* didn't — so callers pass only the parsed draft and the edited values instead
* of threading `subjectSeparator` / `preSubject` / `subjectLine` by hand.
* `edits.body` is emitted as given; callers reconcile their editor's boundary
* newlines before calling.
*/
function rebuildEditedDraft(parsed, edits) {
	const subjectLine = edits.subject === parsed.subject ? parsed.subjectLine : parsed.subjectLine ? replaceSubjectValue(parsed.subjectLine, edits.subject) : void 0;
	return formatEmailDraftContent(edits.subject, edits.body, parsed.subjectSeparator, parsed.preSubject, subjectLine);
}
function encodeMailtoValue(value) {
	return encodeURIComponent(value.replace(/\r\n|\r|\n/g, "\r\n"));
}
function buildMailtoUrlFromDraft(subject, body) {
	const params = [];
	if (subject) params.push(`subject=${encodeMailtoValue(subject)}`);
	if (body) params.push(`body=${encodeMailtoValue(body)}`);
	return params.length ? `mailto:?${params.join("&")}` : "mailto:";
}
//#endregion
//#region src/email-draft/email-draft-errors.ts
/**
* Classify an email-draft load error by its stable backend `error_code` when
* present, falling back to the detail message for responses that don't carry
* one. Centralizes the classification so the desktop and mobile hooks don't
* each string-match backend prose (`Draft not found` / `Connected account not
* found`) for control flow.
*/
function classifyEmailDraftError(error) {
	if (error.errorCode === "draft_not_found") return "draft_not_found";
	if (error.errorCode === "account_not_found") return "account_not_found";
	if (error.message === "Draft not found") return "draft_not_found";
	if (error.message === "Connected account not found") return "account_not_found";
	return "unknown";
}
//#endregion
//#region src/email-draft/email-draft.mapping.ts
function draftToEditForm(draft) {
	const cc = draft.cc_recipients ?? [];
	const bcc = draft.bcc_recipients ?? [];
	const to = Array.from(/* @__PURE__ */ new Set([...draft.main_recipient ? [draft.main_recipient] : [], ...draft.recipients ?? []]));
	return {
		subject: draft.subject ?? "",
		body: draft.body ?? "",
		to,
		cc,
		bcc
	};
}
/**
* Maps the edit form to the update-draft payload fields.
* Sends `body` verbatim (including empty) so clearing it blanks the draft
* rather than leaving the previous body untouched.
*/
function editFormToUpdatePayload(form) {
	return {
		subject: form.subject || void 0,
		body: form.body,
		main_recipient: form.to[0] ?? void 0,
		recipients: form.to.length ? form.to : void 0,
		cc_recipients: form.cc.length ? form.cc : void 0,
		bcc_recipients: form.bcc.length ? form.bcc : void 0
	};
}
function parseRecipients(raw) {
	const parts = [];
	let current = "";
	let inQuotes = false;
	let inAngle = false;
	for (const char of raw) {
		if (char === "\"") inQuotes = !inQuotes;
		else if (!inQuotes && char === "<") inAngle = true;
		else if (!inQuotes && char === ">") inAngle = false;
		else if (!inQuotes && !inAngle && (char === "," || char === ";")) {
			parts.push(current);
			current = "";
			continue;
		}
		current += char;
	}
	parts.push(current);
	return Array.from(new Set(parts.map((part) => part.trim()).filter(Boolean)));
}
const EMAIL_SCHEMA = z.string().email();
const DISPLAY_NAME_RE = /^[^<]*<\s*([^>]+?)\s*>$/;
function normalizeRecipient(raw) {
	const trimmed = raw.trim();
	return trimmed.match(DISPLAY_NAME_RE)?.[1].trim() ?? trimmed;
}
function isValidRecipient(email) {
	return EMAIL_SCHEMA.safeParse(normalizeRecipient(email)).success;
}
function hasRecipient(list, email) {
	const needle = normalizeRecipient(email).toLowerCase();
	return list.some((item) => normalizeRecipient(item).toLowerCase() === needle);
}
function mergeRecipients(existing, candidates) {
	const added = [];
	for (const candidate of candidates) {
		const normalized = normalizeRecipient(candidate);
		if (!hasRecipient([...existing, ...added], normalized)) added.push(normalized);
	}
	return added;
}
//#endregion
//#region src/email-draft/email-draft.types.ts
const EmailDraftSchema = z.object({
	subject: z.string().nullable().optional(),
	body: z.string().nullable().optional(),
	main_recipient: z.string().nullable().optional(),
	recipients: z.array(z.string()).nullable().optional(),
	cc_recipients: z.array(z.string()).nullable().optional(),
	bcc_recipients: z.array(z.string()).nullable().optional()
});
//#endregion
//#region src/integrations/integration-display-name.ts
const integrationDisplayNameMap = {
	gmail: "Gmail",
	googlecalendar: "Google Calendar",
	googledocs: "Google Docs",
	googledrive: "Google Drive",
	dropbox: "Dropbox",
	googlesheets: "Google Sheets",
	applecalendar: "Apple Calendar",
	applemail: "Apple Mail",
	applereminders: "Apple Reminders",
	applecontacts: "Apple Contacts",
	imessage: "iMessage",
	outlook: "Outlook",
	slack: "Slack",
	linkedin: "LinkedIn",
	twitter: "Twitter / X",
	notion: "Notion",
	github: "GitHub",
	zohocrm: "Zoho CRM",
	zoho_crm: "Zoho CRM"
};
const normalizeToolkitKey = (name) => name.toLowerCase().replace(/[^a-z0-9_]/g, "");
const getIntegrationDisplayName = (name) => {
	const match = integrationDisplayNameMap[normalizeToolkitKey(name)];
	if (match) return match;
	const trimmed = name.trim();
	return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : "";
};
//#endregion
//#region src/email-draft/send-draft-card.ts
/**
* Sender line for a send-draft card: includes the account label when present,
* otherwise just the integration name. Resolves the display name from the
* shared registry so desktop and mobile render the same string.
*/
function buildSendDraftFromLine(accountLabel, toolkitName) {
	const integrationName = getIntegrationDisplayName(toolkitName);
	return accountLabel ? `From ${accountLabel} · ${integrationName}` : integrationName;
}
//#endregion
//#region src/email-draft/send-draft-url.ts
function parseSendDraftUrl(href) {
	try {
		const url = new URL(href);
		if (!url.pathname.includes("/integrations/send-draft")) return { kind: "none" };
		const draftId = url.searchParams.get("draft_id");
		const toolkitName = url.searchParams.get("toolkit_name");
		const accountLabel = url.searchParams.get("account_label") ?? "";
		if (!draftId || !toolkitName) return { kind: "none" };
		return {
			kind: "sendDraft",
			draftId,
			toolkitName,
			accountLabel
		};
	} catch {
		return { kind: "none" };
	}
}
//#endregion
//#region src/external-link.ts
const MULTI_PART_TLDS = /* @__PURE__ */ new Set([
	"co.uk",
	"co.jp",
	"co.kr",
	"co.nz",
	"co.za",
	"co.in",
	"co.id",
	"co.th",
	"com.au",
	"com.br",
	"com.mx",
	"com.cn",
	"com.sg",
	"com.ar",
	"com.tr",
	"com.tw",
	"com.hk"
]);
const GLOBAL_BRANDS = /* @__PURE__ */ new Set([
	"amazon",
	"google",
	"apple",
	"microsoft",
	"ebay",
	"yahoo",
	"bing",
	"facebook",
	"instagram",
	"linkedin",
	"twitter",
	"netflix",
	"spotify",
	"paypal",
	"airbnb",
	"booking",
	"expedia",
	"tripadvisor"
]);
const NO_SUBDOMAIN_TRUST = /* @__PURE__ */ new Set([
	"substack.com",
	"medium.com",
	"carrd.co",
	"webflow.io",
	"squarespace.com",
	"wixsite.com",
	"blogspot.com",
	"wordpress.com",
	"tumblr.com",
	"atlassian.com",
	"notion.so",
	"linear.app",
	"monday.com",
	"airtable.com"
]);
const TRUSTED_DOMAINS = /* @__PURE__ */ new Set([
	"google.com",
	"bing.com",
	"duckduckgo.com",
	"yahoo.com",
	"baidu.com",
	"yandex.com",
	"github.com",
	"gitlab.com",
	"bitbucket.org",
	"stackoverflow.com",
	"stackexchange.com",
	"npmjs.com",
	"developer.mozilla.org",
	"developer.apple.com",
	"learn.microsoft.com",
	"docs.microsoft.com",
	"pypi.org",
	"crates.io",
	"rubygems.org",
	"packagist.org",
	"hub.docker.com",
	"hashicorp.com",
	"terraform.io",
	"kubernetes.io",
	"go.dev",
	"rust-lang.org",
	"python.org",
	"nodejs.org",
	"reactjs.org",
	"vuejs.org",
	"angular.io",
	"svelte.dev",
	"nextjs.org",
	"typescriptlang.org",
	"amazon.com",
	"ebay.com",
	"etsy.com",
	"walmart.com",
	"target.com",
	"bestbuy.com",
	"costco.com",
	"homedepot.com",
	"lowes.com",
	"newegg.com",
	"aliexpress.com",
	"shopify.com",
	"wayfair.com",
	"zappos.com",
	"macys.com",
	"nordstrom.com",
	"wikipedia.org",
	"wikimedia.org",
	"medium.com",
	"quora.com",
	"britannica.com",
	"archive.org",
	"wolframalpha.com",
	"linkedin.com",
	"twitter.com",
	"x.com",
	"reddit.com",
	"facebook.com",
	"instagram.com",
	"threads.net",
	"mastodon.social",
	"discord.com",
	"slack.com",
	"app.slack.com",
	"api.slack.com",
	"telegram.org",
	"aws.amazon.com",
	"cloud.google.com",
	"azure.microsoft.com",
	"digitalocean.com",
	"linode.com",
	"cloudflare.com",
	"fastly.com",
	"mongodb.com",
	"planetscale.com",
	"cockroachlabs.com",
	"redis.io",
	"elastic.co",
	"notion.so",
	"figma.com",
	"trello.com",
	"asana.com",
	"monday.com",
	"airtable.com",
	"miro.com",
	"canva.com",
	"dropbox.com",
	"box.com",
	"evernote.com",
	"todoist.com",
	"clickup.com",
	"linear.app",
	"jira.atlassian.com",
	"confluence.atlassian.com",
	"developer.atlassian.com",
	"support.atlassian.com",
	"community.atlassian.com",
	"atlassian.com",
	"outlook.office.com",
	"outlook.live.com",
	"nytimes.com",
	"bbc.com",
	"reuters.com",
	"apnews.com",
	"cnn.com",
	"washingtonpost.com",
	"theguardian.com",
	"wsj.com",
	"bloomberg.com",
	"techcrunch.com",
	"theverge.com",
	"wired.com",
	"arstechnica.com",
	"news.ycombinator.com",
	"youtube.com",
	"vimeo.com",
	"twitch.tv",
	"spotify.com",
	"soundcloud.com",
	"netflix.com",
	"hulu.com",
	"disneyplus.com",
	"primevideo.com",
	"coursera.org",
	"udemy.com",
	"edx.org",
	"khanacademy.org",
	"udacity.com",
	"pluralsight.com",
	"skillshare.com",
	"codecademy.com",
	"freecodecamp.org",
	"leetcode.com",
	"hackerrank.com",
	"paypal.com",
	"stripe.com",
	"square.com",
	"wise.com",
	"revolut.com",
	"coinbase.com",
	"robinhood.com",
	"fidelity.com",
	"schwab.com",
	"vanguard.com",
	"airbnb.com",
	"booking.com",
	"expedia.com",
	"tripadvisor.com",
	"kayak.com",
	"hotels.com",
	"vrbo.com",
	"united.com",
	"delta.com",
	"aa.com",
	"southwest.com",
	"doordash.com",
	"ubereats.com",
	"grubhub.com",
	"instacart.com",
	"yelp.com",
	"opentable.com",
	"littlebird.ai",
	"app.littlebird.ai",
	"openai.com",
	"chatgpt.com",
	"anthropic.com",
	"claude.ai",
	"deepmind.com",
	"cohere.com",
	"huggingface.co",
	"midjourney.com",
	"stability.ai",
	"perplexity.ai",
	"mistral.ai",
	"groq.com"
]);
function extractBrand(hostname) {
	const parts = hostname.replace(/^www\./, "").split(".");
	if (parts.length < 2) return null;
	if (parts.length >= 3) {
		const possibleTld = parts.slice(-2).join(".");
		if (MULTI_PART_TLDS.has(possibleTld)) return parts[parts.length - 3];
	}
	return parts[parts.length - 2];
}
function isTrustedUrl(url) {
	try {
		const { hostname } = new URL(url);
		const normalizedHost = hostname.replace(/^www\./, "");
		if (TRUSTED_DOMAINS.has(normalizedHost)) return true;
		for (const domain of TRUSTED_DOMAINS) if (normalizedHost.endsWith(`.${domain}`) && !NO_SUBDOMAIN_TRUST.has(domain)) return true;
		const brand = extractBrand(normalizedHost);
		if (brand && GLOBAL_BRANDS.has(brand)) return true;
		return false;
	} catch {
		return false;
	}
}
const DANGEROUS_SCHEMES = /^(javascript:|data:|vbscript:|file:)/i;
const NATIVE_SCHEMES = /^(mailto:|tel:|sms:)/i;
function isNativeSchemeUrl(url) {
	return NATIVE_SCHEMES.test(url);
}
function isOpenableUrl(url) {
	if (DANGEROUS_SCHEMES.test(url)) return false;
	try {
		const parsed = new URL(url);
		return parsed.protocol === "http:" || parsed.protocol === "https:";
	} catch {
		return false;
	}
}
//#endregion
//#region src/feedback/feedback-draft-url.ts
function parseFeedbackDraftUrl(href) {
	try {
		const url = new URL(href);
		if (url.pathname.replace(/\/$/, "") !== "/feedback/draft") return { kind: "none" };
		const draftId = url.searchParams.get("draft_id");
		if (!draftId) return { kind: "none" };
		return {
			kind: "feedbackDraft",
			draftId
		};
	} catch {
		return { kind: "none" };
	}
}
//#endregion
//#region src/feedback/feedback-draft.types.ts
const MessageFeedbackContentSchema = z.object({
	type: z.literal("message_feedback"),
	reasoning: z.string(),
	sentiment: z.enum(["positive", "negative"]),
	message_id: z.string()
});
const ProductFeedbackContentSchema = z.object({
	type: z.literal("product_feedback"),
	text: z.string(),
	feedback_type: z.enum([
		"bug",
		"feature",
		"other"
	]),
	calendar_related: z.boolean().default(false)
});
const FeedbackDraftSchema = z.object({
	id: z.string(),
	chat_id: z.string(),
	toolkit_name: z.string(),
	status: z.enum(["draft", "sent"]),
	content: z.discriminatedUnion("type", [MessageFeedbackContentSchema, ProductFeedbackContentSchema])
});
function toSendFeedbackDraftBody(input) {
	return input.type === "message_feedback" ? {
		type: input.type,
		reasoning: input.reasoning,
		include_chat_history: input.includeChatHistory
	} : {
		type: input.type,
		text: input.text,
		feedback_type: input.feedbackType
	};
}
//#endregion
//#region src/feedback/feedback.copy.ts
/**
* Consent labels for message feedback.
*
* The control gates retention of the exact payload behind a rated message —
* the whole chat plus the context Littlebird used in it, not just chat history.
* Wording differs by vote direction because the same control appears in both
* the thumbs-up and thumbs-down flows.
*
* Change the copy here and every surface picks it up.
*/
const FEEDBACK_CONSENT_LABEL_UPVOTE = "Include the chat's history and context to help us learn what worked";
const FEEDBACK_CONSENT_LABEL_DOWNVOTE = "Include the chat's history and context, so we can reproduce and debug the issue";
//#endregion
//#region src/app-origin.ts
const APP_ORIGIN = "https://app.littlebird.ai";
//#endregion
//#region src/file-link/file-link-url.ts
const APP_HOST = new URL(APP_ORIGIN).host;
const FILE_LINK_PATHNAME = /^\/f\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
/** The agent writes absolute links, while accepting relative links keeps rendering robust. */
function parseFileLinkUrl(href) {
	try {
		const url = new URL(href, APP_ORIGIN);
		if (url.protocol !== "https:" || url.host !== APP_HOST) return { kind: "none" };
		const match = url.pathname.match(FILE_LINK_PATHNAME);
		if (!match) return { kind: "none" };
		return {
			kind: "fileLink",
			token: match[1]
		};
	} catch {
		return { kind: "none" };
	}
}
//#endregion
//#region src/greetings.ts
const GREETING_LINES = [
	{
		text: "What's on your mind, {firstName}?",
		plain: "What's on your mind?"
	},
	{
		text: "What are you working on, {firstName}?",
		plain: "What are you working on?"
	},
	{
		text: "Where should we start, {firstName}?",
		plain: "Where should we start?"
	},
	{
		text: "Ready when you are, {firstName}.",
		plain: "Ready when you are."
	},
	{
		text: "Let's get into it, {firstName}.",
		plain: "Let's get into it."
	},
	{
		text: "What can I help with, {firstName}?",
		plain: "What can I help with?"
	},
	{
		text: "What needs attention, {firstName}?",
		plain: "What needs attention?"
	},
	{
		text: "What's up in the air, {firstName}?",
		plain: "What's up in the air?"
	},
	{
		text: "Back at it, {firstName}?",
		plain: "Back at it?"
	},
	{
		text: "Want the bird's-eye view, {firstName}?",
		plain: "Want the bird's-eye view?"
	},
	{
		text: "Bird by bird, {firstName}.",
		plain: "Bird by bird."
	},
	{
		text: "No need to wing it, {firstName}.",
		plain: "No need to wing it."
	},
	{
		text: "Welcome back, {firstName}.",
		plain: "Welcome back."
	},
	{
		text: "Hey there, {firstName}.",
		plain: "Hey there."
	},
	{
		text: "What's on deck, {firstName}?",
		plain: "What's on deck?"
	},
	{
		text: "Want a head start, {firstName}?",
		plain: "Want a head start?"
	},
	{
		text: "What's the first move, {firstName}?",
		plain: "What's the first move?"
	},
	{
		text: "What's hatching, {firstName}?",
		plain: "What's hatching?"
	},
	{
		text: "Getting your ducks in a row, {firstName}?",
		plain: "Getting your ducks in a row?"
	},
	{
		text: "What's the next step, {firstName}?",
		plain: "What's the next step?"
	},
	{
		text: "Where'd you leave off, {firstName}?",
		plain: "Where'd you leave off?"
	},
	{
		text: "What's in flight, {firstName}?",
		plain: "What's in flight?"
	},
	{
		text: "Making headway, {firstName}?",
		plain: "Making headway?"
	},
	{ text: "What are we exploring today?" },
	{ text: "Got something for me?" },
	{ text: "What's the plan for today?" },
	{
		when: "morning",
		text: "Morning, {firstName}. What's first?",
		plain: "Morning. What's first?"
	},
	{
		when: "morning",
		text: "What matters this morning, {firstName}?",
		plain: "What matters this morning?"
	},
	{
		when: "morning",
		text: "Plenty of day ahead, {firstName}.",
		plain: "Plenty of day ahead."
	},
	{
		when: "morning",
		text: "Where's today headed, {firstName}?",
		plain: "Where's today headed?"
	},
	{
		when: "evening",
		text: "Anything to close out, {firstName}?",
		plain: "Anything to close out?"
	},
	{
		when: "evening",
		text: "Where's the evening headed, {firstName}?",
		plain: "Where's the evening headed?"
	},
	{
		when: "evening",
		text: "One more thing tonight, {firstName}?",
		plain: "One more thing tonight?"
	},
	{
		when: "evening",
		text: "Any loose ends, {firstName}?",
		plain: "Any loose ends?"
	},
	{
		when: "evening",
		text: "Good evening, {firstName}.",
		plain: "Good evening."
	},
	{
		when: "late-night",
		text: "Can't sleep or won't sleep, {firstName}?",
		plain: "Can't sleep or won't sleep?"
	},
	{
		when: "late-night",
		text: "Unfinished thought, {firstName}?",
		plain: "Unfinished thought?"
	}
];
const GREETING_LINE_COUNT = GREETING_LINES.length;
const ROTATION_MS = 108e5;
const eligibleIndices = (time) => GREETING_LINES.flatMap((line, index) => !line.when || line.when === time ? [index] : []);
const INDICES_BY_TIME = {
	morning: eligibleIndices("morning"),
	afternoon: eligibleIndices("afternoon"),
	evening: eligibleIndices("evening"),
	"late-night": eligibleIndices("late-night")
};
const timeOfDay = (hour) => {
	if (hour < 5) return "late-night";
	if (hour < 12) return "morning";
	if (hour < 17) return "afternoon";
	if (hour < 23) return "evening";
	return "late-night";
};
const resolveGreetingIndex = (now) => {
	const indices = INDICES_BY_TIME[timeOfDay(now.getHours())];
	return indices[Math.floor(now.getTime() / ROTATION_MS) % indices.length];
};
const TIME_OF_DAY_BOUNDARY_HOURS = [
	5,
	12,
	17,
	23
];
const nextTimeOfDayBoundary = (now) => {
	const next = new Date(now);
	next.setMinutes(0, 0, 0);
	const hour = TIME_OF_DAY_BOUNDARY_HOURS.find((h) => h > now.getHours());
	if (hour === void 0) {
		next.setDate(next.getDate() + 1);
		next.setHours(TIME_OF_DAY_BOUNDARY_HOURS[0]);
	} else next.setHours(hour);
	return next.getTime();
};
const nextGreetingRotationAt = (now) => Math.min((Math.floor(now.getTime() / ROTATION_MS) + 1) * ROTATION_MS, nextTimeOfDayBoundary(now));
const formatGreeting = (index, firstName) => {
	const line = GREETING_LINES[index % GREETING_LINE_COUNT];
	return firstName ? line.text.replace("{firstName}", firstName) : line.plain ?? line.text;
};
//#endregion
//#region src/image-generation-analytics.ts
const ImageGenerationEvents = {
	PROMPT_SUBMITTED: "IMAGE_GENERATION_PROMPT_SUBMITTED",
	COMPLETED: "IMAGE_GENERATION_COMPLETED_CLIENT",
	FAILED: "IMAGE_GENERATION_FAILED_CLIENT",
	VIEWED: "IMAGE_GENERATION_RESULT_VIEWED",
	DOWNLOADED: "IMAGE_GENERATION_RESULT_DOWNLOADED"
};
//#endregion
//#region src/integrations-paid-gating.ts
const FREE_TIER_INTEGRATION_TOOLKITS = ["gmail", "googlecalendar"];
const FREE_TIER_INTEGRATION_TOOLKIT_SET = new Set(FREE_TIER_INTEGRATION_TOOLKITS);
function isFreeTierIntegrationToolkit(toolkitName) {
	return FREE_TIER_INTEGRATION_TOOLKIT_SET.has(toolkitName.toLowerCase());
}
function shouldGateIntegrationConnect({ toolkitName, hasIntegrationAccess, isConnected }) {
	if (hasIntegrationAccess || isConnected) return false;
	return !isFreeTierIntegrationToolkit(toolkitName);
}
function shouldGateMCPConnect({ hasIntegrationAccess, isConnected = false }) {
	return !hasIntegrationAccess && !isConnected;
}
//#endregion
//#region src/integration-status.ts
/** Precedence for duplicate-URL resolution (lower = preferred). */
const integrationStatusPriority = {
	connected: 0,
	probing: 1,
	cancelled: 2,
	expired: 3,
	unreachable: 4,
	unauthorized: 5,
	failed: 6,
	disconnected: 6,
	pending: 7,
	disabled: 8
};
//#endregion
//#region src/integration-utils.ts
const KNOWN_SLUG_PREFIXES = [
	"GOOGLECALENDAR",
	"GOOGLEDRIVE",
	"GOOGLEDOCS",
	"GOOGLESHEETS",
	"GMAIL",
	"GITHUB",
	"NOTION",
	"OUTLOOK"
];
const mapToolNameToLabel = (toolName) => {
	if (toolName.startsWith("mcp:")) {
		const rest = toolName.slice(4);
		if (!rest) return toolName;
		return rest.charAt(0).toUpperCase() + rest.slice(1);
	}
	switch (toolName) {
		case "googlecalendar": return "Google Calendar";
		case "googledocs": return "Google Docs";
		case "googledrive": return "Google Drive";
		case "gmail": return "Gmail";
		case "googlesheets": return "Google Sheets";
		case "notion": return "Notion";
		case "github": return "GitHub";
		case "outlook": return "Microsoft Outlook";
		default: return toolName.length > 0 ? toolName.charAt(0).toUpperCase() + toolName.slice(1) : toolName;
	}
};
const isMcpIdentifier = (slugOrName) => {
	if (!slugOrName) return false;
	return slugOrName.startsWith("mcp_") || slugOrName.startsWith("mcp:");
};
const normalizeIntegrationName = (raw) => {
	const lowered = raw.toLowerCase();
	return lowered.startsWith("mcp:") ? lowered.slice(4) : lowered;
};
/** Strip the integration-name prefix from an MCP tool name so the description
*  doesn't repeat what's already shown in the step title.
*  e.g. stripIntegrationPrefix("notion", "notion_fetch")          → "fetch"
*       stripIntegrationPrefix("axiom",  "axiom_list_dashboards") → "list_dashboards"
*       stripIntegrationPrefix("axiom",  "queryApl")              → "queryApl"  (no prefix)
*/
const stripIntegrationPrefix = (serverName, toolName) => {
	const prefix = `${normalizeIntegrationName(serverName)}_`;
	return toolName.startsWith(prefix) ? toolName.slice(prefix.length) : toolName;
};
/** Sanitized MCP tool segment to title case, e.g. "query_tool" -> "Query Tool", "listDatasets" -> "List Datasets" */
const humanizeMcpToolName = (raw) => raw.replace(/([a-z])([A-Z])/g, "$1_$2").split("_").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
/** Parse "mcp_axiom__query_tool" -> { server: "axiom", tool: "query_tool" } */
const parseMcpSlug = (slug) => {
	if (!slug.startsWith("mcp_")) return void 0;
	const rest = slug.slice(4);
	const idx = rest.indexOf("__");
	if (idx < 0) return void 0;
	return {
		server: rest.slice(0, idx),
		tool: rest.slice(idx + 2)
	};
};
/** Extract the tool-specific part from a slug, stripping the integration prefix.
* e.g. "GMAIL_SEND_EMAIL" → "SEND_EMAIL", "mcp_posthog__org_get" → "org_get"
*/
const extractToolName = (slug) => {
	const parsed = parseMcpSlug(slug);
	if (parsed) return parsed.tool;
	const upper = slug.toUpperCase();
	for (const prefix of KNOWN_SLUG_PREFIXES) if (upper.startsWith(prefix + "_")) return slug.slice(prefix.length + 1);
	const idx = slug.indexOf("_");
	return idx >= 0 ? slug.slice(idx + 1) : void 0;
};
const integrationNameFromSlug = (slug) => {
	if (slug.startsWith("mcp_")) {
		const serverSlug = slug.slice(4).split("__")[0];
		return serverSlug ? serverSlug.toLowerCase() : slug.toLowerCase();
	}
	const upper = slug.toUpperCase();
	for (const prefix of KNOWN_SLUG_PREFIXES) if (upper.startsWith(prefix + "_") || upper === prefix) return prefix.toLowerCase();
	const first = slug.split("_")[0];
	return first ? first.toLowerCase() : slug.toLowerCase();
};
/** Human-readable label for tool_execute titles */
function formatCallIntegrationLabel(toolSlug) {
	const raw = integrationNameFromSlug(toolSlug);
	const mapped = mapToolNameToLabel(raw);
	if (mapped !== raw) return mapped;
	if (!raw.length) return raw;
	return raw.charAt(0).toUpperCase() + raw.slice(1);
}
//#endregion
//#region src/string-hash.ts
function hashString(content) {
	let h = 5381;
	for (let i = 0; i < content.length; i++) h = (h << 5) + h + content.charCodeAt(i) | 0;
	return h.toString(36);
}
//#endregion
//#region src/meeting-notes-context.ts
function buildMeetingNotesSendContext(notes) {
	if (!notes) return void 0;
	const markdown = notesToMarkdown(notes).trim();
	if (!markdown) return void 0;
	return `<meeting-notes>\n${markdown}\n</meeting-notes>`;
}
function hashSendContext(content) {
	return hashString(content);
}
//#endregion
//#region src/meeting-prep-analytics.ts
function buildMeetingPrepEventPayload(meetingId, startDatetime) {
	const scheduledAt = startDatetime ?? null;
	return {
		meeting_id: meetingId,
		meeting_scheduled_at: scheduledAt,
		minutes_until_meeting: scheduledAt ? Math.round((new Date(scheduledAt).getTime() - Date.now()) / 6e4) : null
	};
}
//#endregion
//#region src/meetings/meeting-circles.ts
const withoutHiddenCircles = (circles, hiddenKeys) => {
	if (hiddenKeys.length === 0) return circles;
	const hidden = new Set(hiddenKeys);
	return {
		...circles,
		people: circles.people.filter((circle) => !hidden.has(circle.key)),
		recurring: circles.recurring.filter((circle) => !hidden.has(circle.key))
	};
};
const withCircleHidden = (hiddenKeys, key, hidden) => hidden ? [.../* @__PURE__ */ new Set([...hiddenKeys, key])] : hiddenKeys.filter((hiddenKey) => hiddenKey !== key);
const GROUP_MAX_SIZE = 5;
const GROUP_MIN_MEETINGS = 2;
const FACE_COUNT = 2;
const RECURRING_INSTANCE_ID = /^(.+?)(?:_R\d{8}(?:T\d{6}Z?)?)*_(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z)?$/;
const isDeclined = (attendee) => (attendee.response_status ?? attendee.responseStatus) === "declined";
const otherAttendeeEmails = (currentUserEmail, ...attendeeLists) => {
	const self = currentUserEmail?.toLowerCase();
	const excluded = /* @__PURE__ */ new Map();
	for (const attendee of attendeeLists.flatMap((list) => list ?? [])) {
		const email = attendee.email?.toLowerCase();
		if (!email) continue;
		excluded.set(email, Boolean(excluded.get(email) || email === self || attendee.self || attendee.resource || isDeclined(attendee)));
	}
	return [...excluded].filter(([, isExcluded]) => !isExcluded).map(([email]) => email).sort();
};
const parseRecurringInstance = (eventId) => {
	const match = eventId?.match(RECURRING_INSTANCE_ID);
	if (!match) return null;
	const [, seriesId, ...parts] = match;
	const [year, month, day, hour = 0, minute = 0, second = 0] = parts.filter(Boolean).map(Number);
	return {
		seriesId,
		startsAt: Date.UTC(year, month - 1, day, hour, minute, second)
	};
};
const circleMemberEmails = (currentUserEmail, storedAttendees, calendarAttendees) => otherAttendeeEmails(currentUserEmail, calendarAttendees?.length ? calendarAttendees : storedAttendees);
const recurringInstance = (eventId, seriesId, startsAt) => {
	const parsed = parseRecurringInstance(eventId);
	const id = seriesId || parsed?.seriesId;
	if (!id) return null;
	return {
		seriesId: id,
		startsAt: parsed?.startsAt ?? (Date.parse(startsAt ?? "") || 0)
	};
};
const recurringSeriesId = (eventId, seriesId) => recurringInstance(eventId, seriesId, null)?.seriesId ?? null;
const isInCircle = (circle, meeting) => {
	const seriesId = recurringSeriesId(meeting.eventId, meeting.seriesId);
	if (circle.seriesId !== null) return seriesId === circle.seriesId;
	return seriesId === null && meeting.emails.length === circle.emails.length && meeting.emails.every((email, index) => email === circle.emails[index]);
};
const firstName = (name) => name.split(/[\s@]/)[0];
const joinNames = (names) => names.length === 1 ? names[0] : `${names.slice(0, -1).map(firstName).join(", ")} & ${firstName(names[names.length - 1])}`;
const byMeetingCount = (circles) => [...circles].sort((a, b) => b.meetingIds.length - a.meetingIds.length);
const profilesOf = (attendeeLists) => {
	const profiles = /* @__PURE__ */ new Map();
	for (const attendee of attendeeLists.flatMap((list) => list ?? [])) {
		const email = attendee.email?.toLowerCase();
		if (!email) continue;
		const existing = profiles.get(email);
		profiles.set(email, {
			name: existing?.name || attendee.display_name || attendee.displayName || null,
			avatarUrl: existing?.avatarUrl ?? attendee.avatar_url ?? attendee.avatarUrl ?? null
		});
	}
	return profiles;
};
const pickFaces = (faces) => [...faces].sort((a, b) => Number(!a.avatarUrl) - Number(!b.avatarUrl)).slice(0, FACE_COUNT);
var MeetingCirclesBuilder = class {
	input;
	byAttendees = /* @__PURE__ */ new Map();
	occurrenceByMeetingId = /* @__PURE__ */ new Map();
	seriesById = /* @__PURE__ */ new Map();
	seriesTitles = /* @__PURE__ */ new Map();
	seriesLastStart = /* @__PURE__ */ new Map();
	projectIds = /* @__PURE__ */ new Set();
	hasUnassignedMeetings = false;
	constructor(input) {
		this.input = input;
	}
	addMeeting(meeting) {
		if (meeting.projectId) this.projectIds.add(meeting.projectId);
		else this.hasUnassignedMeetings = true;
		const event = meeting.eventId ? this.input.calendarEventsById.get(meeting.eventId) : void 0;
		const attendeeLists = [meeting.attendees, event?.attendees];
		const emails = circleMemberEmails(this.input.currentUserEmail, meeting.attendees, event?.attendees);
		const instance = recurringInstance(meeting.eventId, meeting.calendarSeriesId || event?.calendar_series_id, meeting.sortAt);
		if (instance) {
			this.addToSeries(instance, emails, attendeeLists, meeting.id);
			this.nameSeries(instance, event?.summary, true);
			this.nameSeries(instance, meeting.name, false);
			return;
		}
		if (emails.length === 0) return;
		this.occurrenceByMeetingId.set(meeting.id, meeting.eventId ?? meeting.id);
		if (emails.length <= GROUP_MAX_SIZE) this.addToAttendeeCircle(meeting, emails, attendeeLists);
	}
	addCalendarEvent(eventId, event) {
		const instance = recurringInstance(eventId, event.calendar_series_id, event.start_datetime);
		if (!instance) return;
		const emails = otherAttendeeEmails(this.input.currentUserEmail, event.attendees);
		if (emails.length === 0) return;
		this.addToSeries(instance, emails, [event.attendees], null);
		this.nameSeries(instance, event.summary, true);
	}
	build() {
		return {
			people: byMeetingCount(this.byAttendees.values()).filter((circle) => circle.emails.length === 1 || this.meetsRepeatedly(circle)),
			recurring: this.recurring(),
			projectIds: [...this.projectIds],
			hasUnassignedMeetings: this.hasUnassignedMeetings
		};
	}
	facesFor(emails, attendeeLists) {
		const profiles = profilesOf(attendeeLists);
		return emails.map((email) => ({
			email,
			name: this.input.displayName(email, profiles.get(email)?.name ?? null),
			avatarUrl: profiles.get(email)?.avatarUrl ?? null
		}));
	}
	addToSeries({ seriesId, startsAt }, emails, attendeeLists, meetingId) {
		let series = this.seriesById.get(seriesId);
		if (!series) {
			series = {
				key: `series:${seriesId}`,
				emails: [],
				seriesId,
				name: "",
				meetingIds: [],
				faces: pickFaces(this.facesFor(emails, attendeeLists))
			};
			this.seriesById.set(seriesId, series);
		}
		if (series.faces.length === 0) series.faces = pickFaces(this.facesFor(emails, attendeeLists));
		series.emails = [.../* @__PURE__ */ new Set([...series.emails, ...emails])].sort();
		if (meetingId) series.meetingIds.push(meetingId);
		this.seriesLastStart.set(seriesId, Math.max(this.seriesLastStart.get(seriesId) ?? 0, startsAt));
	}
	nameSeries({ seriesId, startsAt }, name, fromCalendar) {
		const trimmed = name?.trim();
		if (!trimmed) return;
		const current = this.seriesTitles.get(seriesId);
		if (!current || fromCalendar && !current.fromCalendar || fromCalendar === current.fromCalendar && startsAt > current.startsAt) this.seriesTitles.set(seriesId, {
			name: trimmed,
			startsAt,
			fromCalendar
		});
	}
	addToAttendeeCircle(meeting, emails, attendeeLists) {
		const key = emails.join(",");
		let circle = this.byAttendees.get(key);
		if (!circle) {
			const faces = this.facesFor(emails, attendeeLists);
			circle = {
				key,
				emails,
				seriesId: null,
				name: joinNames(faces.map((face) => face.name)),
				meetingIds: [],
				faces: pickFaces(faces)
			};
			this.byAttendees.set(key, circle);
		}
		circle.meetingIds.push(meeting.id);
	}
	recurring() {
		const lastStart = (series) => this.seriesLastStart.get(series.seriesId ?? "") ?? 0;
		return [...this.seriesById.values()].map((circle) => ({
			...circle,
			name: this.seriesTitles.get(circle.seriesId ?? "")?.name ?? "Untitled"
		})).sort((a, b) => b.meetingIds.length - a.meetingIds.length || lastStart(b) - lastStart(a));
	}
	meetsRepeatedly(circle) {
		return new Set(circle.meetingIds.map((id) => this.occurrenceByMeetingId.get(id))).size >= GROUP_MIN_MEETINGS;
	}
};
const buildMeetingCircles = (input) => {
	const builder = new MeetingCirclesBuilder(input);
	for (const meeting of input.meetings) builder.addMeeting(meeting);
	for (const [eventId, event] of input.calendarEventsById) builder.addCalendarEvent(eventId, event);
	return builder.build();
};
//#endregion
//#region src/meetings/meeting-grouping.ts
const DAY_KEY_FORMAT = "yyyy-MM-dd";
function toDate$1(value) {
	return value instanceof Date ? value : new Date(value);
}
function getEventStartDate(meeting, eventStartsById) {
	return meeting.eventId ? eventStartsById?.get(meeting.eventId) : void 0;
}
function getMeetingBucketDate(meeting, eventStartAt) {
	return toDate$1(meeting.firstTranscriptAt ?? eventStartAt ?? meeting.eventStartAt ?? meeting.createdAt);
}
function getMeetingSortDate(meeting, eventStartAt) {
	return toDate$1(meeting.lastTranscribedAt ?? eventStartAt ?? meeting.eventStartAt ?? meeting.createdAt);
}
function collectUpcomingMeetingIds(upcomingEvents) {
	const meetingIds = /* @__PURE__ */ new Set();
	for (const { meeting } of upcomingEvents) if (meeting) meetingIds.add(meeting.id);
	return meetingIds;
}
/** Groups meetings into local days, newest day first, each day ordered by most recent transcription. */
function groupMeetingsByDate(meetings, options) {
	const excludedIds = options?.excludeMeetingIds;
	const eventStartsById = options?.eventStartsById;
	const buckets = /* @__PURE__ */ new Map();
	for (const meeting of meetings) {
		if (excludedIds?.has(meeting.id)) continue;
		const eventStartAt = getEventStartDate(meeting, eventStartsById);
		const bucketDate = getMeetingBucketDate(meeting, eventStartAt);
		const date = format(bucketDate, DAY_KEY_FORMAT);
		const sortTimestamp = getMeetingSortDate(meeting, eventStartAt).getTime();
		const bucket = buckets.get(date);
		if (bucket) {
			bucket.items.push({
				meeting,
				sortTimestamp
			});
			continue;
		}
		buckets.set(date, {
			date,
			dayTimestamp: bucketDate.getTime(),
			items: [{
				meeting,
				sortTimestamp
			}]
		});
	}
	return [...buckets.values()].sort((left, right) => right.dayTimestamp - left.dayTimestamp).map((bucket) => ({
		date: bucket.date,
		meetings: bucket.items.sort((left, right) => right.sortTimestamp - left.sortTimestamp).map((item) => item.meeting)
	}));
}
/**
* Render a friendly section label for an ISO local-day bucket key:
* Today, Yesterday, weekday for the past week, "Mon, Mar 5" for the
* current year, and "Mar 5, 2024" for prior years.
*/
function getMeetingGroupLabel(isoDay, now = /* @__PURE__ */ new Date()) {
	const date = parseISO(isoDay);
	if (isNaN(date.getTime())) return isoDay;
	const diffDays = differenceInCalendarDays(now, date);
	if (diffDays === 0) return "Today";
	if (diffDays === 1) return "Yesterday";
	if (diffDays > 1 && diffDays < 7) return format(date, "EEEE");
	if (date.getFullYear() === now.getFullYear()) return format(date, "EEE, MMM d");
	return format(date, "MMM d, yyyy");
}
//#endregion
//#region src/messaging-delivery.ts
const MESSAGING_DELIVERY_OFF = "off";
//#endregion
//#region src/reasoning/types.ts
let ReasoningStepType = /* @__PURE__ */ function(ReasoningStepType) {
	ReasoningStepType["Modality"] = "modality";
	ReasoningStepType["Thought"] = "thought";
	return ReasoningStepType;
}({});
let ReasoningStatus = /* @__PURE__ */ function(ReasoningStatus) {
	ReasoningStatus["Pending"] = "pending";
	ReasoningStatus["Complete"] = "complete";
	return ReasoningStatus;
}({});
//#endregion
//#region src/reasoning/modality-types.ts
/** Modality `type` strings (aligned with `@littlebird/sdk` ModalityType values). */
const MT = {
	ChatWithWebSearch: "chat_with_web_search",
	ChatWithWebSearchMulti: "chat_with_web_search_multi",
	ChatWithWebNavigation: "chat_with_web_navigation",
	ChatWithUserContext: "chat_with_user_context",
	ChatWithPreviousContext: "chat_with_previous_context",
	ChatWithNotes: "chat_with_memory",
	EditAssistantNotes: "edit_assistant_notes",
	SelfKnowledge: "self_knowledge",
	ChatWithIntegrations: "chat_with_integrations",
	InternalIntegrations: "internal_integrations",
	ImageGeneration: "generate_image",
	WorldModel: "world_model",
	TodoWrite: "todo_write",
	Todo: "chat_with_todo",
	Memory: "memory",
	Route: "route",
	ReadFileGemini: "read_file_gemini",
	ReadFileCodex: "read_file_codex",
	ReadFileClaude: "read_file_claude",
	EditFile: "edit_file",
	EditFileClaude: "edit_file_claude",
	ApplyPatch: "apply_patch",
	ListDirectory: "list_directory",
	ListDir: "list_dir",
	SearchFileContent: "search_file_content",
	GrepFiles: "grep_files",
	RunShellCommand: "run_shell_command",
	ShellCommand: "shell_command",
	McpToolCall: "mcp_tool_call",
	ReadFile: "read_file",
	WriteFile: "write_file",
	RunShell: "run_shell",
	Bash: "bash",
	RunSandbox: "run_sandbox",
	PresentFile: "present_file",
	GetScreenContext: "get_screen_context",
	BrowserUse: "browser_use",
	ToolSearch: "tool_search",
	ToolExecute: "tool_execute",
	RunSubagent: "run_subagent",
	CancelSubscription: "LB_INTERNAL_CANCEL_SUBSCRIPTION",
	GetSubscriptionStatus: "LB_INTERNAL_GET_SUBSCRIPTION_STATUS",
	CreateMessageFeedbackDraft: "LB_INTERNAL_CREATE_MESSAGE_FEEDBACK_DRAFT",
	CreateProductFeedbackDraft: "LB_INTERNAL_CREATE_PRODUCT_FEEDBACK_DRAFT",
	GetFeedbackDraftStatus: "LB_INTERNAL_GET_FEEDBACK_DRAFT_STATUS"
};
//#endregion
//#region src/reasoning/modality-utils.ts
const getModalitySemanticIdentity = (modality) => {
	const identityPart = (value) => typeof value === "string" ? value : "";
	switch (modality.type) {
		case MT.McpToolCall: return [
			modality.type,
			identityPart(modality.server_slug),
			identityPart(modality.tool_name)
		];
		case MT.ToolExecute: return [
			modality.type,
			identityPart(modality.integration_name),
			identityPart(modality.tool_slug)
		];
		case MT.ChatWithIntegrations:
		case MT.InternalIntegrations: return [modality.type, identityPart(modality.tool_name)];
		default: return [modality.type];
	}
};
const PRESENT_TENSE = {
	Applied: "Applying",
	Connecting: "Connecting",
	Searched: "Searching",
	Sent: "Sending",
	Accessed: "Accessing",
	Visited: "Visiting",
	Browsed: "Browsing",
	Edited: "Editing",
	Listed: "Listing",
	Loaded: "Loading",
	Connected: "Connecting",
	Ran: "Running",
	Updated: "Updating",
	Generated: "Generating",
	Looked: "Looking",
	Used: "Using",
	Retrieved: "Retrieving",
	Requested: "Requesting",
	Checked: "Checking",
	Verified: "Verifying",
	Fetched: "Fetching",
	Reviewed: "Reviewing",
	Pulled: "Pulling",
	Read: "Reading",
	Routed: "Routing",
	Spawned: "Spawning",
	Wrote: "Writing",
	Called: "Calling",
	Asked: "Asking",
	Drafted: "Drafting",
	Prepared: "Preparing"
};
/** Device `terminal` toolkit: run one shell command (see axon-toolkits/terminal). */
const TERMINAL_RUN_SLUG = "TERMINAL_RUN";
const getTensed = (pastTense, isActive) => {
	if (!isActive) return pastTense;
	return PRESENT_TENSE[pastTense] ?? pastTense;
};
const WORKING_WORDS = [
	"processing",
	"analyzing",
	"reasoning",
	"pondering",
	"reflecting",
	"synthesizing",
	"evaluating"
];
const getWorkingWord = () => {
	return WORKING_WORDS[Math.floor(Math.random() * WORKING_WORDS.length)];
};
const toTrimmedString = (value) => {
	if (typeof value !== "string") return void 0;
	return value.trim() || void 0;
};
const toArray = (value) => Array.isArray(value) ? value : [];
const toTrimmedText = (value) => {
	if (value === void 0 || value === null) return void 0;
	const text = String(value).trim();
	return text.length > 0 ? text : void 0;
};
const isContentOldEnough = (createdAt, thresholdMs = 300) => {
	if (!createdAt) return false;
	return Date.now() - createdAt.getTime() > thresholdMs;
};
const getReasoningStatus = (shouldEnd, thread) => {
	const threadStatus = thread?.status;
	if (threadStatus === "errored" || threadStatus === "stopped" || threadStatus === "not_submitted") return "complete";
	if (threadStatus === "done" || threadStatus === "waiting_for_client_tools") return "complete";
	if (shouldEnd) return "complete";
	return "pending";
};
const getStepTitle = (step, isActive) => {
	if (step.type === "thought") return isActive ? "Thinking" : "Thought";
	if (step.type === "modality") return getModalityTitle(step.modality, isActive);
	return "Completed";
};
const SELF_TOOLKIT_NAME = "lb_internal";
const SELF_TOOL_SLUGS = /* @__PURE__ */ new Set([
	MT.CancelSubscription,
	MT.GetSubscriptionStatus,
	MT.CreateMessageFeedbackDraft,
	MT.CreateProductFeedbackDraft,
	MT.GetFeedbackDraftStatus
]);
const SELF_TOOL_COPY = {
	[MT.GetSubscriptionStatus]: {
		titleVerb: "Checked",
		titleNoun: "your subscription",
		descVerb: "Reviewed",
		descNoun: "your plan and billing"
	},
	[MT.CancelSubscription]: {
		titleVerb: "Reviewed",
		titleNoun: "cancellation options",
		descVerb: "Prepared",
		descNoun: "your cancellation steps"
	},
	[MT.CreateMessageFeedbackDraft]: {
		titleVerb: "Drafted",
		titleNoun: "your feedback",
		descVerb: "Prepared",
		descNoun: "a feedback report to review"
	},
	[MT.CreateProductFeedbackDraft]: {
		titleVerb: "Drafted",
		titleNoun: "your feedback",
		descVerb: "Prepared",
		descNoun: "a feedback report to review"
	},
	[MT.GetFeedbackDraftStatus]: {
		titleVerb: "Checked",
		titleNoun: "your feedback status",
		descVerb: "Reviewed",
		descNoun: "whether your feedback was sent"
	}
};
const getSelfToolSlug = (modality) => {
	const slug = toTrimmedString(modality.tool_slug);
	if (slug) return slug;
	if (typeof modality.type === "string" && SELF_TOOL_SLUGS.has(modality.type)) return modality.type;
};
const isSelfToolExecute = (modality) => {
	if (modality.integration_name === SELF_TOOLKIT_NAME) return true;
	const slug = getSelfToolSlug(modality);
	return slug !== void 0 && slug in SELF_TOOL_COPY;
};
const getIntegrationToolName = (step) => {
	if (step.type !== "modality") return void 0;
	const { modality } = step;
	if (modality.type === MT.ChatWithIntegrations || modality.type === MT.InternalIntegrations) {
		const name = modality.tool_name;
		return name ? normalizeIntegrationName(name) : void 0;
	}
	if (modality.type === MT.ToolExecute) {
		if (isSelfToolExecute(modality)) return void 0;
		const slug = toTrimmedString(modality.tool_slug);
		if (slug === TERMINAL_RUN_SLUG) return "Terminal";
		if (slug) return integrationNameFromSlug(slug);
		const integrationName = toTrimmedString(modality.integration_name);
		return integrationName ? normalizeIntegrationName(integrationName) : void 0;
	}
};
const getIntegrationTitle = (modality, isActive) => {
	const toolName = "tool_name" in modality ? modality.tool_name : void 0;
	const label = toolName ? mapToolNameToLabel(toolName) : "integration";
	return `${getTensed("Accessed", isActive)} ${label}`;
};
function modalityTitleChatWithUserContext(modality, isActive) {
	const filters = modality.filters;
	if (filters?.list_meetings_only) return `${getTensed("Searched", isActive)} through your meetings`;
	if (filters?.app === "Littlebird Chat") return `${getTensed("Looked", isActive)} through past conversations`;
	return `${getTensed("Looked", isActive)} through your context`;
}
function modalityTitleRoute(modality, isActive) {
	const routeCode = `code ${modality.code ?? "N/A"}`;
	const durationMs = modality.duration_ms;
	const routeDuration = typeof durationMs === "number" ? `${(durationMs / 1e3).toFixed(2)}s` : "duration N/A";
	const routeTarget = modality.target_agent ?? modality.target_alias ?? "unknown";
	return `${getTensed("Routed", isActive)} to ${routeTarget} (${routeCode}, ${routeDuration})`;
}
function modalityTitleSelfTool(modality, isActive) {
	const slug = getSelfToolSlug(modality);
	const copy = slug ? SELF_TOOL_COPY[slug] : void 0;
	if (copy) return `${getTensed(copy.titleVerb, isActive)} ${copy.titleNoun}`;
	return `${getTensed("Used", isActive)} Littlebird`;
}
function modalityTitleToolExecute(modality, isActive) {
	if (isSelfToolExecute(modality)) return modalityTitleSelfTool(modality, isActive);
	const slug = toTrimmedString(modality.tool_slug);
	const integrationName = toTrimmedString(modality.integration_name);
	if (slug === TERMINAL_RUN_SLUG) return `${getTensed("Used", isActive)} Terminal`;
	if (!slug && integrationName) {
		const label = mapToolNameToLabel(integrationName);
		return `${getTensed("Connecting", isActive)} ${label}`;
	}
	const raw = slug ? integrationNameFromSlug(slug) : "";
	return raw ? mapToolNameToLabel(raw) : "integration";
}
function modalityTitleMcpToolCall(modality, _isActive) {
	const serverName = String(modality.server_name ?? "");
	return serverName.charAt(0).toUpperCase() + serverName.slice(1);
}
function modalityTitleRunSubagent(modality, _isActive) {
	return String(modality.description ?? "").trim() || "Subagent";
}
const BROWSER_ACTION_TITLES = {
	start: ["Started browser", "Starting browser"],
	open: ["Opened page", "Opening page"],
	click: ["Clicked page", "Clicking page"],
	double_click: ["Double-clicked page", "Double-clicking page"],
	right_click: ["Right-clicked page", "Right-clicking page"],
	type: ["Entered text", "Entering text"],
	keypress: ["Pressed key", "Pressing key"],
	move: ["Moved cursor", "Moving cursor"],
	drag: ["Dragged on page", "Dragging on page"],
	wait: ["Waited for page", "Waiting for page"],
	stop: ["Stopped browser", "Stopping browser"],
	navigate: ["Page navigation", "Opening page"],
	click_at: ["Page click", "Clicking page"],
	type_text: ["Text entry", "Entering text"],
	press_key: ["Key press", "Pressing key"],
	scroll: ["Page scroll", "Scrolling page"],
	screenshot: ["Page screenshot", "Capturing screenshot"],
	inspect: ["Page inspection", "Inspecting page"],
	evaluate: ["Ran page JavaScript", "Running page JavaScript"],
	click_ref: ["Element click", "Clicking element"],
	fill: ["Field input", "Filling field"],
	select: ["Option selection", "Selecting option"],
	set_checked: ["Control update", "Updating control"],
	press_ref: ["Element key press", "Pressing element key"],
	wait_for: ["Page wait", "Waiting for page"],
	extract: ["Page read", "Reading page"],
	observe: ["Page inspection", "Inspecting page"],
	act: ["Page action", "Performing page action"],
	act_ref: ["Referenced page action", "Performing referenced action"],
	list_tabs: ["Tab list", "Listing tabs"],
	list_downloads: ["Listed downloads", "Listing downloads"],
	save_download: ["Saved download", "Saving download"],
	upload_file: ["Uploaded file", "Uploading file"],
	save_tab_file: ["Saved file from tab", "Saving file from tab"],
	open_tab: ["Opened tab", "Opening tab"],
	switch_tab: ["Switched tab", "Switching tab"],
	close_tab: ["Closed tab", "Closing tab"],
	refresh_tab: ["Refreshed tab", "Refreshing tab"]
};
function modalityTitleBrowserUse(modality, isActive) {
	const action = String(modality.action ?? "");
	if (action === "navigate" || action === "open" || action === "start") try {
		const navigateInput = action === "navigate" ? modality.navigate_action_input : void 0;
		const url = typeof navigateInput === "object" && navigateInput !== null ? navigateInput.url : modality.url;
		const hostname = new URL(String(url ?? "")).hostname;
		if (hostname) return `${isActive ? "Opening" : "Navigation to"} ${hostname}`;
	} catch {}
	return BROWSER_ACTION_TITLES[action]?.[isActive ? 1 : 0] ?? `${getTensed("Used", isActive)} browser`;
}
function modalityTitleGroupBasics(modality, isActive) {
	switch (modality.type) {
		case MT.ChatWithWebSearch:
		case MT.ChatWithWebSearchMulti: return `${getTensed("Searched", isActive)} the web`;
		case MT.ChatWithWebNavigation: return `${getTensed("Browsed", isActive)} the web`;
		case MT.ChatWithUserContext: return modalityTitleChatWithUserContext(modality, isActive);
		case MT.ChatWithNotes:
		case MT.EditAssistantNotes: return `${getTensed("Updated", isActive)} notes`;
		case MT.SelfKnowledge: return `${getTensed("Used", isActive)} self knowledge`;
		case MT.ChatWithIntegrations:
		case MT.InternalIntegrations: return getIntegrationTitle(modality, isActive);
		case MT.ChatWithPreviousContext: return `${getTensed("Used", isActive)} previous context`;
		case MT.ImageGeneration: return `${getTensed("Generated", isActive)} an image`;
		case MT.WorldModel: return `${getTensed("Accessed", isActive)} your life graph`;
		case MT.TodoWrite: return `${getTensed("Updated", isActive)} tasks`;
		case MT.Route: return modalityTitleRoute(modality, isActive);
		default: return null;
	}
}
function modalityTitleGroupTools(modality, isActive) {
	switch (modality.type) {
		case MT.ReadFile:
		case MT.ReadFileGemini:
		case MT.ReadFileCodex:
		case MT.ReadFileClaude: return `${getTensed("Read", isActive)} file`;
		case MT.EditFile:
		case MT.EditFileClaude: return `${getTensed("Edited", isActive)} file`;
		case MT.ApplyPatch: return `${getTensed("Applied", isActive)} patch`;
		case MT.ListDirectory:
		case MT.ListDir: return `${getTensed("Listed", isActive)} directory`;
		case MT.SearchFileContent:
		case MT.GrepFiles: return `${getTensed("Searched", isActive)} files`;
		case MT.RunShellCommand:
		case MT.ShellCommand: return `${getTensed("Ran", isActive)} shell command`;
		case MT.McpToolCall: return modalityTitleMcpToolCall(modality, isActive);
		case MT.ToolExecute: return modalityTitleToolExecute(modality, isActive);
		case MT.WriteFile: return `${getTensed("Wrote", isActive)} file`;
		case MT.RunShell:
		case MT.Bash: return `${getTensed("Ran", isActive)} command`;
		case MT.RunSandbox: return `${getTensed("Ran", isActive)} sandbox`;
		case MT.GetScreenContext: return `${getTensed("Read", isActive)} screen`;
		case MT.BrowserUse: return modalityTitleBrowserUse(modality, isActive);
		case MT.RunSubagent: return modalityTitleRunSubagent(modality, isActive);
		case MT.CancelSubscription:
		case MT.GetSubscriptionStatus:
		case MT.CreateMessageFeedbackDraft:
		case MT.CreateProductFeedbackDraft:
		case MT.GetFeedbackDraftStatus: return modalityTitleSelfTool(modality, isActive);
		default: return "Completed";
	}
}
const getModalityTitle = (modality, isActive) => {
	if (modality.type === MT.RunSandbox || modality.type === MT.PresentFile) {
		const description = String(modality.description ?? "").trim();
		if (description) return description;
	}
	const label = isActive ? modality.ui_title_active_override : modality.ui_title_override;
	if (typeof label === "string" && label) return label;
	return modalityTitleGroupBasics(modality, isActive) ?? modalityTitleGroupTools(modality, isActive);
};
const truncate = (value, max = 80) => {
	if (value.length <= max) return value;
	return `${value.slice(0, max - 3)}...`;
};
const maybePush = (parts, label, value) => {
	if (value === void 0) return;
	const text = String(value).trim();
	if (!text) return;
	parts.push(`${label}: ${text}`);
};
const getPathSummary = (paths) => {
	if (paths.length === 0) return void 0;
	const visible = paths.slice(0, 3);
	const hiddenCount = paths.length - visible.length;
	if (hiddenCount <= 0) return visible.join(", ");
	return `${visible.join(", ")} (+${hiddenCount} more)`;
};
const summarizeApplyPatch = (patch) => {
	const summary = {
		addCount: 0,
		deleteCount: 0,
		moveCount: 0,
		updateCount: 0,
		paths: []
	};
	if (!patch) return summary;
	const seenPaths = /* @__PURE__ */ new Set();
	const addPath = (path) => {
		if (!path) return;
		const normalized = path.trim();
		if (!normalized || seenPaths.has(normalized)) return;
		seenPaths.add(normalized);
		summary.paths.push(normalized);
	};
	for (const line of patch.split("\n")) {
		const trimmed = line.trim();
		if (trimmed.startsWith("*** Add File:")) {
			summary.addCount += 1;
			addPath(trimmed.slice(13));
			continue;
		}
		if (trimmed.startsWith("*** Delete File:")) {
			summary.deleteCount += 1;
			addPath(trimmed.slice(16));
			continue;
		}
		if (trimmed.startsWith("*** Update File:")) {
			summary.updateCount += 1;
			addPath(trimmed.slice(16));
			continue;
		}
		if (trimmed.startsWith("*** Move to:")) {
			summary.moveCount += 1;
			addPath(trimmed.slice(12));
		}
	}
	return summary;
};
function editToolDescriptionReadFile(modality, isActive) {
	const details = [];
	maybePush(details, isActive ? "reading" : "read", modality.file_path);
	maybePush(details, "offset", modality.offset);
	maybePush(details, "limit", modality.limit);
	if (modality.type === MT.ReadFileCodex) maybePush(details, "mode", modality.mode);
	return details.join(" | ");
}
function editToolDescriptionEditFile(modality, isActive) {
	const details = [];
	maybePush(details, isActive ? "editing" : "edited", modality.file_path);
	maybePush(details, "replacements", modality.expected_replacements);
	return details.join(" | ");
}
function editToolDescriptionApplyPatch(modality) {
	const details = [];
	const summary = summarizeApplyPatch(modality.patch);
	if (summary.addCount > 0) details.push(`${summary.addCount} add`);
	if (summary.updateCount > 0) details.push(`${summary.updateCount} update`);
	if (summary.deleteCount > 0) details.push(`${summary.deleteCount} delete`);
	if (summary.moveCount > 0) details.push(`${summary.moveCount} move`);
	const pathSummary = getPathSummary(summary.paths);
	if (pathSummary) details.push(`files: ${pathSummary}`);
	return details.join(" | ");
}
function editToolDescriptionListDirectory(modality) {
	const details = [];
	maybePush(details, "dir", modality.dir_path);
	if (modality.ignore?.length) details.push(`ignore: ${modality.ignore.join(", ")}`);
	return details.join(" | ");
}
function editToolDescriptionListDir(modality) {
	const details = [];
	maybePush(details, "dir", modality.dir_path ?? ".");
	maybePush(details, "depth", modality.depth);
	maybePush(details, "limit", modality.limit);
	maybePush(details, "offset", modality.offset);
	return details.join(" | ");
}
function editToolDescriptionSearchFileContent(modality) {
	const details = [];
	maybePush(details, "pattern", truncate(String(modality.pattern ?? ""), 80));
	maybePush(details, "path", modality.dir_path);
	maybePush(details, "include", modality.include);
	return details.join(" | ");
}
function editToolDescriptionGrepFiles(modality) {
	const details = [];
	maybePush(details, "pattern", truncate(String(modality.pattern ?? ""), 80));
	maybePush(details, "path", modality.path);
	maybePush(details, "include", modality.include);
	maybePush(details, "limit", modality.limit);
	return details.join(" | ");
}
function editToolDescriptionRunShellCommand(modality) {
	const details = [];
	maybePush(details, "cmd", truncate(String(modality.command ?? ""), 90));
	maybePush(details, "dir", modality.dir_path);
	if (Boolean(modality.is_background)) details.push("background");
	return details.join(" | ");
}
function editToolDescriptionShellCommand(modality) {
	const details = [];
	maybePush(details, "cmd", truncate(String(modality.command ?? ""), 90));
	maybePush(details, "dir", modality.workdir);
	maybePush(details, "timeout ms", modality.timeout_ms);
	return details.join(" | ");
}
function editToolDescriptionWriteFile(modality, isActive) {
	const details = [];
	maybePush(details, isActive ? "writing" : "wrote", modality.file_path);
	maybePush(details, "mode", modality.mode);
	return details.join(" | ");
}
function editToolDescriptionRunShell(modality) {
	const details = [];
	maybePush(details, "cmd", truncate(String(modality.command ?? ""), 90));
	return details.join(" | ");
}
function editToolDescriptionBash(modality) {
	const details = [];
	maybePush(details, "cmd", truncate(String(modality.command ?? ""), 90));
	return details.join(" | ");
}
const getEditToolDescription = (modality, isActive) => {
	switch (modality.type) {
		case MT.ReadFile:
		case MT.ReadFileGemini:
		case MT.ReadFileCodex:
		case MT.ReadFileClaude: return editToolDescriptionReadFile(modality, isActive);
		case MT.EditFile:
		case MT.EditFileClaude: return editToolDescriptionEditFile(modality, isActive);
		case MT.ApplyPatch: return editToolDescriptionApplyPatch(modality);
		case MT.ListDirectory: return editToolDescriptionListDirectory(modality);
		case MT.ListDir: return editToolDescriptionListDir(modality);
		case MT.SearchFileContent: return editToolDescriptionSearchFileContent(modality);
		case MT.GrepFiles: return editToolDescriptionGrepFiles(modality);
		case MT.RunShellCommand: return editToolDescriptionRunShellCommand(modality);
		case MT.ShellCommand: return editToolDescriptionShellCommand(modality);
		case MT.WriteFile: return editToolDescriptionWriteFile(modality, isActive);
		case MT.RunShell: return editToolDescriptionRunShell(modality);
		case MT.Bash: return editToolDescriptionBash(modality);
		case MT.RunSandbox: return editToolDescriptionRunShell(modality);
		default: return;
	}
};
const getToolDescription = (toolName, isActive) => {
	switch (toolName) {
		case "gmail": return `${getTensed("Retrieved", isActive)} your emails and messages`;
		case "googlecalendar": return `${getTensed("Checked", isActive)} your calendar events`;
		case "googledrive": return `${getTensed("Searched", isActive)} your files and folders`;
		case "googledocs": return `${getTensed("Searched", isActive)} through your documents`;
		case "googlesheets": return `${getTensed("Pulled", isActive)} your spreadsheets`;
		case "notion": return `${getTensed("Searched", isActive)} your pages and databases`;
		case "linear": return `${getTensed("Reviewed", isActive)} your issues and projects`;
		case "github": return `${getTensed("Fetched", isActive)} your repositories and pull requests`;
		case "outlook": return `${getTensed("Accessed", isActive)} your Outlook messages and events`;
		default: return `${getTensed("Accessed", isActive)} integration data`;
	}
};
const getIntegrationActionDescription = (modality, isActive) => {
	const toolName = "tool_name" in modality ? modality.tool_name : void 0;
	return getToolDescription(toolName, isActive);
};
function modalityDescriptionMcpToolCall(modality) {
	const serverName = String(modality.server_name ?? "");
	const toolName = String(modality.tool_name ?? "");
	const stripped = serverName ? stripIntegrationPrefix(serverName, toolName) : toolName;
	return humanizeMcpToolName(stripped) || void 0;
}
function modalityDescriptionSelfTool(modality, isActive) {
	const slug = getSelfToolSlug(modality);
	const copy = slug ? SELF_TOOL_COPY[slug] : void 0;
	if (copy) return `${getTensed(copy.descVerb, isActive)} ${copy.descNoun}`;
}
const TOOL_EXECUTE_ACTION_COPY = {
	GMAIL_FETCH_EMAILS: {
		verb: "Searched",
		noun: "your emails"
	},
	GMAIL_LIST_THREADS: {
		verb: "Listed",
		noun: "email threads"
	},
	GMAIL_LIST_LABELS: {
		verb: "Listed",
		noun: "Gmail labels"
	},
	GMAIL_LIST_MESSAGES: {
		verb: "Listed",
		noun: "email messages"
	},
	GMAIL_FETCH_MESSAGE_BY_THREAD_ID: {
		verb: "Read",
		noun: "an email thread"
	},
	GMAIL_SEND_EMAIL: {
		verb: "Sent",
		noun: "an email"
	},
	GOOGLECALENDAR_EVENTS_LIST: {
		verb: "Listed",
		noun: "calendar events"
	},
	GOOGLECALENDAR_FIND_EVENT: {
		verb: "Searched",
		noun: "calendar events"
	},
	GOOGLECALENDAR_LIST_CALENDARS: {
		verb: "Listed",
		noun: "calendars"
	},
	GOOGLECALENDAR_FIND_FREE_SLOTS: {
		verb: "Checked",
		noun: "availability"
	},
	GOOGLECALENDAR_EVENTS_INSTANCES: {
		verb: "Listed",
		noun: "recurring event instances"
	},
	GOOGLECALENDAR_GET_CALENDAR: {
		verb: "Read",
		noun: "calendar details"
	},
	GOOGLECALENDAR_SETTINGS_LIST: {
		verb: "Read",
		noun: "calendar settings"
	}
};
const getToolExecuteActionDescription = (slug, integrationName, isActive) => {
	const copy = TOOL_EXECUTE_ACTION_COPY[slug];
	if (copy) return `${getTensed(copy.verb, isActive)} ${copy.noun}`;
	const extractedToolName = extractToolName(slug);
	if (extractedToolName !== void 0) return humanizeMcpToolName(extractedToolName) || void 0;
	if (!integrationName) return void 0;
	const integrationPrefix = integrationName?.replace(/[^a-z0-9]/gi, "") ?? "";
	const hasIntegrationPrefix = integrationPrefix.length > 0 && slug.slice(0, integrationPrefix.length).toLowerCase() === integrationPrefix.toLowerCase();
	if (hasIntegrationPrefix && slug.length === integrationPrefix.length) return;
	const toolName = hasIntegrationPrefix && /[A-Z]/.test(slug[integrationPrefix.length] ?? "") ? slug.slice(integrationPrefix.length) : slug;
	return humanizeMcpToolName(toolName) || void 0;
};
function modalityDescriptionToolExecute(modality, isActive) {
	if (isSelfToolExecute(modality)) return modalityDescriptionSelfTool(modality, isActive);
	const slug = toTrimmedString(modality.tool_slug);
	if (slug === TERMINAL_RUN_SLUG) return void 0;
	const integrationName = toTrimmedString(modality.integration_name);
	if (slug && isMcpIdentifier(slug)) {
		const parsed = parseMcpSlug(slug);
		if (parsed) {
			const stripped = stripIntegrationPrefix(parsed.server, parsed.tool);
			return humanizeMcpToolName(stripped) || void 0;
		}
		return;
	}
	if (isMcpIdentifier(integrationName)) return void 0;
	if (slug) {
		const description = getToolExecuteActionDescription(slug, integrationName, isActive);
		if (description) return description;
	}
	const name = integrationName ? normalizeIntegrationName(integrationName) : void 0;
	return getToolDescription(name, isActive);
}
const getModalityDescription = (modality, isActive) => {
	switch (modality.type) {
		case MT.SelfKnowledge: return `${getTensed("Used", isActive)} knowledge about capabilities and features`;
		case MT.WorldModel:
			if (modality.query) return `${getTensed("Retrieved", isActive)} context for: ${modality.query}`;
			return `${getTensed("Retrieved", isActive)} knowledge about your personal network and relationships`;
		case MT.ChatWithIntegrations:
		case MT.InternalIntegrations: return getIntegrationActionDescription(modality, isActive);
		case MT.Route: return modality.reasoning ?? `${getTensed("Routed", isActive)} by classifier decision`;
		case MT.ReadFile:
		case MT.ReadFileGemini:
		case MT.ReadFileCodex:
		case MT.ReadFileClaude:
		case MT.EditFile:
		case MT.EditFileClaude:
		case MT.ApplyPatch:
		case MT.ListDirectory:
		case MT.ListDir:
		case MT.SearchFileContent:
		case MT.GrepFiles:
		case MT.RunShellCommand:
		case MT.ShellCommand:
		case MT.WriteFile:
		case MT.Bash:
		case MT.RunShell:
		case MT.RunSandbox: return getEditToolDescription(modality, isActive);
		case MT.McpToolCall: return modalityDescriptionMcpToolCall(modality);
		case MT.ToolExecute: return modalityDescriptionToolExecute(modality, isActive);
		case MT.RunSubagent: return String(modality.description ?? "").trim() || void 0;
		case MT.CancelSubscription:
		case MT.GetSubscriptionStatus:
		case MT.CreateMessageFeedbackDraft:
		case MT.CreateProductFeedbackDraft:
		case MT.GetFeedbackDraftStatus: return modalityDescriptionSelfTool(modality, isActive);
		default: return;
	}
};
const getWebSearchQueries = (modality) => {
	if (modality.type === MT.ChatWithWebSearchMulti) return (modality.search_queries ?? []).filter(Boolean);
	if (modality.type === MT.ChatWithWebSearch) {
		const q = modality.search_query;
		return q ? [q] : [];
	}
	return [];
};
const formatFilterKey = (key) => key.split("_").filter(Boolean).map((part, index) => {
	const lowered = part.toLowerCase();
	if (index === 0) return lowered.charAt(0).toUpperCase() + lowered.slice(1);
	return lowered;
}).join(" ");
const formatUserContextFilterChip = (key, value) => {
	if (key === "list_meetings_only") return value ? "Meetings only" : void 0;
	const text = toTrimmedText(value);
	if (!text) return void 0;
	if (key === "app") return `App: ${text}`;
	if (key === "data_source") return `Source: ${text}`;
	return `${formatFilterKey(key)}: ${text}`;
};
const getDateRangeChip = (dateRange) => {
	if (!dateRange || typeof dateRange !== "object") return void 0;
	const dateRangeObj = dateRange;
	const start = toTrimmedText(dateRangeObj.start);
	const end = toTrimmedText(dateRangeObj.end);
	if (start && end) return `Date: ${start} to ${end}`;
	if (start) return `From: ${start}`;
	if (end) return `Until: ${end}`;
};
const getUserContextChipGroups = (modality) => {
	if (modality.type !== MT.ChatWithUserContext) return {
		queryChips: [],
		filterChips: []
	};
	const queryChips = [...toArray(modality.search_queries), ...toArray(modality.search_queries_messages)].map(toTrimmedString).filter((query) => query !== void 0);
	const filters = modality.filters && typeof modality.filters === "object" ? Object.entries(modality.filters).map(([key, value]) => formatUserContextFilterChip(key, value)).filter((chip) => Boolean(chip)) : [];
	const dateChip = getDateRangeChip(modality.date_range);
	if (dateChip) filters.push(dateChip);
	return {
		queryChips,
		filterChips: queryChips.length > 0 ? [] : filters.length > 0 ? filters : ["All context"]
	};
};
const getDomain = (url) => {
	try {
		return new URL(url).hostname;
	} catch {
		return url;
	}
};
function editChipsReadFile(modality) {
	const chips = [String(modality.file_path ?? "")];
	if (modality.type === MT.ReadFileCodex && modality.mode) chips.push(`mode ${modality.mode}`);
	return { chips };
}
function editChipsEditFile(modality) {
	const chips = [String(modality.file_path ?? "")];
	if (modality.expected_replacements !== void 0) chips.push(`replacements ${modality.expected_replacements}`);
	return { chips };
}
function editChipsApplyPatch(modality) {
	const summary = summarizeApplyPatch(modality.patch);
	const chips = [];
	if (summary.addCount > 0) chips.push(`add ${summary.addCount}`);
	if (summary.updateCount > 0) chips.push(`update ${summary.updateCount}`);
	if (summary.deleteCount > 0) chips.push(`delete ${summary.deleteCount}`);
	if (summary.moveCount > 0) chips.push(`move ${summary.moveCount}`);
	chips.push(...summary.paths.slice(0, 3));
	if (summary.paths.length > 3) chips.push(`+${summary.paths.length - 3} more`);
	return { chips };
}
function editChipsListDirectory(modality) {
	const chips = [String(modality.dir_path ?? "")];
	if (modality.ignore?.length) chips.push(`ignore ${modality.ignore.join(", ")}`);
	return { chips };
}
function editChipsListDir(modality) {
	const dirPath = modality.dir_path;
	const chips = [dirPath !== null && dirPath !== void 0 ? String(dirPath) : "."];
	if (modality.depth !== void 0) chips.push(`depth ${modality.depth}`);
	if (modality.limit !== void 0) chips.push(`limit ${modality.limit}`);
	if (modality.offset !== void 0) chips.push(`offset ${modality.offset}`);
	return { chips };
}
function editChipsSearchFileContent(modality) {
	const chips = [String(modality.pattern ?? "")];
	if (modality.include) chips.push(`include ${modality.include}`);
	return {
		context: modality.dir_path,
		chips
	};
}
function editChipsGrepFiles(modality) {
	const chips = [String(modality.pattern ?? "")];
	if (modality.include) chips.push(`include ${modality.include}`);
	if (modality.limit !== void 0) chips.push(`limit ${modality.limit}`);
	return {
		context: modality.path,
		chips
	};
}
function editChipsRunShellCommand(modality, includeCommand) {
	const chips = includeCommand ? [String(modality.command ?? "")] : [];
	if (Boolean(modality.is_background)) chips.push("background");
	return {
		context: modality.dir_path,
		chips
	};
}
function editChipsShellCommand(modality, includeCommand) {
	const chips = includeCommand ? [String(modality.command ?? "")] : [];
	if (modality.timeout_ms !== void 0) chips.push(`timeout ${modality.timeout_ms}ms`);
	return {
		context: modality.workdir,
		chips
	};
}
function editChipsWriteFile(modality) {
	return { chips: [String(modality.file_path ?? "")] };
}
function editChipsRunShellOrBash(modality, includeCommand) {
	return { chips: includeCommand ? [String(modality.command ?? "")] : [] };
}
const getEditToolChips = (modality, includeCommand = true) => {
	switch (modality.type) {
		case MT.ReadFile:
		case MT.ReadFileGemini:
		case MT.ReadFileCodex:
		case MT.ReadFileClaude: return editChipsReadFile(modality);
		case MT.EditFile:
		case MT.EditFileClaude: return editChipsEditFile(modality);
		case MT.ApplyPatch: return editChipsApplyPatch(modality);
		case MT.ListDirectory: return editChipsListDirectory(modality);
		case MT.ListDir: return editChipsListDir(modality);
		case MT.SearchFileContent: return editChipsSearchFileContent(modality);
		case MT.GrepFiles: return editChipsGrepFiles(modality);
		case MT.RunShellCommand: return editChipsRunShellCommand(modality, includeCommand);
		case MT.ShellCommand: return editChipsShellCommand(modality, includeCommand);
		case MT.WriteFile: return editChipsWriteFile(modality);
		case MT.RunShell:
		case MT.Bash:
		case MT.RunSandbox: return editChipsRunShellOrBash(modality, includeCommand);
		default: return { chips: [] };
	}
};
const EDIT_TOOL_TYPES = /* @__PURE__ */ new Set([
	MT.ReadFile,
	MT.ReadFileGemini,
	MT.ReadFileCodex,
	MT.ReadFileClaude,
	MT.EditFile,
	MT.EditFileClaude,
	MT.ApplyPatch,
	MT.ListDirectory,
	MT.ListDir,
	MT.SearchFileContent,
	MT.GrepFiles,
	MT.RunShellCommand,
	MT.ShellCommand,
	MT.WriteFile,
	MT.RunShell,
	MT.Bash,
	MT.RunSandbox
]);
function filterDisplayableModalities(modalities, options) {
	const showSelfKnowledge = options?.showSelfKnowledge ?? false;
	return (modalities ?? []).filter((modality) => {
		if (!showSelfKnowledge && modality.type === MT.SelfKnowledge) return false;
		if (modality.type === MT.ChatWithPreviousContext) return false;
		if (modality.type === MT.ImageGeneration) return false;
		if (modality.type.startsWith("client__")) return false;
		if (modality.type === MT.ToolSearch) return false;
		if (modality.type === MT.ToolExecute && !modality.tool_slug && !modality.integration_name) return false;
		return true;
	});
}
/** Matches desktop `MODALITY_REGISTRY` expandability (has body renderer, not bodyless). */
const EXPANDABLE_MODALITY_TYPES = /* @__PURE__ */ new Set([
	MT.ChatWithUserContext,
	MT.ChatWithWebSearch,
	MT.ChatWithWebSearchMulti,
	MT.ChatWithWebNavigation,
	MT.ChatWithNotes,
	MT.EditAssistantNotes,
	MT.SelfKnowledge,
	MT.ChatWithIntegrations,
	MT.InternalIntegrations,
	MT.ImageGeneration,
	MT.WorldModel,
	MT.Route,
	MT.TodoWrite,
	MT.McpToolCall,
	MT.ToolExecute,
	MT.RunSubagent,
	...EDIT_TOOL_TYPES
]);
function modalityHasExpandableContent(modality) {
	if (modality.type === MT.GetScreenContext) return false;
	return EXPANDABLE_MODALITY_TYPES.has(modality.type);
}
//#endregion
//#region src/reasoning/loader-activity.ts
const LOADER_ACTIVITY_BY_MODALITY = /* @__PURE__ */ new Map([
	[MT.ChatWithWebSearch, "search"],
	[MT.ChatWithWebSearchMulti, "search"],
	[MT.ChatWithWebNavigation, "search"],
	[MT.ChatWithUserContext, "search"],
	[MT.ChatWithPreviousContext, "search"],
	[MT.ToolSearch, "search"],
	[MT.SearchFileContent, "search"],
	[MT.GrepFiles, "search"],
	[MT.WorldModel, "search"],
	[MT.ReadFile, "read"],
	[MT.ReadFileGemini, "read"],
	[MT.ReadFileCodex, "read"],
	[MT.ReadFileClaude, "read"],
	[MT.ListDirectory, "read"],
	[MT.ListDir, "read"],
	[MT.GetScreenContext, "read"],
	[MT.ChatWithNotes, "read"],
	[MT.Memory, "read"],
	[MT.SelfKnowledge, "read"],
	[MT.Todo, "read"],
	[MT.EditFile, "write"],
	[MT.EditFileClaude, "write"],
	[MT.ApplyPatch, "write"],
	[MT.WriteFile, "write"],
	[MT.RunShellCommand, "write"],
	[MT.ShellCommand, "write"],
	[MT.RunShell, "write"],
	[MT.Bash, "write"],
	[MT.RunSandbox, "write"],
	[MT.PresentFile, "write"],
	[MT.TodoWrite, "write"],
	[MT.EditAssistantNotes, "write"],
	[MT.ImageGeneration, "write"],
	[MT.ToolExecute, "write"],
	[MT.McpToolCall, "write"],
	[MT.RunSubagent, "write"],
	[MT.ChatWithIntegrations, "write"],
	[MT.InternalIntegrations, "write"],
	[MT.Route, "think"]
]);
const deriveLoaderActivity = (modalities) => {
	const lastModalityType = modalities?.[modalities.length - 1]?.type;
	return lastModalityType && LOADER_ACTIVITY_BY_MODALITY.get(lastModalityType) || "think";
};
//#endregion
//#region src/reasoning/display-selection.ts
function selectReasoningDisplayEntries(steps, status, getDisplayMetadata) {
	const metadataByIndex = steps.map(getDisplayMetadata);
	let activeIndex = -1;
	if (status === "pending") {
		for (let index = metadataByIndex.length - 1; index >= 0; index -= 1) if (metadataByIndex[index].visible) {
			activeIndex = index;
			break;
		}
	}
	const displayEntries = [];
	let previousCollapseKey;
	steps.forEach((step, index) => {
		const metadata = metadataByIndex[index];
		if (!metadata.visible) return;
		const isActive = index === activeIndex;
		const { collapseKey } = metadata;
		if (!isActive && collapseKey !== void 0 && collapseKey === previousCollapseKey) {
			displayEntries[displayEntries.length - 1].count += 1;
			return;
		}
		displayEntries.push({
			step,
			count: 1
		});
		previousCollapseKey = collapseKey;
	});
	return displayEntries;
}
function selectReasoningDisplaySteps(steps, status, getDisplayMetadata) {
	return selectReasoningDisplayEntries(steps, status, getDisplayMetadata).map(({ step }) => step);
}
//#endregion
//#region src/reasoning/todo-utils.ts
const TODO_WRITE = "todo_write";
const STATUS_COMPLETED = "completed";
const STATUS_CANCELLED = "cancelled";
const STATUS_IN_PROGRESS = "in_progress";
function getHeaderItem(items) {
	const active = items.find((i) => i.status === STATUS_IN_PROGRESS);
	if (active) return active;
	return [...items].reverse().find((i) => i.status === STATUS_COMPLETED || i.status === STATUS_CANCELLED);
}
function extractLatestTodoWriteItems(messages) {
	for (let i = messages.length - 1; i >= 0; i--) {
		const modalities = messages[i].modalitiesUsed ?? [];
		for (let j = modalities.length - 1; j >= 0; j--) {
			const mod = modalities[j];
			if (mod.type === TODO_WRITE) return mod.todos ?? [];
		}
	}
	return null;
}
function getStaticTodoWriteItems(modality) {
	if (modality.type !== TODO_WRITE) return [];
	return modality.todos ?? [];
}
function getTodoWriteSummary(items) {
	return {
		doneCount: items.filter((i) => i.status === STATUS_COMPLETED || i.status === STATUS_CANCELLED).length,
		totalCount: items.length,
		headerItem: getHeaderItem(items)
	};
}
//#endregion
//#region src/reasoning/correlate-tool-calls.ts
const SUBAGENT_REQUEST_ID_PREFIX = "subagent:";
const RUN_SUBAGENT_TYPE = "run_subagent";
const TOOL_EXECUTE_TYPE = "tool_execute";
const DEVICE_TOOLKIT_PREFIX = "device:";
/**
* A device-addressed `tool_execute` step (`integration_name` = `device:<toolkit>@<device>`).
* Its tool call is keyed by the inner `tool_slug`, not by `tool_execute`: the live
* `client_tool_call_request` and the synced error entry for a call rejected before
* dispatch (BE-3143) both carry `tool_name: <tool_slug>`. Other `tool_execute` steps
* (integrations) have no tool call to bind, so they stay uncorrelated.
*/
function deviceToolExecuteMatchKey(modality) {
	const integrationName = modality.integration_name;
	const toolSlug = modality.tool_slug;
	if (typeof integrationName !== "string" || !integrationName.startsWith(DEVICE_TOOLKIT_PREFIX) || typeof toolSlug !== "string" || !toolSlug) return;
	return {
		toolName: toolSlug,
		argKey: "command",
		argValue: commandFromArgumentsJson(modality.arguments)
	};
}
/** `command` from a JSON-string argument blob; '' when absent or malformed (BE-3143 bad-JSON case). */
function commandFromArgumentsJson(raw) {
	if (typeof raw !== "string") return "";
	let parsed;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return "";
	}
	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return "";
	const value = parsed.command;
	return typeof value === "string" ? value : "";
}
function toolMatchKeyForCorrelatableModality(modality) {
	switch (modality.type) {
		case "write_file": return {
			toolName: "write_file",
			argKey: "file_path",
			argValue: String(modality.file_path ?? "")
		};
		case "edit_file": return {
			toolName: "edit_file",
			argKey: "file_path",
			argValue: String(modality.file_path ?? "")
		};
		case "read_file": return {
			toolName: "read_file",
			argKey: "file_path",
			argValue: String(modality.file_path ?? "")
		};
		case "bash": return {
			toolName: "bash",
			argKey: "command",
			argValue: String(modality.command ?? "")
		};
		case "run_sandbox": return {
			toolName: "run_sandbox",
			argKey: "command",
			argValue: String(modality.command ?? "")
		};
		case "browser_sign_in_start": return {
			toolName: "browser_sign_in_start",
			argKey: "url",
			argValue: String(modality.url ?? "")
		};
		case RUN_SUBAGENT_TYPE: return {
			toolName: "run_subagent",
			argKey: "description",
			argValue: String(modality.description ?? "")
		};
		case TOOL_EXECUTE_TYPE: return deviceToolExecuteMatchKey(modality);
		default: return;
	}
}
function correlateToolCalls(modalities, toolCalls) {
	const out = /* @__PURE__ */ new Map();
	if (!toolCalls || toolCalls.length === 0) return out;
	const buckets = /* @__PURE__ */ new Map();
	toolCalls.forEach((tc, i) => {
		const bucket = buckets.get(tc.tool_name) ?? [];
		bucket.push(i);
		buckets.set(tc.tool_name, bucket);
	});
	const consumed = /* @__PURE__ */ new Set();
	modalities.forEach((modality, modalityIndex) => {
		const match = toolMatchKeyForCorrelatableModality(modality);
		if (!match) return;
		const bucket = buckets.get(match.toolName);
		if (!bucket) return;
		const isSignIn = modality.type === "browser_sign_in_start";
		const matchesByToolId = (modality.type === RUN_SUBAGENT_TYPE || isSignIn) && Boolean(modality.tool_id);
		let chosenIdx = matchesByToolId ? bucket.find((i) => !consumed.has(i) && String(toolCalls[i].tool_id ?? "") === String(modality.tool_id)) : void 0;
		if (isSignIn && matchesByToolId && chosenIdx === void 0) return;
		if (chosenIdx === void 0 && modality.type === RUN_SUBAGENT_TYPE && modality.tool_index != null && modality.tool_index !== "") {
			const requestId = `${SUBAGENT_REQUEST_ID_PREFIX}${modality.tool_index}`;
			chosenIdx = bucket.find((i) => !consumed.has(i) && String(toolCalls[i].request_id ?? "") === requestId);
		}
		chosenIdx ??= bucket.find((i) => !consumed.has(i) && String(toolCalls[i].arguments[match.argKey] ?? "") === match.argValue);
		if (chosenIdx === void 0) chosenIdx = bucket.find((i) => !consumed.has(i));
		if (chosenIdx === void 0) return;
		consumed.add(chosenIdx);
		out.set(modalityIndex, toolCalls[chosenIdx]);
	});
	return out;
}
function buildToolCallByStepIdMap(messages, filterDisplayableModalities) {
	const toolCallByStepId = /* @__PURE__ */ new Map();
	for (const message of messages) {
		if (message.role !== "assistant" || !message.toolCalls?.length) continue;
		const displayable = filterDisplayableModalities(message.modalitiesUsed);
		const correlated = correlateToolCalls(displayable, message.toolCalls);
		displayable.forEach((modality, modalityIndex) => {
			const tc = correlated.get(modalityIndex);
			if (tc) toolCallByStepId.set(`modality-${message.id}-${modality.type}-${modalityIndex}`, tc);
		});
	}
	return toolCallByStepId;
}
const slotKey = (messageId, index) => `${messageId}\u0000${index}`;
/**
* Answers whether `message.toolCalls[index]` was consumed by reasoning
* correlation, meaning a modality step already renders it. Matches on array
* index rather than object identity, which MobX proxy wrappers do not preserve.
*/
function buildConsumedToolCallLookup(messages, filterDisplayableModalities) {
	const slots = /* @__PURE__ */ new Set();
	for (const message of messages) {
		if (message.role !== "assistant" || !message.toolCalls?.length) continue;
		const calls = message.toolCalls;
		const correlated = correlateToolCalls(filterDisplayableModalities(message.modalitiesUsed), calls);
		for (const tc of correlated.values()) {
			let idx = calls.indexOf(tc);
			if (idx === -1) idx = calls.findIndex((c) => c.tool_name === tc.tool_name && (c.request_id ?? null) === (tc.request_id ?? null) && (c.tool_id ?? null) === (tc.tool_id ?? null));
			if (idx !== -1) slots.add(slotKey(message.id, idx));
		}
	}
	return (messageId, index) => slots.has(slotKey(messageId, index));
}
function enrichReasoningStepsWithToolCalls(steps, toolCallByStepId) {
	if (toolCallByStepId.size === 0) return steps;
	return steps.map((step) => {
		if (step.type !== "modality") return step;
		const tc = toolCallByStepId.get(step.id);
		return tc ? {
			...step,
			toolCall: tc
		} : step;
	});
}
//#endregion
//#region src/reasoning/opens-turn.ts
/**
* Whether a message opens a turn: a user message, unless the user sent it into a running turn or
* the server refused it because a reply held the chat, so no reply answers it
*/
const opensTurn = (message) => message.role === "user" && !message.joinedTurn && !message.refusedAsBusy;
//#endregion
//#region src/reasoning/message-grouping.ts
/**
* A thought is only worth rendering when it has real content. Empty/whitespace
* strings and the literal sentinel `"None"` (e.g. a stringified Python `None`
* leaking from upstream) must not create a Thought step or a stray "None"
* bubble in chat.
*/
const isMeaningfulThought = (content) => {
	const trimmed = content?.trim();
	return !!trimmed && trimmed !== "None";
};
/** Chat statuses considered "terminal" — no more streaming, mark as complete. */
const TERMINAL_THREAD_STATUSES = /* @__PURE__ */ new Set([
	"errored",
	"stopped",
	"not_submitted",
	"done",
	"waiting_for_client_tools"
]);
const STATUS_STREAMING = "streaming";
const isThreadTerminal = (thread) => {
	const status = thread?.status;
	if (!status) return false;
	return TERMINAL_THREAD_STATUSES.has(status);
};
const TODO_WRITE_TYPE = "todo_write";
const toDate = (value) => value instanceof Date ? value : new Date(value);
const hasVisibleAssistantContent = (message) => {
	if (message.content?.trim()) return true;
	const fileIds = message.fileIds;
	return Array.isArray(fileIds) && fileIds.length > 0;
};
const isModalityStep = (step) => step.type === "modality";
const isTodoWriteStep = (step) => isModalityStep(step) && step.modality.type === TODO_WRITE_TYPE;
const createAccumulator = () => ({
	steps: [],
	currentTurnTodoWriteStep: void 0
});
const flushAccumulator = (acc, options) => {
	const drained = acc.steps;
	acc.steps = [];
	if (options?.resetTodoMerge) acc.currentTurnTodoWriteStep = void 0;
	return drained;
};
const mergeAdjacentThoughtSteps = (steps) => {
	const merged = [];
	for (const step of steps) {
		const prev = merged[merged.length - 1];
		if (step.type === "thought" && prev?.type === "thought") {
			const combined = [prev.hiddenContent?.trim(), step.hiddenContent?.trim()].filter(Boolean).join("\n\n");
			merged[merged.length - 1] = {
				...prev,
				hiddenContent: combined
			};
			continue;
		}
		merged.push(step);
	}
	return merged;
};
const buildFinalizeReasoning = (chronologicalMessages, thread) => {
	const newestMessage = chronologicalMessages[chronologicalMessages.length - 1];
	const threadIsTerminal = isThreadTerminal(thread);
	return (inheritedSteps, ownSteps, message, options) => {
		const isTailAssistantStreaming = !options?.forceComplete && !threadIsTerminal && message.role === "assistant" && (options?.mergedHiddenStreamingTail || message === newestMessage && message.status === STATUS_STREAMING);
		const mergedInherited = mergeAdjacentThoughtSteps(inheritedSteps);
		const mergedOwn = mergeAdjacentThoughtSteps(ownSteps);
		return {
			steps: mergeAdjacentThoughtSteps([...mergedInherited, ...mergedOwn]),
			inheritedSteps: mergedInherited,
			ownSteps: mergedOwn,
			status: isTailAssistantStreaming ? "pending" : "complete"
		};
	};
};
const buildFinalizeStandaloneReasoning = (thread) => {
	const threadIsTerminal = isThreadTerminal(thread);
	return (steps, options) => {
		const isTailStreaming = !options?.forceComplete && !threadIsTerminal;
		const mergedSteps = mergeAdjacentThoughtSteps(steps);
		return {
			id: `reasoning-${mergedSteps[0]?.id ?? "standalone"}`,
			beforeMessageId: options?.beforeMessageId,
			steps: mergedSteps,
			status: isTailStreaming ? "pending" : "complete"
		};
	};
};
const partitionTodoSteps = (steps) => {
	const remaining = [];
	const todos = [];
	for (const step of steps) if (isTodoWriteStep(step)) todos.push(step);
	else remaining.push(step);
	return {
		remaining,
		todos
	};
};
const rebuildEntry = (inheritedSteps, ownSteps, status) => ({
	steps: [...inheritedSteps, ...ownSteps],
	inheritedSteps,
	ownSteps,
	status
});
const rebalanceTurnTodoWritesToLatestVisible = (displayMessages, reasoningData) => {
	const moveTurnTodoSteps = (turnStart, turnEndExclusive) => {
		let latestVisibleIdx = -1;
		for (let i = turnEndExclusive - 1; i >= turnStart; i--) if (displayMessages[i].role === "assistant") {
			latestVisibleIdx = i;
			break;
		}
		if (latestVisibleIdx < turnStart) return;
		const targetId = displayMessages[latestVisibleIdx].id;
		const stepsToReanchor = [];
		for (let i = turnStart; i <= latestVisibleIdx; i++) {
			const message = displayMessages[i];
			if (message.role !== "assistant") continue;
			const entry = reasoningData.get(message.id);
			if (!entry) continue;
			const inheritedSplit = partitionTodoSteps(entry.inheritedSteps);
			const ownSplit = partitionTodoSteps(entry.ownSteps);
			stepsToReanchor.push(...inheritedSplit.todos, ...ownSplit.todos);
			if (inheritedSplit.todos.length === 0 && ownSplit.todos.length === 0) continue;
			reasoningData.set(message.id, rebuildEntry(inheritedSplit.remaining, ownSplit.remaining, entry.status));
		}
		if (stepsToReanchor.length === 0) return;
		const targetEntry = reasoningData.get(targetId);
		if (!targetEntry) return;
		reasoningData.set(targetId, rebuildEntry([...targetEntry.inheritedSteps, ...stepsToReanchor], targetEntry.ownSteps, targetEntry.status));
	};
	let turnStart = 0;
	for (let i = 0; i < displayMessages.length; i++) if (opensTurn(displayMessages[i])) {
		moveTurnTodoSteps(turnStart, i);
		turnStart = i + 1;
	}
	moveTurnTodoSteps(turnStart, displayMessages.length);
};
const collectThreadTodoWriteSteps = (displayMessages, reasoningData, standaloneReasoningItems) => {
	const todoSteps = [];
	for (const message of displayMessages) {
		if (message.role !== "assistant") continue;
		const entry = reasoningData.get(message.id);
		if (!entry) continue;
		todoSteps.push(...entry.steps.filter(isTodoWriteStep));
	}
	for (const item of standaloneReasoningItems) todoSteps.push(...item.steps.filter(isTodoWriteStep));
	return todoSteps;
};
const markLatestTodoWriteAsLive = (displayMessages, reasoningData, standaloneReasoningItems) => {
	const todoSteps = collectThreadTodoWriteSteps(displayMessages, reasoningData, standaloneReasoningItems);
	for (const step of todoSteps) delete step.isLiveTodo;
	const lastTodo = todoSteps[todoSteps.length - 1];
	if (lastTodo) lastTodo.isLiveTodo = true;
};
const makeThoughtStep = (id, messageId, hiddenContent, createdAt) => ({
	id,
	messageId,
	hiddenContent,
	type: "thought",
	createdAt
});
const addThoughtStep = (acc, id, messageId, hiddenContent, createdAt) => {
	acc.steps.push(makeThoughtStep(id, messageId, hiddenContent, createdAt));
};
const addModalitySteps = (acc, messageId, modalities, createdAt) => {
	modalities.forEach((modality, modalityIndex) => {
		if (modality.type === TODO_WRITE_TYPE && acc.currentTurnTodoWriteStep) {
			acc.currentTurnTodoWriteStep.modality = modality;
			return;
		}
		const step = {
			id: `modality-${messageId}-${modality.type}-${modalityIndex}`,
			messageId,
			modality,
			type: "modality",
			createdAt
		};
		if (modality.type === TODO_WRITE_TYPE) acc.currentTurnTodoWriteStep = step;
		acc.steps.push(step);
	});
};
const recordPlaceholderReasoning = (message, reasoningData) => {
	reasoningData.set(message.id, {
		steps: [],
		inheritedSteps: [],
		ownSteps: [],
		status: "complete"
	});
};
const flushPendingOntoLastAssistant = (ctx, beforeMessageId) => {
	const pendingSteps = flushAccumulator(ctx.accumulator);
	if (pendingSteps.length === 0) return;
	const lastVisible = ctx.displayMessages[ctx.displayMessages.length - 1];
	if (lastVisible?.role !== "assistant") {
		ctx.standaloneReasoningItems.push(ctx.finalizeStandalone(pendingSteps, {
			beforeMessageId,
			forceComplete: true
		}));
		return;
	}
	const existing = ctx.reasoningData.get(lastVisible.id);
	const inheritedSteps = existing?.inheritedSteps ?? [];
	const ownSteps = [...existing?.ownSteps ?? [], ...pendingSteps];
	ctx.reasoningData.set(lastVisible.id, ctx.finalize(inheritedSteps, ownSteps, lastVisible, { forceComplete: true }));
};
const handleUserMessage = (ctx, message) => {
	flushPendingOntoLastAssistant(ctx, message.id);
	if (opensTurn(message)) ctx.accumulator.currentTurnTodoWriteStep = void 0;
	ctx.displayMessages.push(message);
	recordPlaceholderReasoning(message, ctx.reasoningData);
};
const handleAssistantMessage = (ctx, message) => {
	const isVisible = hasVisibleAssistantContent(message);
	const inheritedSteps = isVisible ? flushAccumulator(ctx.accumulator) : [];
	const messageCreatedAt = toDate(message.createdAt);
	const hiddenContent = message.hiddenContent;
	if (ctx.showThoughts && isMeaningfulThought(hiddenContent)) addThoughtStep(ctx.accumulator, `thought-${message.id}`, message.id, hiddenContent, messageCreatedAt);
	const thinkingContent = message.thinkingContent;
	if (ctx.showThinking && ctx.showThoughts && isMeaningfulThought(thinkingContent)) {
		if (isVisible) inheritedSteps.push(makeThoughtStep(`thinking-${message.id}`, message.id, thinkingContent, messageCreatedAt));
		else addThoughtStep(ctx.accumulator, `thinking-${message.id}`, message.id, thinkingContent, messageCreatedAt);
	}
	if ((message.modalitiesUsed?.length ?? 0) > 0) {
		const displayable = ctx.filterDisplayable(message.modalitiesUsed);
		addModalitySteps(ctx.accumulator, message.id, displayable, messageCreatedAt);
	}
	if (isVisible) {
		const ownSteps = flushAccumulator(ctx.accumulator);
		ctx.displayMessages.push(message);
		ctx.reasoningData.set(message.id, ctx.finalize(inheritedSteps, ownSteps, message));
	}
};
const mergeTrailingReasoningOntoLastAssistant = (ctx) => {
	const trailingSteps = flushAccumulator(ctx.accumulator);
	if (trailingSteps.length === 0) return;
	const lastVisible = ctx.displayMessages[ctx.displayMessages.length - 1];
	if (lastVisible?.role !== "assistant") {
		ctx.standaloneReasoningItems.push(ctx.finalizeStandalone(trailingSteps));
		return;
	}
	const existing = ctx.reasoningData.get(lastVisible.id);
	const inheritedSteps = existing?.inheritedSteps ?? [];
	const ownSteps = [...existing?.ownSteps ?? [], ...trailingSteps];
	ctx.reasoningData.set(lastVisible.id, ctx.finalize(inheritedSteps, ownSteps, lastVisible, { mergedHiddenStreamingTail: true }));
};
/**
* Group assistant `hiddenContent` and `modalitiesUsed` onto visible assistant
* replies in a single chronological pass.
*
* The algorithm:
*   1. Normalize `messages` to oldest → newest order.
*   2. Walk chronologically, accumulating reasoning steps from assistant rows
*      with no visible content.
*   3. When a visible assistant reply arrives, flush the accumulator onto it
*      (inheriting earlier modalities + thoughts) before pushing it to the
*      output list.
*   4. Trailing assistant-only reasoning (e.g. tail tooling steps after the
*      most recent visible reply) is merged into that last visible reply. If no
*      visible assistant reply exists yet, it is returned as a standalone item.
*   5. `TodoWrite` is treated as a single mutable artifact per turn: repeated
*      emits within the same user→assistant turn update the same step (latest
*      payload wins). After the chronological pass, the consolidated TodoWrite
*      step is re-anchored onto the latest visible assistant in its turn, and
*      the newest TodoWrite step in the thread is tagged `isLiveTodo` so
*      consumers can pick the active widget. Desktop filters TodoWrite out
*      upstream (see `apps/desktop/.../reasoning.utils.ts`); these passes
*      no-op there and exist for mobile.
*   6. Reasoning status is `Pending` when the thread is non-terminal and either
*      the visible tail assistant row is streaming, or trailing hidden-only
*      assistant rows are still streaming onto the last visible reply;
*      otherwise it is `Complete`.
*/
const buildReasoningDisplayItems = (messages, thread, options) => {
	const displayMessages = [];
	const reasoningData = /* @__PURE__ */ new Map();
	const standaloneReasoningItems = [];
	if (messages.length === 0) return {
		displayMessages,
		reasoningData,
		standaloneReasoningItems
	};
	const chronologicalMessages = [...messages].sort((a, b) => toDate(a.createdAt).getTime() - toDate(b.createdAt).getTime());
	const ctx = {
		accumulator: createAccumulator(),
		displayMessages,
		reasoningData,
		standaloneReasoningItems,
		finalize: buildFinalizeReasoning(chronologicalMessages, thread),
		finalizeStandalone: buildFinalizeStandaloneReasoning(thread),
		filterDisplayable: options.filterDisplayableModalities,
		showThinking: options.showThinking ?? false,
		showThoughts: options.showThoughts ?? true
	};
	for (const message of chronologicalMessages) if (message.role === "user") handleUserMessage(ctx, message);
	else if (message.role === "assistant") handleAssistantMessage(ctx, message);
	else {
		displayMessages.push(message);
		recordPlaceholderReasoning(message, reasoningData);
	}
	mergeTrailingReasoningOntoLastAssistant(ctx);
	rebalanceTurnTodoWritesToLatestVisible(displayMessages, reasoningData);
	markLatestTodoWriteAsLive(displayMessages, reasoningData, standaloneReasoningItems);
	return {
		displayMessages,
		reasoningData,
		standaloneReasoningItems
	};
};
//#endregion
//#region src/reasoning/last-in-response.ts
/**
* Identify assistant messages that are "last in their response sequence":
* the last assistant reply before each user message that opens a new turn,
* plus the final reply in the thread when it is not busy. A message the user
* sent into a running turn does not end its reply sequence. These rows surface the
* copy / vote action row on both desktop and mobile.
*
* Input must be visible display messages in **chronological**
* (oldest → newest) order.
*
* @param displayMessages - Visible display messages (user + assistant).
* @param options.isBusy  - Whether the thread is actively streaming or
*   thinking. When true the trailing (most recent) assistant reply is
*   suppressed so intermediate steps don't flash controls.
* @returns A `Set<string>` of assistant message IDs that should show the
*   action row.
*/
function computeLastInResponseSequenceIds(displayMessages, options) {
	const ids = /* @__PURE__ */ new Set();
	let pendingAssistantId = null;
	for (const message of displayMessages) if (message.role === "assistant") pendingAssistantId = message.id;
	else if (opensTurn(message) && pendingAssistantId !== null) {
		ids.add(pendingAssistantId);
		pendingAssistantId = null;
	}
	if (pendingAssistantId !== null && !options.isBusy) ids.add(pendingAssistantId);
	return ids;
}
//#endregion
//#region src/reasoning/turn-activity-label.ts
const ACTIVITY_TIERS = [
	{
		throughStep: 2,
		labels: [
			"Taking off",
			"Leaving the perch",
			"Spreading wings"
		]
	},
	{
		throughStep: 5,
		labels: [
			"Foraging",
			"Scouting",
			"Pecking about"
		]
	},
	{
		throughStep: 10,
		labels: [
			"Circling",
			"Wheeling",
			"Soaring",
			"Casting about"
		]
	},
	{
		throughStep: 18,
		labels: [
			"Combing about",
			"Rummaging",
			"Gleaning",
			"Turning stones",
			"Eagle-eyeing",
			"Preening"
		]
	},
	{
		throughStep: 30,
		labels: [
			"Ranging wider",
			"Casting wider",
			"Widening the arc",
			"Flying high"
		]
	}
];
const LONG_HAUL_LABELS = [
	"Going the distance",
	"Not landing yet",
	"Still flying",
	"Nesting",
	"Roosting",
	"Peacocking"
];
const hashTurnId = (turnId) => {
	let hash = 0;
	for (const character of turnId) hash = hash * 31 + character.charCodeAt(0) | 0;
	return Math.abs(hash);
};
const getTurnActivityLabel = (stepCount, turnId) => {
	const labels = ACTIVITY_TIERS.find(({ throughStep }) => stepCount <= throughStep)?.labels ?? LONG_HAUL_LABELS;
	return labels[hashTurnId(turnId) % labels.length];
};
//#endregion
//#region src/reasoning/use-rolling-entry.ts
const BASE_DWELL_MS = 900;
const MIN_DWELL_MS = 260;
function useRollingEntry(entries, isActive, { minDwellMs = MIN_DWELL_MS } = {}) {
	const [index, setIndex] = useState(entries.length - 1);
	const target = entries.length - 1;
	const heldSinceRef = useRef(Date.now());
	useEffect(() => {
		if (!isActive) {
			setIndex(target);
			return;
		}
		if (index >= target) return;
		const backlog = target - index;
		const dwell = Math.max(minDwellMs, BASE_DWELL_MS / backlog);
		const remaining = heldSinceRef.current + dwell - Date.now();
		const timer = setTimeout(() => {
			heldSinceRef.current = Date.now();
			setIndex((current) => current + 1);
		}, Math.max(0, remaining));
		return () => clearTimeout(timer);
	}, [
		index,
		isActive,
		minDwellMs,
		target
	]);
	return entries[Math.max(0, Math.min(index, target))];
}
//#endregion
//#region src/referral-share-url.ts
const CHANNEL_SLUGS = {
	x: "x",
	linkedin: "li",
	email: "em",
	invite_link: "code",
	mobile_share_sheet: "m",
	littlebird_draft: "lb"
};
function buildReferralShareUrl(channel, referralCode) {
	return `https://lilbird.co/s/${CHANNEL_SLUGS[channel]}?ref=${referralCode}`;
}
function displayReferralShareUrl(channel, referralCode) {
	return buildReferralShareUrl(channel, referralCode).replace("https://", "");
}
//#endregion
//#region src/referral-share-messages.ts
const shareVariantId = (id) => id;
const X_VARIANTS = [
	{
		variantId: shareVariantId("x_receipts"),
		build: (url) => `Asked Littlebird "what have I been putting off for weeks" and holy hell, I'm such a procrastinator. It had receipts.\n\n${url}`
	},
	{
		variantId: shareVariantId("x_walking_into"),
		build: (url) => `"What am I walking into today" is a question I now ask an AI instead of scrolling four apps in bed.\n\n${url}`
	},
	{
		variantId: shareVariantId("x_morning_brief"),
		build: (url) => `My calendar knows what's on today. Littlebird knows which meeting I haven't prepped and which thread I've been pretending not to see.\n\n${url}`
	},
	{
		variantId: shareVariantId("x_meetings"),
		build: (url) => `Stopped taking meeting notes a month ago and my follow-ups somehow got better. Not sure how to feel about being outperformed by my own note-taker.\n\n${url}`
	}
];
const LINKEDIN_VARIANTS = [
	{
		variantId: shareVariantId("li_noticing"),
		build: (url) => `For the past couple of months, an AI has been sitting in on my meetings, reading the threads I ignore, and writing my morning brief.\n\nThe part I didn't expect: it's less about what it automates, more about what it notices. Which commitments I'm quietly dropping. Which recurring meetings aren't earning their spot.\n\nMy invite link if you want to try Littlebird: ${url}`
	},
	{
		variantId: shareVariantId("li_meetings"),
		build: (url) => `I stopped taking notes in meetings, and my follow-ups got better.\n\nLittlebird writes the notes, preps me before the next call, and drafts the follow-ups the way I'd write them — it's already seen the whole thread.\n\nInvite link: ${url}`
	},
	{
		variantId: shareVariantId("li_honest_colleague"),
		build: (url) => `I asked an AI that's been watching my work week: "What have I been putting off?"\n\nIt answered with a list. A correct one. Rude.\n\nInvite link if you want an uncomfortably honest assistant of your own: ${url}`
	}
];
const MOBILE_SHARE_SHEET_VARIANTS = [
	{
		variantId: shareVariantId("m_context"),
		build: (url) => `You'd probably like this — an AI assistant that watches my actual work (meetings, email, what's on screen), so I never have to explain anything to it. Feels made for how you run your week. ${url}`
	},
	{
		variantId: shareVariantId("m_receipts"),
		build: (url) => `Asked Littlebird "what have I been putting off for weeks" and it listed them. With receipts. You should honestly ask it the same thing. ${url}`
	},
	{
		variantId: shareVariantId("m_meetings"),
		build: (url) => `Littlebird's been writing my meeting notes and prepping me before calls — I've fully stopped pretending to take notes. I think you'd get even more out of it than I do. ${url}`
	}
];
const EMAIL_VARIANTS = [
	{
		variantId: shareVariantId("em_thought_of_you"),
		subject: "thought you'd like Littlebird",
		build: (url, code) => `Hey!\n\nBeen meaning to send this your way. I've been using Littlebird for a while — it's an AI assistant that sees your actual work (meetings, email, what's on screen), so you never have to explain context to it. Every morning it tells me what's on, what to prep, and what I'm about to drop — which turns out to be a lot.\n\nIt made me think of you.\n\n${url}\n\nIf it asks for a code at signup, mine is ${code}.`
	},
	{
		variantId: shareVariantId("em_meetings"),
		subject: "the meeting notes thing I mentioned",
		build: (url, code) => `Hey!\n\nI stopped taking meeting notes — Littlebird sits in on my calls, writes them, and preps me for the next one. Because it sees the rest of my work too, the prep actually lands. My follow-ups have never gone out this fast.\n\nWith the weeks you run, I think you'd feel the difference fast.\n\n${url}\n\nIf it asks for a code at signup, mine is ${code}.`
	},
	{
		variantId: shareVariantId("em_short"),
		subject: "try Littlebird (on me)",
		build: (url, code) => `Hey!\n\nQuick one — Littlebird is the first AI assistant I don't have to brief. It's already up to speed on my meetings, email, and whatever I'm working on. I think it'd fit how you work too.\n\n${url}\n\nIf it asks for a code at signup, mine is ${code}.`
	}
];
/**
* What "Copy link" puts on the clipboard: the link with a short personal
* lead-in, so a paste carries context but the prefix is trivial to delete
* when only the link is wanted. Deliberately a single fixed string —
* people edit this one, so it should be predictable.
*/
function buildInviteLinkCopyText(referralCode) {
	return `Btw, I've been trying Littlebird for a while, and I love it. It's a delight to use and has already meaningfully changed my workflows. I think it'd fit how you work too. ${buildReferralShareUrl("invite_link", referralCode)}`;
}
function pickRandom(variants) {
	return variants[Math.floor(Math.random() * variants.length)];
}
const POST_VARIANTS = {
	x: X_VARIANTS,
	linkedin: LINKEDIN_VARIANTS,
	mobile_share_sheet: MOBILE_SHARE_SHEET_VARIANTS
};
function pickReferralShareMessage(channel, referralCode) {
	const url = buildReferralShareUrl(channel, referralCode);
	const variant = pickRandom(POST_VARIANTS[channel]);
	return {
		variantId: variant.variantId,
		text: variant.build(url)
	};
}
function pickReferralShareEmail(referralCode) {
	const url = buildReferralShareUrl("email", referralCode);
	const variant = pickRandom(EMAIL_VARIANTS);
	return {
		variantId: variant.variantId,
		subject: variant.subject,
		body: variant.build(url, referralCode)
	};
}
/**
* Chat prompt for the "Let Littlebird write it" share option: the assistant
* drafts the referral note from the user's real usage, in their voice. No
* two shares come out the same, and the note itself demonstrates what the
* product knows — which no canned template can.
*/
function buildLittlebirdDraftPrompt(referralCode) {
	return `Help me write a short, personal note recommending Littlebird to a friend. Ask me who it's for if that would help you make it personal. Look at how I've actually been using you — the briefs, meetings, drafts, and questions that keep coming up — and pick one or two real moments that would land with someone who knows me (nothing sensitive or confidential). Tell it through what happened, not praise — the way I would actually text a friend. Close with a line on why I think it'd fit them, and end with my invite link: ${buildReferralShareUrl("littlebird_draft", referralCode)}\n\nKeep it under 120 words. Once I'm happy with it, offer to reshape it for X, LinkedIn or sending to a friend.`;
}
//#endregion
//#region src/routines/device-words.ts
/** "your Mac": how a title names the device, by its platform */
const deviceWord = (platform) => {
	if (platform === "macos") return "your Mac";
	if (platform === "windows") return "your Windows PC";
	if (platform === "ios" || platform === "android") return "your phone";
	return "your device";
};
/** "Mac": the platform on a device row */
const platformName = (platform) => {
	if (platform === "macos") return "Mac";
	if (platform === "windows") return "Windows PC";
	if (platform === "ios" || platform === "android") return "phone";
	return "device";
};
const deviceCount = (count) => `${count} ${count === 1 ? "device" : "devices"}`;
/** One device by its platform word, several by their count: "your Mac", "2 devices" */
const devicesWord = (devices) => devices.length === 1 ? deviceWord(devices[0].platform) : deviceCount(devices.length);
//#endregion
//#region src/routines/gmail-query.ts
const TOKEN_PATTERN = /(?:[^\s"(){}]|"(?:\\.|[^"\\])*")+|[(){}]/g;
const OPERATOR_PATTERN = /^([a-zA-Z][\w-]*):(.*)$/;
const WHITESPACE_PATTERN = /\s/g;
const DISTANCE_PATTERN = /^\d+$/;
const OPERATOR_LABELS = /* @__PURE__ */ new Map([
	["deliveredto", "delivered to"],
	["filename", "file"],
	["label", "labelled"],
	["list", "mailing list"],
	["older", "before"],
	["newer", "after"],
	["older_than", "older than"],
	["newer_than", "newer than"],
	["larger", "larger than"],
	["smaller", "smaller than"],
	["size", "size above"],
	["rfc822msgid", "message ID"]
]);
const parseQueryTerm = (token, scoped) => {
	const excluded = token.startsWith("-");
	if (excluded) token = token.slice(1);
	const operator = token.match(OPERATOR_PATTERN);
	let label = operator ? OPERATOR_LABELS.get(operator[1]) ?? operator[1].replaceAll("_", " ") : scoped || token === "(" || token === "{" || !token ? "" : "containing";
	let value = operator ? operator[2] : token;
	if (excluded) label = `not ${label}`.trim();
	if (value.startsWith("+")) {
		label = `${label} exactly`.trim();
		value = value.slice(1);
	}
	return {
		label,
		value,
		scoped: scoped || !!operator
	};
};
const parseGmailQuery = (query) => {
	const tokens = query.match(TOKEN_PATTERN) ?? [];
	const rawQuery = [{
		type: "condition",
		label: "",
		value: query
	}];
	if (tokens.join("").replace(WHITESPACE_PATTERN, "") !== query.replace(WHITESPACE_PATTERN, "")) return rawQuery;
	let position = 0;
	const parseParts = (closingToken, scoped = false) => {
		const parts = [];
		let needsJoin = false;
		let nearWord = false;
		while (position < tokens.length && tokens[position] !== closingToken) {
			const token = tokens[position++];
			if (token === ")" || token === "}") return null;
			if (token === "OR" || token === "AND") {
				parts.push({
					type: "text",
					value: token.toLowerCase()
				});
				needsJoin = false;
				continue;
			}
			if (token === "AROUND" && DISTANCE_PATTERN.test(tokens[position] ?? "")) {
				parts.push({
					type: "text",
					value: "within"
				}, {
					type: "condition",
					label: "",
					value: tokens[position++]
				}, {
					type: "text",
					value: "words of"
				});
				needsJoin = false;
				nearWord = true;
				continue;
			}
			if (needsJoin && closingToken) parts.push({
				type: "text",
				value: closingToken === "}" ? "or" : "and"
			});
			const term = parseQueryTerm(token, scoped || nearWord);
			const value = term.value || tokens[position++] || "";
			if (value === "(" || value === "{") {
				const closing = value === "(" ? ")" : "}";
				const children = parseParts(closing, term.scoped);
				if (!children || tokens[position++] !== closing) return null;
				parts.push({
					type: "group",
					label: term.label,
					parts: children
				});
			} else {
				if (!value || value === ")" || value === "}") return null;
				parts.push({
					type: "condition",
					label: term.label,
					value
				});
			}
			needsJoin = true;
			nearWord = false;
		}
		return parts;
	};
	return parseParts() ?? rawQuery;
};
//#endregion
//#region src/routine-schedule.ts
function toRoutineScheduleParts({ recurrenceType, triggerTime, hourlyEndTime, weekDays, monthDay, advancedMonths, advancedDayMode, advancedMonthDays, advancedTimes, intervalCount, intervalUnit, intervalStartAt, intervalEndAt }) {
	return {
		frequency: recurrenceType,
		triggerTime,
		hourlyEndTime,
		weekDays,
		monthDay,
		advancedMonths,
		advancedDayMode,
		advancedMonthDays,
		advancedTimes,
		intervalCount,
		intervalUnit,
		intervalStartAt,
		intervalEndAt
	};
}
const WEEK_DAYS = [
	{
		code: "MO",
		label: "Mon"
	},
	{
		code: "TU",
		label: "Tue"
	},
	{
		code: "WE",
		label: "Wed"
	},
	{
		code: "TH",
		label: "Thu"
	},
	{
		code: "FR",
		label: "Fri"
	},
	{
		code: "SA",
		label: "Sat"
	},
	{
		code: "SU",
		label: "Sun"
	}
];
const WEEKDAY_LABELS = Object.fromEntries(WEEK_DAYS.map((day) => [day.code, day.label]));
const EVERY_MONTH = Array.from({ length: 12 }, (_, index) => index + 1);
const ADVANCED_MONTH_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);
const WEEK_DAY_CODES = WEEK_DAYS.map((day) => day.code);
const WEEKDAY_ONLY_CODES = [
	"MO",
	"TU",
	"WE",
	"TH",
	"FR"
];
/** Mirrors `_MIN_INTERVAL` in backend/lb/utils/recurrence.py; the API rejects anything below it. */
const MIN_ROUTINE_INTERVAL_MINUTES = 5;
const ROUTINE_INTERVAL_UNITS = [
	"minutes",
	"hours",
	"days"
];
const ROUTINE_INTERVAL_COUNTS = {
	minutes: [
		5,
		10,
		15,
		20,
		30,
		45
	],
	hours: [
		1,
		2,
		3,
		4,
		6,
		8,
		12
	],
	days: [
		1,
		2,
		3,
		4,
		5,
		6
	]
};
const INTERVAL_UNIT_MINUTES = {
	minutes: 1,
	hours: 60,
	days: 1440
};
function routineIntervalMinutes(count, unit) {
	return count * INTERVAL_UNIT_MINUTES[unit];
}
/** The counts to offer, including `count` itself — dropping it would rewrite the stored schedule. */
function routineIntervalCounts(unit, count) {
	const counts = ROUTINE_INTERVAL_COUNTS[unit];
	if (counts.includes(count) || !Number.isInteger(count) || count < 1) return counts;
	return [...counts, count].sort((a, b) => a - b);
}
const DEFAULT_ROUTINE_INTERVAL_COUNT = 30;
const DEFAULT_ROUTINE_INTERVAL_UNIT = "minutes";
const DEFAULT_ROUTINE_SCHEDULE = {
	frequency: "Daily",
	triggerTime: "09:30",
	weekDays: ["MO"],
	monthDay: 1,
	isValid: true,
	hourlyEndTime: "18:00",
	advancedMonths: [...EVERY_MONTH],
	advancedDayMode: "weekDays",
	advancedMonthDays: [1],
	advancedTimes: ["09:30"],
	intervalCount: 30,
	intervalUnit: DEFAULT_ROUTINE_INTERVAL_UNIT,
	intervalStartAt: null,
	intervalEndAt: null
};
const MONTH_DAYS = Array.from({ length: 28 }, (_, index) => index + 1);
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
function isValidRoutineTime(time) {
	return TIME_PATTERN.test(time);
}
const toValidTime = (time, fallback) => isValidRoutineTime(time) ? time : fallback;
function parseRoutineRecurrenceRule(recurrenceRule) {
	const frequencyMatch = recurrenceRule?.match(/FREQ=(DAILY|WEEKLY|MONTHLY)/i);
	const timeMatch = recurrenceRule?.match(/BYTIME=(\d{2}:\d{2})/);
	const weekDaysMatch = recurrenceRule?.match(/BYDAY=([A-Z,]+)/);
	const monthDayMatch = recurrenceRule?.match(/BYMONTHDAY=(\d{1,2})/);
	const rawFrequency = frequencyMatch?.[1]?.toUpperCase();
	return {
		frequency: rawFrequency === "DAILY" ? "Daily" : rawFrequency === "WEEKLY" ? "Weekly" : rawFrequency === "MONTHLY" ? "Monthly" : void 0,
		triggerTime: timeMatch?.[1] ?? null,
		weekDays: weekDaysMatch?.[1]?.split(",") ?? null,
		monthDay: monthDayMatch?.[1] ? Number(monthDayMatch[1]) : null
	};
}
function formatRoutineWeekDays(days) {
	return days.map((day) => WEEKDAY_LABELS[day] ?? day).join(", ");
}
const toTime = (hour, minute) => `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
const isUnique = (values) => new Set(values).size === values.length;
/** True when the rule carries a `DTSTART` anchor: only the legacy `BYTIME` presets go without one. */
function isCanonicalRecurrenceRule(rule) {
	return Boolean(rule && /DTSTART/i.test(rule));
}
const toRuleBody = (rule) => rule.split("\n").find((line) => line.startsWith("RRULE:"))?.slice(6) ?? rule;
const HOURLY_RULE = /^FREQ=HOURLY(?:;BYHOUR=(\d{1,2}(?:,\d{1,2})*))?;BYMINUTE=(\d{1,2})(?:;BYDAY=(MO,TU,WE,TH,FR))?$/i;
const ADVANCED_WEEK_DAYS_RULE = /^FREQ=YEARLY;BYMONTH=(\d{1,2}(?:,\d{1,2})*);BYDAY=([A-Z]{2}(?:,[A-Z]{2})*);BYHOUR=(\d{1,2}(?:,\d{1,2})*);BYMINUTE=(\d{1,2})$/i;
const ADVANCED_MONTH_DAYS_RULE = /^FREQ=YEARLY;BYMONTH=(\d{1,2}(?:,\d{1,2})*);BYMONTHDAY=(\d{1,2}(?:,\d{1,2})*);BYHOUR=(\d{1,2}(?:,\d{1,2})*);BYMINUTE=(\d{1,2})$/i;
const INTERVAL_FREQ_BY_UNIT = {
	minutes: "MINUTELY",
	hours: "HOURLY",
	days: "DAILY"
};
const INTERVAL_UNIT_BY_FREQ = {
	MINUTELY: "minutes",
	HOURLY: "hours",
	DAILY: "days"
};
const INTERVAL_RULE = /^FREQ=(MINUTELY|HOURLY|DAILY);INTERVAL=(\d{1,3})(?:;UNTIL=(\d{8}T\d{6}))?$/i;
const DTSTART_LINE = /^DTSTART:(\d{8}T\d{6})$/i;
const BASIC_DATE_TIME = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})$/;
/** A `rrule` option that was never set, in either of the two shapes the library uses for that. */
const isNilOption = (value) => value === void 0 || value === null;
function localDateTimeFromParts(parts) {
	const [year, month, day, hour, minute, second = 0] = parts;
	const date = new Date(year, month - 1, day, hour, minute, second);
	return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day && date.getHours() === hour && date.getMinutes() === minute && date.getSeconds() === second ? date : null;
}
/** A floating iCal basic datetime as a Date, read as local wall clock — as the backend reads it. */
function parseBasicDateTime(value) {
	const match = value.match(BASIC_DATE_TIME);
	return match ? localDateTimeFromParts(match.slice(1, 7).map(Number)) : null;
}
function hasRebuildableAnchor(rule, body) {
	if (rule === body) return true;
	const [startLine, ruleLine, ...extraLines] = rule.split("\n");
	const start = startLine.match(DTSTART_LINE)?.[1];
	return extraLines.length === 0 && start !== void 0 && parseBasicDateTime(start) !== null && ruleLine === `RRULE:${body}`;
}
/** A Date as the 'YYYY-MM-DDTHH:mm' local value the interval controls hold. */
function toRoutineDateTimeValue(date) {
	const pad = (value) => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
/** 'YYYY-MM-DDTHH:mm' local as a Date, or null when it isn't one. */
function fromRoutineDateTimeValue(value) {
	if (!value) return null;
	const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
	if (!match) return null;
	return localDateTimeFromParts(match.slice(1).map(Number));
}
function parseIntervalRoutineRule(rule) {
	const match = toRuleBody(rule).match(INTERVAL_RULE);
	if (!match) return null;
	const unit = INTERVAL_UNIT_BY_FREQ[match[1].toUpperCase()];
	const count = Number(match[2]);
	const dtstartLine = rule.split("\n").map((line) => line.trim()).find((line) => /^DTSTART/i.test(line));
	const startAt = dtstartLine ? parseBasicDateTime(dtstartLine.match(DTSTART_LINE)?.[1] ?? "") : null;
	const endAt = match[3] ? parseBasicDateTime(match[3]) : null;
	return {
		...DEFAULT_ROUTINE_SCHEDULE,
		frequency: "Interval",
		intervalCount: count,
		intervalUnit: unit,
		intervalStartAt: startAt ? toRoutineDateTimeValue(startAt) : null,
		intervalEndAt: endAt ? toRoutineDateTimeValue(endAt) : null,
		triggerTime: startAt ? toTime(startAt.getHours(), startAt.getMinutes()) : DEFAULT_ROUTINE_SCHEDULE.triggerTime,
		isValid: count >= 1 && routineIntervalMinutes(count, unit) >= 5 && (!dtstartLine || startAt !== null) && (!match[3] || endAt !== null)
	};
}
function parseHourlyRoutineRule(rule) {
	const body = toRuleBody(rule);
	if (!/^FREQ=HOURLY(?:;|$)/i.test(body)) return null;
	const match = body.match(HOURLY_RULE);
	const hours = match?.[1]?.split(",").map(Number) ?? [];
	const minute = Number(match?.[2] ?? 0);
	const isContiguous = hours.every((hour, index) => hour >= 0 && hour <= 23 && (index === 0 || hour === hours[index - 1] + 1));
	const dtstartLine = rule.split("\n").map((line) => line.trim()).find((line) => /^DTSTART/i.test(line));
	const startAt = dtstartLine ? parseBasicDateTime(dtstartLine.match(DTSTART_LINE)?.[1] ?? "") : null;
	return {
		...DEFAULT_ROUTINE_SCHEDULE,
		frequency: "Hourly",
		triggerTime: toValidTime(toTime(hours[0] ?? 0, minute), DEFAULT_ROUTINE_SCHEDULE.triggerTime),
		hourlyEndTime: toValidTime(toTime(hours.at(-1) ?? 23, minute), DEFAULT_ROUTINE_SCHEDULE.hourlyEndTime),
		intervalStartAt: startAt ? toRoutineDateTimeValue(startAt) : null,
		weekDays: match?.[3]?.split(",") ?? [...WEEK_DAY_CODES],
		isValid: hasRebuildableAnchor(rule, body) && match !== null && minute <= 59 && isContiguous
	};
}
function parseAdvancedRoutineRule(rule) {
	const body = toRuleBody(rule);
	if (!/^FREQ=YEARLY(?:;|$)/i.test(body)) return null;
	const weekDaysMatch = body.match(ADVANCED_WEEK_DAYS_RULE);
	const monthDaysMatch = body.match(ADVANCED_MONTH_DAYS_RULE);
	const match = weekDaysMatch ?? monthDaysMatch;
	const months = match?.[1]?.split(",").map(Number) ?? [];
	const weekDays = weekDaysMatch?.[2]?.split(",") ?? [];
	const monthDays = monthDaysMatch?.[2]?.split(",").map(Number) ?? [];
	const hours = match?.[3]?.split(",").map(Number) ?? [];
	const minute = Number(match?.[4] ?? 0);
	const times = hours.map((hour) => toTime(hour, minute)).filter(isValidRoutineTime);
	const hasSupportedDays = weekDaysMatch ? weekDays.length > 0 && weekDays.every((day) => WEEK_DAY_CODES.includes(day)) && isUnique(weekDays) : monthDays.length > 0 && monthDays.every((day) => ADVANCED_MONTH_DAYS.includes(day)) && isUnique(monthDays);
	const dtstartLine = rule.split("\n").map((line) => line.trim()).find((line) => /^DTSTART/i.test(line));
	const startAt = dtstartLine ? parseBasicDateTime(dtstartLine.match(DTSTART_LINE)?.[1] ?? "") : null;
	return {
		...DEFAULT_ROUTINE_SCHEDULE,
		frequency: "Advanced",
		triggerTime: times[0] ?? DEFAULT_ROUTINE_SCHEDULE.triggerTime,
		weekDays,
		advancedDayMode: monthDaysMatch ? "monthDays" : "weekDays",
		advancedMonths: months.filter((month) => EVERY_MONTH.includes(month)),
		advancedMonthDays: monthDays.filter((day) => ADVANCED_MONTH_DAYS.includes(day)),
		advancedTimes: times.length ? times : [...DEFAULT_ROUTINE_SCHEDULE.advancedTimes],
		intervalStartAt: startAt ? toRoutineDateTimeValue(startAt) : null,
		isValid: hasRebuildableAnchor(rule, body) && match !== null && months.length > 0 && months.every((month) => EVERY_MONTH.includes(month)) && isUnique(months) && hasSupportedDays && hours.length > 0 && hours.every((hour) => hour >= 0 && hour <= 23) && isUnique(hours) && minute <= 59
	};
}
function parseRoutineSchedule(recurrenceRule) {
	if (!recurrenceRule) return {
		...DEFAULT_ROUTINE_SCHEDULE,
		isValid: false
	};
	const built = parseIntervalRoutineRule(recurrenceRule) ?? parseHourlyRoutineRule(recurrenceRule) ?? parseAdvancedRoutineRule(recurrenceRule);
	if (built) return built;
	const recurrence = parseRoutineRecurrenceRule(recurrenceRule);
	const triggerTime = recurrence.triggerTime ?? DEFAULT_ROUTINE_SCHEDULE.triggerTime;
	const parsedWeekDays = recurrence.weekDays ?? [];
	const weekDays = parsedWeekDays.length ? parsedWeekDays : [...DEFAULT_ROUTINE_SCHEDULE.weekDays];
	const monthDay = recurrence.monthDay ?? DEFAULT_ROUTINE_SCHEDULE.monthDay;
	const hasRequiredTime = recurrence.triggerTime !== null;
	const hasRequiredWeekDays = parsedWeekDays.length > 0;
	const hasRequiredMonthDay = recurrence.monthDay !== null;
	const hasValidWeekDays = parsedWeekDays.every((day) => WEEKDAY_LABELS[day] !== void 0);
	if (!recurrence.frequency) return {
		...DEFAULT_ROUTINE_SCHEDULE,
		isValid: false
	};
	return {
		...DEFAULT_ROUTINE_SCHEDULE,
		frequency: recurrence.frequency,
		triggerTime,
		advancedTimes: [toValidTime(triggerTime, DEFAULT_ROUTINE_SCHEDULE.triggerTime)],
		weekDays: weekDays.length ? weekDays : [...DEFAULT_ROUTINE_SCHEDULE.weekDays],
		monthDay,
		isValid: hasRequiredTime && isValidRoutineTime(triggerTime) && (recurrence.frequency !== "Weekly" || hasRequiredWeekDays && hasValidWeekDays) && (recurrence.frequency !== "Monthly" || hasRequiredMonthDay && MONTH_DAYS.includes(monthDay))
	};
}
const WEEKDAY_CODES = [
	"SU",
	"MO",
	"TU",
	"WE",
	"TH",
	"FR",
	"SA"
];
function isRoutineScheduleDueOn(schedule, date) {
	if (!schedule.isValid) return false;
	const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const start = fromRoutineDateTimeValue(schedule.intervalStartAt);
	const startDay = start && new Date(start.getFullYear(), start.getMonth(), start.getDate());
	switch (schedule.frequency) {
		case "Daily": return true;
		case "Weekly": return schedule.weekDays.includes(WEEKDAY_CODES[date.getDay()]);
		case "Monthly": return schedule.monthDay === date.getDate();
		case "Hourly":
			if (startDay && day < startDay) return false;
			return schedule.weekDays.includes(WEEKDAY_CODES[date.getDay()]);
		case "Interval": {
			const end = fromRoutineDateTimeValue(schedule.intervalEndAt);
			if (end && day > end) return false;
			if (startDay && day < startDay) return false;
			if (schedule.intervalUnit !== "days" || schedule.intervalCount === 1) return true;
			if (!startDay) return true;
			return Math.round((day.getTime() - startDay.getTime()) / (24 * HOUR_MS)) % schedule.intervalCount === 0;
		}
		case "Advanced":
			if (startDay && day < startDay) return false;
			return schedule.advancedMonths.includes(date.getMonth() + 1) && (schedule.advancedDayMode === "monthDays" ? schedule.advancedMonthDays.includes(date.getDate()) : schedule.weekDays.includes(WEEKDAY_CODES[date.getDay()]));
	}
}
function isRoutineDueOn(recurrenceRule, date) {
	return isRoutineScheduleDueOn(parseRoutineSchedule(recurrenceRule), date);
}
function buildRoutineRecurrenceRule({ frequency, triggerTime, weekDays, monthDay, hourlyEndTime, advancedMonths, advancedDayMode, advancedMonthDays, advancedTimes, intervalCount, intervalUnit, intervalStartAt, intervalEndAt }) {
	if (frequency === "Advanced") {
		const dayPart = advancedDayMode === "monthDays" ? `BYMONTHDAY=${[...advancedMonthDays].sort((a, b) => a - b).join(",")}` : `BYDAY=${WEEK_DAY_CODES.filter((code) => weekDays.includes(code)).join(",")}`;
		const times = [...advancedTimes].sort();
		const hours = times.map((time) => Number(time.split(":")[0]));
		const minute = Number(times[0]?.split(":")[1] ?? 0);
		return `FREQ=YEARLY;BYMONTH=${[...advancedMonths].sort((a, b) => a - b).join(",")};${dayPart};BYHOUR=${hours.join(",")};BYMINUTE=${minute}`;
	}
	if (frequency === "Interval") {
		const parts = [`FREQ=${INTERVAL_FREQ_BY_UNIT[intervalUnit]}`, `INTERVAL=${Math.max(1, Math.round(intervalCount))}`];
		if (intervalEndAt) parts.push(`UNTIL=${toUntilBasic(intervalEndAt)}`);
		const body = parts.join(";");
		return intervalStartAt ? `DTSTART:${toDtstartBasic(intervalStartAt)}\nRRULE:${body}` : body;
	}
	if (frequency === "Hourly") {
		const [startHour, startMinute] = triggerTime.split(":").map(Number);
		const endHour = Number(hourlyEndTime.split(":")[0]);
		const hours = Array.from({ length: endHour - startHour + 1 }, (_, index) => startHour + index);
		const parts = ["FREQ=HOURLY"];
		if (hours.length < 24) parts.push(`BYHOUR=${hours.join(",")}`);
		parts.push(`BYMINUTE=${startMinute}`);
		if (weekDays.length > 0 && weekDays.length < 7) parts.push(`BYDAY=${weekDays.join(",")}`);
		return parts.join(";");
	}
	if (frequency === "Weekly") return `FREQ=WEEKLY;BYDAY=${weekDays.join(",")};BYTIME=${triggerTime}`;
	if (frequency === "Monthly") return `FREQ=MONTHLY;BYMONTHDAY=${monthDay};BYTIME=${triggerTime}`;
	return `FREQ=DAILY;BYTIME=${triggerTime}`;
}
function syncAdvancedTime(times, index, value) {
	const previousMinute = times[index]?.split(":")[1];
	const nextMinute = value.split(":")[1];
	const nextTimes = times.map((time, timeIndex) => {
		if (timeIndex === index) return value;
		return previousMinute === nextMinute ? time : `${time.split(":")[0]}:${nextMinute}`;
	});
	return isUnique(nextTimes) ? nextTimes : times;
}
function formatMonthDayOrdinal(day) {
	const suffixes = [
		"th",
		"st",
		"nd",
		"rd"
	];
	const remainder = day % 100;
	return `${day}${suffixes[(remainder - 20) % 10] ?? suffixes[remainder] ?? suffixes[0]}`;
}
const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;
/** An interval schedule's end as 'Aug 1, 6:00 PM', in the viewer's locale. */
function formatRoutineIntervalEnd(endAt, formatTime) {
	const end = fromRoutineDateTimeValue(endAt);
	if (!end) return endAt;
	return `${end.toLocaleDateString(void 0, {
		month: "short",
		day: "numeric"
	})}, ${formatTime(toTime(end.getHours(), end.getMinutes()))}`;
}
/** 'Every 30 minutes', 'Every hour', 'Every 3 days'. */
function formatRoutineInterval(count, unit) {
	const singular = unit.slice(0, -1);
	return count === 1 ? `Every ${singular}` : `Every ${count} ${unit}`;
}
function formatRoutineScheduleSummary(schedule, { formatTime }) {
	const time = formatTime(schedule.triggerTime);
	if (schedule.frequency === "Interval") {
		const cadence = formatRoutineInterval(schedule.intervalCount, schedule.intervalUnit);
		return schedule.intervalEndAt ? `${cadence} · until ${formatRoutineIntervalEnd(schedule.intervalEndAt, formatTime)}` : cadence;
	}
	if (schedule.frequency === "Advanced") {
		const dayCount = schedule.advancedDayMode === "monthDays" ? schedule.advancedMonthDays.length : schedule.weekDays.length;
		return `Advanced · ${pluralize(schedule.advancedMonths.length, "month")} · ${pluralize(dayCount, "day")} · ${pluralize(schedule.advancedTimes.length, "time")}`;
	}
	if (schedule.frequency === "Hourly") return `Hourly · ${schedule.weekDays.length === 7 ? "Every day" : WEEKDAY_ONLY_CODES.every((day) => schedule.weekDays.includes(day)) && schedule.weekDays.length === WEEKDAY_ONLY_CODES.length ? "Weekdays" : pluralize(schedule.weekDays.length, "day")} · ${time}–${formatTime(schedule.hourlyEndTime)}`;
	if (schedule.frequency === "Weekly") return `Weekly · ${schedule.weekDays.length > 0 ? schedule.weekDays.map((day) => WEEKDAY_LABELS[day] ?? day).join(", ") : WEEKDAY_LABELS.MO} · ${time}`;
	if (schedule.frequency === "Monthly") return `Monthly · ${formatMonthDayOrdinal(schedule.monthDay)} · ${time}`;
	return `Daily · ${time}`;
}
const BYTIME_HHMM_RE = /BYTIME=(\d{1,2}):(\d{2})/i;
const BYTIME_MINUTE_RE = /BYTIME=(\d{1,2})(?!:)/i;
/** Rewrite the legacy `BYTIME` token into canonical `BYHOUR`/`BYMINUTE` so `rrule` can parse it. */
function normalizeRecurrenceRule(rule) {
	const hhmm = rule.match(BYTIME_HHMM_RE);
	if (hhmm) return `${rule.replace(BYTIME_HHMM_RE, "").replace(/;;+/g, ";").replace(/;$/, "")};BYHOUR=${Number(hhmm[1])};BYMINUTE=${Number(hhmm[2])}`.replace(/^;/, "");
	const minute = rule.match(BYTIME_MINUTE_RE);
	if (minute) return `${rule.replace(BYTIME_MINUTE_RE, "").replace(/;;+/g, ";").replace(/;$/, "")};BYMINUTE=${Number(minute[1])}`.replace(/^;/, "");
	return rule;
}
const HOUR_MS = 36e5;
const CADENCE_SAMPLE_SIZE = 200;
const RULE_SET_KEYWORDS = [
	"RDATE",
	"EXDATE",
	"EXRULE"
];
/** A rule shape the routine field deliberately refuses, as opposed to one `rrulestr` couldn't parse. */
var UnsupportedRuleError = class extends Error {};
/**
* Throw unless the rule is a single RRULE with an optional `DTSTART`. A recurrence set has no single
* cadence to check, and the two implementations read one differently — `dateutil` merges every
* `RRULE` line while `rrule` reports both but iterates only the first — so neither side can be
* trusted to notice an extra run hiding inside the hourly floor.
*/
function assertSingleRule(rule) {
	const lines = rule.trim().split("\n").map((line) => line.trim().toUpperCase()).filter(Boolean);
	if (lines.filter((line) => line.startsWith("RRULE:")).length > 1) throw new UnsupportedRuleError("A routine schedule must be a single recurrence rule");
	const keyword = RULE_SET_KEYWORDS.find((candidate) => lines.some((line) => line.startsWith(candidate)));
	if (keyword) throw new UnsupportedRuleError(`${keyword} is not supported in a routine schedule`);
}
function buildRRule(rule) {
	assertSingleRule(rule);
	const normalized = normalizeRecurrenceRule(rule.trim());
	const hasDtstart = /DTSTART/i.test(normalized);
	const parsed = rrulestr(hasDtstart ? normalized : `RRULE:${normalized.replace(/^RRULE:/i, "")}`);
	if (!(parsed instanceof RRule)) throw new UnsupportedRuleError("A routine schedule must be a single recurrence rule");
	return parsed;
}
function smallestGapMs(rrule) {
	const occurrences = rrule.all((_, index) => index < CADENCE_SAMPLE_SIZE);
	let smallest = Infinity;
	for (let index = 1; index < occurrences.length; index++) smallest = Math.min(smallest, occurrences[index].getTime() - occurrences[index - 1].getTime());
	return Number.isFinite(smallest) ? smallest : null;
}
const toFloatingUtc = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
const fromFloatingUtc = (date) => new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
/**
* The rule's next occurrence after `now`, reading its times as wall-clock times in the local
* timezone, the way the backend reads them in the user's timezone. `rrule` computes in UTC, so both
* ends are shifted through a floating UTC date. Null when the rule has no later occurrence, cannot
* be parsed, or leaves its interval, count, weekday, month day or month to a start date it does
* not carry.
*/
function nextRoutineOccurrence(recurrenceRule, now = /* @__PURE__ */ new Date()) {
	if (!recurrenceRule) return null;
	try {
		const { origOptions } = buildRRule(recurrenceRule);
		const needsAnchor = (origOptions.interval ?? 1) > 1 || origOptions.count != null || origOptions.freq === RRule.WEEKLY && origOptions.byweekday == null || origOptions.freq === RRule.MONTHLY && origOptions.bymonthday == null && origOptions.byweekday == null || origOptions.freq === RRule.YEARLY && origOptions.bymonth == null;
		if (!origOptions.dtstart && needsAnchor) return null;
		const floatingNow = toFloatingUtc(now);
		const next = new RRule({
			...origOptions,
			tzid: null,
			dtstart: origOptions.dtstart ?? floatingNow
		}).after(floatingNow);
		return next ? fromFloatingUtc(next) : null;
	} catch {
		return null;
	}
}
/**
* Smallest gap between consecutive occurrences over the rule's first `CADENCE_SAMPLE_SIZE`, or null
* when there is no gap to measure — fewer than two occurrences, a recurrence set, or an unparseable
* rule. Sampled from the rule's own start rather than from now, so a given rule always measures the
* same; mirrors `min_occurrence_gap` in backend/lb/utils/recurrence.py.
*/
function minOccurrenceGapMs(recurrenceRule) {
	if (!recurrenceRule) return null;
	try {
		return smallestGapMs(buildRRule(recurrenceRule));
	} catch {
		return null;
	}
}
/** True when consecutive occurrences are less than 24h apart. Backend stays authoritative. */
function isMoreFrequentThanDaily(recurrenceRule) {
	const gap = minOccurrenceGapMs(recurrenceRule);
	return gap !== null && gap < 24 * HOUR_MS;
}
const MIN_INTERVAL_ERROR = "Routines can run at most once every 5 minutes";
const NON_INTERVAL_SUB_HOURLY_ERROR = "A routine that runs more than once an hour must repeat on a fixed interval";
const INTERVAL_TOO_SMALL_ERROR = "Recurrence interval must be at least 1";
const INTERVAL_TOO_LARGE_ERROR = "Recurrence interval is too large";
const COUNT_TOO_LARGE_ERROR = "Recurrence count is too large";
const MAX_RRULE_INTEGER = 2147483647;
const MIN_INTERVAL_MS = 3e5;
/**
* True when the rule is one fixed repeating period — the only sub-hourly shape the backend can
* install as an open-ended Temporal interval spec. Mirrors `get_interval_rule_shape`.
*/
function isFixedIntervalRule(rrule) {
	const o = rrule.origOptions;
	if ([
		o.byhour,
		o.byweekday,
		o.bymonth,
		o.bymonthday,
		o.bysetpos,
		o.byyearday,
		o.byweekno,
		o.bysecond
	].some((part) => !isNilOption(part) && !(Array.isArray(part) && part.length === 0))) return false;
	const byminute = isNilOption(o.byminute) ? [] : Array.isArray(o.byminute) ? o.byminute : [o.byminute];
	const interval = o.interval ?? 1;
	if (!isNilOption(o.count) && o.count < 1) return false;
	if (o.freq === RRule.MINUTELY) return byminute.length === 0 && interval >= 1;
	if (o.freq === RRule.HOURLY) return byminute.length <= 1 && interval >= 1;
	return false;
}
function fixedIntervalMs(rrule) {
	return (rrule.origOptions.freq === RRule.MINUTELY ? 6e4 : HOUR_MS) * (rrule.origOptions.interval ?? 1);
}
/**
* Validate a raw RRULE against the routine contract: a single parseable rule, running no more than
* once every five minutes, and — below the hourly mark, or at any minutely frequency — repeating on
* one fixed period. Returns a human-readable summary on success.
*/
function validateRecurrenceRule(rule) {
	if (!rule || !rule.trim()) return {
		isValid: false,
		error: "Enter a recurrence rule"
	};
	try {
		const rrule = buildRRule(rule);
		const { freq } = rrule.options;
		const { count, interval = 1 } = rrule.origOptions;
		if (!Number.isSafeInteger(interval) || interval > MAX_RRULE_INTEGER) return {
			isValid: false,
			error: INTERVAL_TOO_LARGE_ERROR
		};
		if (interval < 1) return {
			isValid: false,
			error: INTERVAL_TOO_SMALL_ERROR
		};
		if (!isNilOption(count) && (!Number.isSafeInteger(count) || count > MAX_RRULE_INTEGER)) return {
			isValid: false,
			error: COUNT_TOO_LARGE_ERROR
		};
		if (freq === RRule.SECONDLY) return {
			isValid: false,
			error: MIN_INTERVAL_ERROR
		};
		if (freq === RRule.MINUTELY && !isFixedIntervalRule(rrule)) return {
			isValid: false,
			error: NON_INTERVAL_SUB_HOURLY_ERROR
		};
		const gap = freq === RRule.MINUTELY ? fixedIntervalMs(rrule) : smallestGapMs(rrule);
		if (gap !== null && gap < MIN_INTERVAL_MS) return {
			isValid: false,
			error: MIN_INTERVAL_ERROR
		};
		if (freq !== RRule.MINUTELY && gap !== null && gap < HOUR_MS && !isFixedIntervalRule(rrule)) return {
			isValid: false,
			error: NON_INTERVAL_SUB_HOURLY_ERROR
		};
		return {
			isValid: true,
			summary: rrule.toText()
		};
	} catch (error) {
		return {
			isValid: false,
			error: error instanceof UnsupportedRuleError ? error.message : "Not a valid recurrence rule"
		};
	}
}
const DEFAULT_ADVANCED_SCHEDULE = {
	frequency: "Daily",
	interval: 1,
	triggerTime: "09:30",
	hourlyMinute: 0,
	weekDays: ["MO"],
	monthDays: [1],
	startAt: null,
	end: { type: "never" },
	isCustom: false
};
const FREQ_TO_TOKEN = {
	Hourly: "HOURLY",
	Daily: "DAILY",
	Weekly: "WEEKLY",
	Monthly: "MONTHLY"
};
const RRULE_WEEKDAY_CODES = [
	"MO",
	"TU",
	"WE",
	"TH",
	"FR",
	"SA",
	"SU"
];
function splitTime(time) {
	const [h, m] = time.split(":");
	return {
		hour: Number(h) || 0,
		minute: Number(m) || 0
	};
}
const pad2 = (n) => String(n).padStart(2, "0");
function toDtstartBasic(local) {
	const [date, time] = local.split("T");
	const [y, mo, da] = date.split("-");
	const { hour, minute } = splitTime(time ?? "00:00");
	return `${y}${mo}${da}T${pad2(hour)}${pad2(minute)}00`;
}
function toUntilBasic(local) {
	const [date, time] = local.split("T");
	const [y, mo, da] = date.split("-");
	const { hour, minute } = splitTime(time ?? "23:59");
	return `${y}${mo}${da}T${pad2(hour)}${pad2(minute)}00`;
}
/** Local wall-clock "now" as a floating iCal value — seconds dropped, as the backend does. */
function toDtstartBasicFromDate(now) {
	return `${`${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}`}T${pad2(now.getHours())}${pad2(now.getMinutes())}00`;
}
/** True when the rule is in the legacy `BYTIME` dialect — the preset form older clients can parse. */
function isLegacyDialect(rule) {
	return /BYTIME=/i.test(rule);
}
function isSimplePresetSchedule(s) {
	return !s.isCustom && s.frequency !== "Hourly" && s.interval === 1 && s.startAt === null && s.end.type === "never" && !(s.frequency === "Monthly" && s.monthDays.length > 1);
}
/**
* Compose an RRULE from the structured schedule. Simple daily/weekly/monthly
* schedules keep emitting the legacy `BYTIME` dialect so existing consumers
* (widgets, mobile) still parse them; anything using hourly / interval / start /
* end emits a canonical RRULE, which always carries a `DTSTART:` line.
*
* A canonical rule with no start date anchors on `now` rather than omitting the
* line: that's the origin the backend would fill in anyway, and sending it keeps
* the rule the client stores identical to the one it reads back.
*/
function buildAdvancedRecurrenceRule(s, now = /* @__PURE__ */ new Date()) {
	if (isSimplePresetSchedule(s)) return buildRoutineRecurrenceRule({
		...DEFAULT_ROUTINE_SCHEDULE,
		frequency: s.frequency,
		triggerTime: s.triggerTime,
		weekDays: s.weekDays,
		monthDay: s.monthDays[0] ?? 1
	});
	const parts = [`FREQ=${FREQ_TO_TOKEN[s.frequency]}`];
	if (s.interval > 1) parts.push(`INTERVAL=${s.interval}`);
	if (s.frequency === "Hourly") parts.push(`BYMINUTE=${s.hourlyMinute}`);
	else {
		const { hour, minute } = splitTime(s.triggerTime);
		if (s.frequency === "Weekly" && s.weekDays.length) parts.push(`BYDAY=${s.weekDays.join(",")}`);
		if (s.frequency === "Monthly" && s.monthDays.length) parts.push(`BYMONTHDAY=${[...s.monthDays].sort((a, b) => a - b).join(",")}`);
		parts.push(`BYHOUR=${hour}`, `BYMINUTE=${minute}`);
	}
	if (s.end.type === "count") parts.push(`COUNT=${s.end.count}`);
	if (s.end.type === "until") parts.push(`UNTIL=${toUntilBasic(s.end.until)}`);
	return `DTSTART:${s.startAt ? toDtstartBasic(s.startAt) : toDtstartBasicFromDate(now)}\nRRULE:${parts.join(";")}`;
}
function formatFloatingDate(d, withTime) {
	const pad = (n) => String(n).padStart(2, "0");
	const date = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
	return withTime ? `${date}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}` : date;
}
const TOKEN_TO_FREQ = {
	[RRule.HOURLY]: "Hourly",
	[RRule.DAILY]: "Daily",
	[RRule.WEEKLY]: "Weekly",
	[RRule.MONTHLY]: "Monthly"
};
/**
* Parse an RRULE back into the structured schedule, or null when it uses
* features the structured controls can't represent (so the caller shows the raw
* RRULE text field instead).
*/
function parseAdvancedRecurrenceRule(rule) {
	if (!rule || !rule.trim()) return null;
	let rrule;
	try {
		rrule = buildRRule(rule);
	} catch {
		return null;
	}
	const isNil = (v) => v === void 0 || v === null;
	const o = rrule.origOptions;
	const frequency = isNil(o.freq) ? void 0 : TOKEN_TO_FREQ[o.freq];
	if (!frequency) return null;
	if ([
		o.bysetpos,
		o.bymonth,
		o.byyearday,
		o.byweekno
	].some((v) => !isNil(v))) return null;
	const asArray = (v) => isNil(v) ? [] : Array.isArray(v) ? v : [v];
	const byhour = asArray(o.byhour);
	const byminute = asArray(o.byminute);
	const bymonthday = asArray(o.bymonthday);
	if (byhour.length > 1 || byminute.length > 1) return null;
	const weekDays = [];
	for (const w of asArray(o.byweekday)) {
		const n = typeof w === "number" ? w : w.weekday;
		if (!isNil(typeof w === "number" ? void 0 : w.n)) return null;
		const code = RRULE_WEEKDAY_CODES[n];
		if (code) weekDays.push(code);
	}
	const hour = byhour[0] ?? 0;
	const minute = byminute[0] ?? 0;
	const hasDtstart = /DTSTART/i.test(rule);
	let end = { type: "never" };
	if (!isNil(o.count)) end = {
		type: "count",
		count: o.count
	};
	else if (!isNil(o.until)) end = {
		type: "until",
		until: formatFloatingDate(o.until, true)
	};
	return {
		frequency,
		interval: o.interval ?? 1,
		triggerTime: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
		hourlyMinute: minute,
		weekDays: weekDays.length ? weekDays : [...DEFAULT_ADVANCED_SCHEDULE.weekDays],
		monthDays: bymonthday.length ? [...bymonthday].sort((a, b) => a - b) : [...DEFAULT_ADVANCED_SCHEDULE.monthDays],
		startAt: hasDtstart ? formatFloatingDate(rrule.options.dtstart, true) : null,
		end,
		isCustom: !isLegacyDialect(rule)
	};
}
//#endregion
//#region src/routines/routine-run.types.ts
const REPORT_TEXT_GENERATING = "GENERATING";
const REPORT_TEXT_NO_DATA = "NO DATA";
/** Mirrors the desktop routine schema's defaults. */
const ROUTINE_SOURCE_DEFAULTS = {
	kind: "digest",
	completedAt: null,
	icon: null,
	projectId: null,
	agentMode: "regular",
	isExperimental: false,
	pauseReason: null,
	emailNotificationsEnabled: false,
	autoPauseEnabled: true,
	deliveryChannel: "off",
	mayReachOut: true,
	waitingOnYou: null,
	lastManualGenerationAt: null,
	teamShare: null
};
const toRoutineIsoString = (value) => typeof value === "string" ? value : value.toISOString();
//#endregion
//#region src/routines/routine-run.adapter.ts
const MAX_HIGHLIGHT_WORDS = 8;
const USER_WAIT_STATUSES = {
	reply: "needs_reply",
	approval: "needs_approval"
};
/**
* What an open run waits on its user for. A run past its deadline is told to
* finish without them, so it waits on nothing even before that turn starts.
*/
const getRunUserWait = (report, now = Date.now()) => {
	const reachOut = report.reachOut;
	if (!(report.text.trim() === "GENERATING") || !reachOut?.waiting) return null;
	return reachOut.deadline.getTime() > now ? reachOut.waiting : null;
};
/** How lists name a run that waits on its user */
const formatUserWaitTitle = (wait) => wait.kind === "reply" ? `Needs you: ${wait.reason}` : "Waiting for your approval";
const getRunStatus = (reportText, metadata, waitingOn = [], userWait = null) => {
	if (metadata?.status) return metadata.status;
	const normalizedText = reportText.trim();
	if (normalizedText === "GENERATING") {
		if (userWait) return USER_WAIT_STATUSES[userWait.kind];
		return waitingOn.length > 0 ? "waiting" : "running";
	}
	if (normalizedText === "NO DATA") return "clear";
	return "found";
};
const isRunWaiting = (report) => getRunStatus(report.text, void 0, report.waitingOn) === "waiting";
const getFallbackHighlightTitle = (reportText, status, waitingOn = [], userWait = null) => {
	if (status === "running") return "Running now";
	if (status === "waiting") return `Waiting for ${devicesWord(waitingOn)}`;
	if (userWait && (status === "needs_reply" || status === "needs_approval")) return formatUserWaitTitle(userWait);
	if (status === "clear") return "No new findings";
	if (status === "error") return "Run needs attention";
	const plainText = stripMarkdown(reportText).replace(/\s+/g, " ").trim();
	if (!plainText || plainText.startsWith("{") || plainText.startsWith("[")) return "Run completed";
	const words = plainText.split(" ");
	const title = words.slice(0, MAX_HIGHLIGHT_WORDS).join(" ");
	return words.length > MAX_HIGHLIGHT_WORDS ? `${title}…` : title;
};
/**
* A run is unread once it has stopped running and nobody has opened it.
*
* Kept off the view model: building one strips the markdown off every report
* body via `highlightTitle`, which this two-field answer does not need.
*/
const isRoutineRunUnread = (reportText, hasBeenViewed, metadata) => {
	return getRunStatus(reportText, metadata) !== "running" && !hasBeenViewed;
};
const adaptReportToRoutineRun = (report, metadata) => {
	const waitingOn = report.waitingOn ?? [];
	const userWait = getRunUserWait(report);
	const status = getRunStatus(report.text, metadata, waitingOn, userWait);
	const scheduledAt = toRoutineIsoString(report.scheduledAt);
	return {
		id: report.referenceId,
		routineId: report.reportTemplateId,
		status,
		highlightTitle: metadata?.highlightTitle?.trim() || getFallbackHighlightTitle(report.text, status, waitingOn, userWait),
		timestamp: scheduledAt,
		scheduledAt,
		narrative: report.text,
		transcript: metadata?.transcript,
		usage: metadata?.usage,
		stepCount: metadata?.stepCount,
		chatId: report.chatId ?? void 0,
		artifactFileIds: report.artifactFileIds ?? [],
		waitingOn,
		ranWithout: report.ranWithout ?? [],
		reachOut: report.reachOut ?? null,
		backend: report.backend ?? null,
		isUnread: isRoutineRunUnread(report.text, report.lastViewed !== null, metadata),
		createdAt: toRoutineIsoString(report.createdAt),
		updatedAt: report.updatedAt === void 0 ? void 0 : toRoutineIsoString(report.updatedAt)
	};
};
const compareRunsNewestFirst = (routineRunA, routineRunB) => {
	const routineRunATime = Date.parse(routineRunA.timestamp);
	const routineRunBTime = Date.parse(routineRunB.timestamp);
	const safeRoutineRunATime = Number.isNaN(routineRunATime) ? 0 : routineRunATime;
	return (Number.isNaN(routineRunBTime) ? 0 : routineRunBTime) - safeRoutineRunATime || routineRunB.id - routineRunA.id;
};
const adaptSchedule = (routine, formatTime) => {
	const schedule = parseRoutineSchedule(routine.recurrenceRule);
	const endsOnATime = schedule.frequency !== "Hourly" && schedule.frequency !== "Advanced" && schedule.frequency !== "Interval";
	const summary = formatRoutineScheduleSummary(schedule, { formatTime });
	return {
		recurrenceType: routine.recurrenceType,
		recurrenceRule: routine.recurrenceRule,
		frequency: schedule.frequency,
		triggerTime: schedule.triggerTime,
		hourlyEndTime: schedule.hourlyEndTime,
		weekDays: schedule.weekDays,
		monthDay: schedule.monthDay,
		hasFrequency: schedule.frequency === "Hourly" || schedule.frequency === "Advanced" || schedule.frequency === "Interval" || parseRoutineRecurrenceRule(routine.recurrenceRule).frequency !== void 0,
		isValid: schedule.isValid,
		summary: !schedule.isValid ? routine.recurrenceType || "Scheduled" : endsOnATime ? summary.replace(/ · (?=[^·]+$)/, " at ") : summary
	};
};
const adaptRoutineToViewModel = (routine, reports, options = {}) => {
	const formatTime = options.formatTime ?? ((triggerTime) => triggerTime);
	const runs = reports.filter((report) => report.reportTemplateId === routine.referenceId).map((report) => adaptReportToRoutineRun(report, options.metadataByReportId?.[report.referenceId])).sort(compareRunsNewestFirst);
	return {
		id: routine.referenceId,
		title: routine.title,
		kind: routine.kind ?? ROUTINE_SOURCE_DEFAULTS.kind,
		completedAt: routine.completedAt ? toRoutineIsoString(routine.completedAt) : ROUTINE_SOURCE_DEFAULTS.completedAt,
		icon: routine.icon ?? ROUTINE_SOURCE_DEFAULTS.icon,
		projectId: routine.projectId ?? ROUTINE_SOURCE_DEFAULTS.projectId,
		instructions: routine.prompt,
		agentMode: routine.agentMode ?? ROUTINE_SOURCE_DEFAULTS.agentMode,
		isPaused: routine.isPaused,
		isAdminPaused: routine.isAdminPaused ?? false,
		isExperimental: routine.isExperimental ?? ROUTINE_SOURCE_DEFAULTS.isExperimental,
		pauseReason: routine.pauseReason ?? ROUTINE_SOURCE_DEFAULTS.pauseReason,
		isUnread: runs[0]?.isUnread ?? false,
		notificationsEnabled: routine.notificationsEnabled,
		emailNotificationsEnabled: routine.emailNotificationsEnabled ?? ROUTINE_SOURCE_DEFAULTS.emailNotificationsEnabled,
		deliveryChannel: routine.deliveryChannel ?? ROUTINE_SOURCE_DEFAULTS.deliveryChannel,
		autoPauseEnabled: routine.autoPauseEnabled ?? ROUTINE_SOURCE_DEFAULTS.autoPauseEnabled,
		mayReachOut: routine.mayReachOut ?? ROUTINE_SOURCE_DEFAULTS.mayReachOut,
		waitingOnYou: routine.waitingOnYou ?? ROUTINE_SOURCE_DEFAULTS.waitingOnYou,
		lastManualGenerationAt: routine.lastManualGenerationAt ? toRoutineIsoString(routine.lastManualGenerationAt) : ROUTINE_SOURCE_DEFAULTS.lastManualGenerationAt,
		teamShare: routine.teamShare ?? ROUTINE_SOURCE_DEFAULTS.teamShare,
		schedule: adaptSchedule(routine, formatTime),
		runs,
		searchText: `${routine.title} ${routine.prompt}`.toLowerCase(),
		createdAt: toRoutineIsoString(routine.createdAt),
		updatedAt: toRoutineIsoString(routine.updatedAt)
	};
};
//#endregion
//#region src/routines/routine-actions.ts
const IMPROVE_ROUTINE_PROMPT = "I want to improve this routine. Ask me what I don't like about recent runs, what's missing, and what's too noisy. Then propose better instructions and apply them once I confirm.";
const USER_ROUTINE_PERMISSIONS = {
	canEditDefinition: true,
	canChangeAgentMode: true,
	canChangeDeliverySettings: true,
	canPause: true,
	canResume: true,
	canRun: true,
	canDelete: true,
	canShare: true,
	canImprove: true
};
const EXPERIMENTAL_ROUTINE_PERMISSIONS = {
	canEditDefinition: false,
	canChangeAgentMode: false,
	canChangeDeliverySettings: false,
	canPause: true,
	canResume: true,
	canRun: false,
	canDelete: false,
	canShare: false,
	canImprove: false
};
const ROUTINE_AUTO_PAUSE_RESUME_CONFIRMATION = {
	title: "Resume this routine?",
	description: "Littlebird auto-pauses routines when their recent reports go unread, so you don't keep spending usage credits on something you're not looking at. To stop this routine from pausing again, edit the routine and turn off Auto-pause in its settings.",
	confirmText: "Resume"
};
const ROUTINE_AGENT_ARCHIVED_RESUME_CONFIRMATION = {
	title: "Resume this routine?",
	description: "This routine paused when its agent was archived. Its reports will show up here, but not in the agent's thread until you restore the agent.",
	confirmText: "Resume"
};
const ROUTINE_REMOVE_TEAM_TEMPLATE_CONFIRMATION = {
	title: "Remove from the team library?",
	description: "Teammates who already added this routine keep theirs. It just stops showing up for your team.",
	confirmText: "Remove"
};
const getResumeConfirmation = (routine) => routine.pauseReason === "manual" || routine.pauseReason === "agent_paused" ? null : routine.pauseReason === "agent_archived" ? ROUTINE_AGENT_ARCHIVED_RESUME_CONFIRMATION : ROUTINE_AUTO_PAUSE_RESUME_CONFIRMATION;
const getWaitingFor = (newestRun) => newestRun && isRunWaiting(newestRun) ? devicesWord(newestRun.waitingOn ?? []) : void 0;
/** A waiting run blocks Run; a pending run's message wins */
const getRunDisabledReason = (isRunPending, waitingFor) => {
	if (isRunPending) return "A run is in progress";
	return waitingFor && `A run is waiting for ${waitingFor}`;
};
const getRoutineActionState = (routine, latestCompletedRun) => {
	const permissions = routine.permissions ?? (routine.isExperimental ? EXPERIMENTAL_ROUTINE_PERMISSIONS : USER_ROUTINE_PERMISSIONS);
	const waitingFor = getWaitingFor(routine.newestRun);
	const runDisabledReason = getRunDisabledReason(routine.isRunPending ?? false, waitingFor);
	const improveThreadId = latestCompletedRun?.chatId;
	const canImprove = Boolean(improveThreadId);
	return {
		permissions,
		canRun: !routine.isPaused && !runDisabledReason && permissions.canRun,
		canConfigure: permissions.canEditDefinition || permissions.canChangeDeliverySettings,
		canDelete: permissions.canDelete,
		canShare: permissions.canShare,
		runDisabledReason,
		waitingFor,
		canPause: !routine.isPaused && permissions.canPause,
		canResume: routine.isPaused && permissions.canResume && !(routine.isAdminPaused ?? false),
		canImprove: canImprove && permissions.canImprove,
		improveDisabledReason: !permissions.canImprove ? "This routine cannot be changed" : canImprove ? void 0 : latestCompletedRun === void 0 ? "Checking for a completed run..." : "Complete a run before improving this routine",
		improveRunId: canImprove ? latestCompletedRun?.id : void 0,
		improveThreadId: canImprove ? improveThreadId : void 0
	};
};
//#endregion
//#region src/routines/routine-chat.ts
/**
* The first user message of a thread rebuilt from a routine run.
*
* Both clients must produce the same string: it is what the assistant's stored
* reply answers, so a run rebuilt on mobile matches one rebuilt on desktop. The
* period is the run's own schedule stepped back once from when it ran.
*/
const buildRoutineRunChatPrompt = (prompt, recurrenceRule, scheduledAt) => {
	const { frequency } = parseRoutineRecurrenceRule(recurrenceRule);
	const startDate = startOfDay(frequency === "Weekly" ? subWeeks(scheduledAt, 1) : frequency === "Monthly" ? subMonths(scheduledAt, 1) : subDays(scheduledAt, 1));
	return `${prompt}\n\nThis is for the time period ${`${format(startDate, "MMMM d")} - ${format(scheduledAt, "MMMM d, yyyy")}`}.`;
};
//#endregion
//#region src/routines/routine-delivery.ts
/** The channels a report can be texted to; `'off'` is the absence of one. */
const ROUTINE_MESSAGING_CHANNELS = ["imessage", "whatsapp_business"];
const ROUTINE_MESSAGING_CHANNEL_LABELS = {
	imessage: "iMessage",
	whatsapp_business: "WhatsApp"
};
const parseRoutineMessagingChannels = (payload) => {
	if (!payload || typeof payload !== "object") return [];
	const enabled = payload;
	return ROUTINE_MESSAGING_CHANNELS.filter((channel) => enabled[channel] === true);
};
const buildRoutineDeliveryOptions = ({ enabledChannels, connections, selectedChannel }) => {
	const connected = enabledChannels.flatMap((channel) => {
		const connection = connections.get(channel);
		return connection ? [{
			channel,
			connection
		}] : [];
	});
	const options = [{
		channel: "off",
		label: "Off",
		disabled: false
	}, ...connected.map(({ channel, connection }) => {
		const name = ROUTINE_MESSAGING_CHANNEL_LABELS[channel];
		return {
			channel,
			label: connection.hasActiveConversation ? name : `${name} (unavailable)`,
			disabled: !connection.hasActiveConversation
		};
	})];
	const selected = options.find((option) => option.channel === selectedChannel && !option.disabled);
	return {
		isVisible: enabledChannels.length > 0,
		needsConnection: connected.length === 0,
		options,
		selectedLabel: selected?.label ?? "Off",
		isSelectionUnavailable: selectedChannel !== "off" && selected === void 0 && (!enabledChannels.includes(selectedChannel) || connections.has(selectedChannel))
	};
};
//#endregion
//#region src/routines/routine-filtering.ts
const STATUS_MATCHERS = {
	active: (routine) => !routine.isPaused && !routine.isExperimental && !routine.completedAt,
	paused: (routine) => routine.isPaused && !routine.isExperimental && !routine.completedAt,
	experimental: (routine) => routine.isExperimental,
	unread: (routine) => routine.isUnread,
	suggestions: () => false
};
const filterRoutineViewModels = (routines, { query = "", statuses = [], frequencies = [] } = {}) => {
	const normalizedQuery = query.trim().toLowerCase();
	return routines.filter((routine) => {
		const matchesStatus = statuses.length === 0 || statuses.some((status) => STATUS_MATCHERS[status](routine));
		const matchesFrequency = frequencies.length === 0 || routine.schedule.hasFrequency && frequencies.includes(routine.schedule.frequency);
		const matchesQuery = !normalizedQuery || routine.searchText.includes(normalizedQuery);
		return matchesStatus && matchesFrequency && matchesQuery;
	});
};
//#endregion
//#region src/routines/routine-form.ts
const WEEKDAYS_FREQUENCY = "Weekdays";
const ROUTINE_FREQUENCY_OPTIONS = [
	{
		value: "Hourly",
		description: "Every hour within a daily time range",
		isAdvancedOnly: true
	},
	{
		value: "Daily",
		description: "Every day at a chosen time"
	},
	{
		value: WEEKDAYS_FREQUENCY,
		description: "Monday to Friday at a chosen time"
	},
	{
		value: "Weekly",
		description: "Every week on chosen days"
	},
	{
		value: "Monthly",
		description: "Every month on a chosen day"
	},
	{
		value: "Interval",
		description: "Every few minutes, hours, or days",
		isAdvancedOnly: true
	},
	{
		value: "Advanced",
		description: "Choose months, days, and times",
		isAdvancedOnly: true
	}
];
const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec"
];
const buildRoutineFormRecurrenceRule = (draft) => buildRoutineRecurrenceRule(toRoutineScheduleParts(draft));
const BUILT_RECURRENCE_TYPE = "time_interval";
const buildRoutineUpsertInput = (draft, { storedRecurrenceType, storedRecurrenceRule } = {}) => {
	const recurrenceRule = draft.preservedRecurrenceRule ?? buildRoutineFormRecurrenceRule(draft);
	const storedSchedule = storedRecurrenceRule == null ? null : parseRoutineSchedule(storedRecurrenceRule);
	const canonicalStoredRule = storedSchedule?.isValid ? buildRoutineRecurrenceRule(storedSchedule) : null;
	const scheduleUnchanged = draft.preservedRecurrenceRule !== null || recurrenceRule === canonicalStoredRule;
	const outgoingRule = scheduleUnchanged ? storedRecurrenceRule || recurrenceRule : recurrenceRule;
	return {
		title: draft.title.trim(),
		icon: draft.icon,
		prompt: draft.prompt.trim(),
		recurrenceType: scheduleUnchanged ? storedRecurrenceType || BUILT_RECURRENCE_TYPE : BUILT_RECURRENCE_TYPE,
		recurrenceRule: draft.scheduleEnabled ? outgoingRule : null,
		notificationsEnabled: draft.notificationsEnabled,
		emailNotificationsEnabled: draft.emailNotificationsEnabled,
		deliveryChannel: draft.deliveryChannel,
		autoPauseEnabled: draft.autoPauseEnabled,
		mayReachOut: draft.mayReachOut,
		agentMode: draft.agentMode,
		triggers: draft.triggers,
		kind: draft.kind
	};
};
//#endregion
//#region src/routines/routine-selection.ts
const selectRoutineRun = (runs, { requestedRunId, pinnedRunId }) => {
	const requestedRun = runs.find((run) => run.id === requestedRunId);
	const pinnedRun = pinnedRunId === void 0 || pinnedRunId === null ? void 0 : runs.find((run) => run.id === pinnedRunId);
	return {
		selectedRun: requestedRun ?? pinnedRun ?? runs[0] ?? null,
		isPinned: requestedRun !== void 0 || pinnedRun !== void 0
	};
};
/**
* The run to select after deleting one, preferring the older neighbour.
* `runIds` are the runs as they stood before the deletion, newest first.
* `null` when none is left.
*/
const selectRunIdAfterDeletion = (runIds, deletedRunId) => {
	const deletedIndex = runIds.indexOf(deletedRunId);
	if (deletedIndex === -1) return null;
	return runIds[deletedIndex + 1] ?? runIds[deletedIndex - 1] ?? null;
};
const isCompletedRoutineRun = (run) => {
	const narrative = run.narrative.trim();
	return narrative !== "" && narrative !== "GENERATING" && narrative !== "NO DATA";
};
const findLatestCompletedRoutineRun = (runs) => runs.find(isCompletedRoutineRun);
//#endregion
//#region src/routines/routine-suggestions.ts
const normalizeRoutineTitle = (title) => title.trim().replace(/[.!?]+$/, "").toLowerCase();
const dedupeRoutineSuggestions = (suggestions, routines) => {
	const existingTitles = new Set(routines.map((routine) => normalizeRoutineTitle(routine.title)));
	return suggestions.filter((suggestion) => !existingTitles.has(normalizeRoutineTitle(suggestion.title)));
};
const suggestionToRoutineTemplate = (suggestion) => {
	const { frequency, triggerTime, weekDays, monthDay } = parseRoutineRecurrenceRule(suggestion.recurrenceRule);
	return {
		id: `generated-${suggestion.id}`,
		title: suggestion.title,
		recurrence_type: "time_interval",
		recurrence_rule: suggestion.recurrenceRule,
		prompt: suggestion.prompt,
		description: suggestion.rationale,
		tags: [],
		integrations: suggestion.integrations,
		frequency: frequency ?? null,
		trigger_time: triggerTime ?? "",
		week_day: weekDays?.join(",") ?? null,
		month_day: monthDay
	};
};
//#endregion
//#region src/routines/use-initial-routine-draft.ts
const useInitialRoutineDraft = (open, createDraft) => useMemo(createDraft, [open, createDraft]);
//#endregion
//#region src/routines/use-max-mode-fallback.ts
/**
* Max stays pickable while entitlements load. Once they resolve without it, a
* draft falls back but a routine already stored on Max keeps it.
*/
function useMaxModeFallback({ agentMode, storedAgentMode, isMaxAvailable, isResolved, setAgentMode, notifyInfo }) {
	useEffect(() => {
		if (!isResolved || isMaxAvailable) return;
		if (agentMode !== "max" || storedAgentMode === "max") return;
		setAgentMode("regular");
		notifyInfo("Switched to Default", "Max needs a paid plan.");
	}, [
		agentMode,
		isMaxAvailable,
		isResolved,
		notifyInfo,
		setAgentMode,
		storedAgentMode
	]);
}
//#endregion
//#region src/routines/use-routine-form-draft.ts
const useRoutineFormDraft = ({ routineDrafts, open = true, keepDraftOnClose = false, draftOwnerId, draftKey, initialDraft, onOpenChange }) => {
	"use no memo";
	const [, refreshDraft] = useReducer((revision) => revision + 1, 0);
	const draft = routineDrafts.getDraft(draftOwnerId, draftKey) ?? initialDraft;
	const updateDraft = useCallback((updates) => {
		routineDrafts.updateDraft(draftOwnerId, draftKey, initialDraft, updates);
		refreshDraft();
	}, [
		draftKey,
		draftOwnerId,
		initialDraft,
		routineDrafts
	]);
	const clearDraft = useCallback(() => {
		routineDrafts.clearDraft(draftOwnerId, draftKey);
		refreshDraft();
	}, [
		draftKey,
		draftOwnerId,
		routineDrafts
	]);
	const handleOpenChange = useCallback((nextOpen) => {
		if (!nextOpen && !keepDraftOnClose) routineDrafts.clearDraft(draftOwnerId, draftKey);
		onOpenChange?.(nextOpen);
	}, [
		draftKey,
		draftOwnerId,
		keepDraftOnClose,
		onOpenChange,
		routineDrafts
	]);
	useEffect(() => {
		if (!open) {
			if (!keepDraftOnClose) routineDrafts.clearDraft(draftOwnerId, draftKey);
			return;
		}
		routineDrafts.initializeDraft(draftOwnerId, draftKey, initialDraft);
		refreshDraft();
	}, [
		draftKey,
		draftOwnerId,
		initialDraft,
		keepDraftOnClose,
		open,
		routineDrafts
	]);
	return {
		draft,
		updateDraft,
		clearDraft,
		handleOpenChange
	};
};
//#endregion
//#region src/routines/use-routine-refresh.ts
const ROUTINES_REFRESH_INTERVAL_MS = 3e5;
const REPORTS_REFRESH_INTERVAL_MS = 6e4;
const REPORTS_GENERATING_REFRESH_INTERVAL_MS = 5e3;
/** The shared routines cadence; knows nothing about the store it refreshes. */
const useRoutineRefresh = ({ refreshRoutines, refreshReports, hasGeneratingReport = false, enabled = true, reportsEnabled = enabled, subscribeToForeground }) => {
	const refreshRef = useRef({
		refreshRoutines,
		refreshReports
	});
	refreshRef.current = {
		refreshRoutines,
		refreshReports
	};
	useEffect(() => {
		if (!enabled) return;
		refreshRef.current.refreshRoutines();
		const routinesTimer = setInterval(() => refreshRef.current.refreshRoutines(), ROUTINES_REFRESH_INTERVAL_MS);
		return () => clearInterval(routinesTimer);
	}, [enabled]);
	useEffect(() => {
		if (!reportsEnabled) return;
		refreshRef.current.refreshReports();
	}, [reportsEnabled]);
	useEffect(() => {
		if (!reportsEnabled) return;
		const reportsTimer = setInterval(() => refreshRef.current.refreshReports(), hasGeneratingReport ? REPORTS_GENERATING_REFRESH_INTERVAL_MS : REPORTS_REFRESH_INTERVAL_MS);
		return () => clearInterval(reportsTimer);
	}, [reportsEnabled, hasGeneratingReport]);
	useEffect(() => {
		if (!subscribeToForeground) return;
		if (!enabled && !reportsEnabled) return;
		return subscribeToForeground(() => {
			if (enabled) refreshRef.current.refreshRoutines();
			if (reportsEnabled) refreshRef.current.refreshReports();
		});
	}, [
		enabled,
		reportsEnabled,
		subscribeToForeground
	]);
};
//#endregion
//#region src/routines/webhook-endpoint-setup-url.ts
/**
* The link the agent writes after it creates an event source. The backend builds it on its own frontend URL, which
* differs per environment, so any host matches. An app that doesn't know the link opens it as Settings > Webhooks.
*/
function parseWebhookEndpointSetupUrl(href) {
	try {
		const url = new URL(href);
		if (url.pathname.replace(/\/$/, "") !== "/chats") return { kind: "none" };
		const endpointId = url.searchParams.get("webhook_endpoint_id");
		if (!endpointId) return { kind: "none" };
		return {
			kind: "webhookEndpointSetup",
			endpointId
		};
	} catch {
		return { kind: "none" };
	}
}
//#endregion
//#region src/sharing/share-link.types.ts
const DEFAULT_SHARE_STATUS = {
	isShared: false,
	publicToken: null,
	visibility: "private",
	sharedTeamId: null,
	sharedMsgLength: null,
	currentMsgLength: 0,
	hasNewMessages: false
};
//#endregion
//#region src/sharing/share-link.url.ts
function buildSharedChatUrl(chatId, publicToken, baseUrl) {
	const url = new URL(`/shared-chat/${chatId}`, baseUrl);
	url.searchParams.set("publicToken", publicToken);
	return url.toString();
}
function buildSharedArenaUrl(chatIds, publicTokens, baseUrl) {
	const url = new URL("/shared-arena", baseUrl);
	url.searchParams.set("chatIds", chatIds.join(","));
	url.searchParams.set("publicTokens", publicTokens.join(","));
	return url.toString();
}
function buildSharedMeetingUrl(meetingId, publicToken, baseUrl) {
	const url = new URL(`/shared-meeting/${meetingId}`, baseUrl);
	if (publicToken) url.searchParams.set("publicToken", publicToken);
	return url.toString();
}
function buildSharedSiteUrl(token, baseUrl) {
	return new URL(`/artifact/${token}`, baseUrl).toString();
}
function buildProjectJoinUrl(token, baseUrl) {
	return new URL(`/projects/join/${token}`, baseUrl).toString();
}
//#endregion
//#region src/sharing/use-share-link.ts
const COPIED_RESET_MS = 1500;
function useLatestOperation() {
	const epochRef = useRef(0);
	return useCallback(() => {
		const epoch = ++epochRef.current;
		return () => epochRef.current === epoch;
	}, []);
}
function collectTokens(results) {
	const tokens = [];
	for (const result of results) {
		if (result.publicToken === null) return null;
		tokens.push(result.publicToken);
	}
	return tokens;
}
async function loadShareStatus(ids, adapter, isLatest, set) {
	set.setPhase({ kind: "checking" });
	set.setUrl(null);
	set.setShareStatus(null);
	set.setInvitedEmails([]);
	try {
		const results = await Promise.all(ids.map((id) => adapter.getStatus(id)));
		if (!isLatest()) return;
		const allShared = results.every((result) => result.isShared);
		const tokens = collectTokens(results);
		if (allShared && tokens) {
			set.setUrl(adapter.buildUrl(ids, tokens));
			set.setShareStatus({
				...results[0],
				hasNewMessages: results.some((r) => r.hasNewMessages)
			});
			set.setInvitedEmails(results[0].invitedEmails ?? []);
		} else set.setShareStatus(allShared ? results[0] : DEFAULT_SHARE_STATUS);
	} catch (error) {
		if (isLatest()) set.setShareStatus(DEFAULT_SHARE_STATUS);
		adapter.notifyError?.("Failed to load share status", error);
	} finally {
		if (isLatest()) set.setPhase({ kind: "idle" });
	}
}
async function createShareLink(ids, visibility, adapter, isLatest, set) {
	set.setPhase({
		kind: "creating",
		visibility
	});
	try {
		const results = await Promise.all(ids.map((id) => adapter.setVisibility(id, visibility)));
		if (!isLatest()) return;
		const tokens = collectTokens(results);
		if (!tokens) {
			adapter.notifyError?.("Please try again");
			return;
		}
		const constructedUrl = adapter.buildUrl(ids, tokens);
		const first = results[0];
		set.setUrl(constructedUrl);
		set.setShareStatus({
			isShared: true,
			publicToken: tokens[0],
			visibility,
			sharedTeamId: first.sharedTeamId,
			sharedMsgLength: first.sharedMsgLength,
			currentMsgLength: first.sharedMsgLength ?? 0,
			hasNewMessages: false
		});
		adapter.onShared?.(ids, visibility);
		const copiedToClipboard = await adapter.onLinkCreated?.(constructedUrl, ids);
		if (!isLatest() || copiedToClipboard !== true) return;
		set.setCopied(true);
		setTimeout(() => set.setCopied(false), COPIED_RESET_MS);
	} catch (error) {
		if (isLatest()) adapter.notifyError?.("Please try again", error);
	} finally {
		if (isLatest()) set.setPhase({ kind: "idle" });
	}
}
async function revokeShareLink(ids, adapter, isLatest, set) {
	set.setPhase({ kind: "revoking" });
	try {
		await Promise.all(ids.map((id) => adapter.revoke(id)));
		if (!isLatest()) return;
		set.setUrl(null);
		set.setShareStatus(DEFAULT_SHARE_STATUS);
		set.setInvitedEmails([]);
		adapter.onUnshared?.(ids);
	} catch (error) {
		if (isLatest()) adapter.notifyError?.("Please try again", error);
	} finally {
		if (isLatest()) set.setPhase({ kind: "idle" });
	}
}
async function copyShareLink(url, notify, setCopied) {
	try {
		await navigator.clipboard.writeText(url);
		notify.notifySuccess?.("Copied to clipboard");
		if (setCopied) {
			setCopied(true);
			setTimeout(() => setCopied(false), COPIED_RESET_MS);
		}
		return true;
	} catch (error) {
		notify.notifyError?.("You can select the URL and copy manually.", error);
		return false;
	}
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function useInviteRequests(adapter, setInvitedEmails) {
	const queue = useRef(Promise.resolve());
	const generation = useRef(0);
	const beginInviteOperation = useLatestOperation();
	return {
		send: useCallback((request, rollback, errorMessage) => {
			const isLatest = beginInviteOperation();
			const queuedIn = generation.current;
			queue.current = queue.current.then(async () => {
				if (queuedIn !== generation.current) return;
				try {
					const emails = await request();
					if (isLatest()) setInvitedEmails(emails);
				} catch (error) {
					if (queuedIn === generation.current) setInvitedEmails(rollback);
					adapter.notifyError?.(errorMessage, error);
				}
			});
		}, [
			adapter,
			beginInviteOperation,
			setInvitedEmails
		]),
		invalidate: useCallback(() => {
			generation.current += 1;
			beginInviteOperation();
		}, [beginInviteOperation])
	};
}
function useShareInvites(id, adapter) {
	const [invitedEmails, setInvitedEmails] = useState([]);
	const { send, invalidate } = useInviteRequests(adapter, setInvitedEmails);
	return {
		invitedEmails,
		setInvitedEmails: useCallback((emails) => {
			invalidate();
			setInvitedEmails(emails);
		}, [invalidate]),
		handleAddInvite: useCallback((draft) => {
			const email = draft.trim().toLowerCase();
			if (!EMAIL_PATTERN.test(email)) return "Enter a valid email address";
			if (invitedEmails.includes(email)) return "Already invited";
			const { invites } = adapter;
			if (!id || !invites) return "Inviting people isn't available here";
			setInvitedEmails((emails) => [...emails, email]);
			send(() => invites.add(id, email), (emails) => emails.filter((e) => e !== email), `Couldn't invite ${email}`);
			return null;
		}, [
			id,
			adapter,
			invitedEmails,
			send
		]),
		handleRemoveInvite: useCallback((email) => {
			const { invites } = adapter;
			if (!id || !invites) return;
			setInvitedEmails((emails) => emails.filter((e) => e !== email));
			send(() => invites.remove(id, email), (emails) => emails.includes(email) ? emails : [...emails, email], `Couldn't remove ${email}`);
		}, [
			id,
			adapter,
			send
		])
	};
}
function useShareController({ ids, adapter, open }) {
	const [url, setUrl] = useState(null);
	const [shareStatus, setShareStatus] = useState(null);
	const [phase, setPhase] = useState(open ? { kind: "checking" } : { kind: "idle" });
	const [copied, setCopied] = useState(false);
	const idsKey = ids.join(",");
	const stableIds = useRef(ids);
	if (idsKey !== stableIds.current.join(",")) stableIds.current = ids;
	const beginOperation = useLatestOperation();
	const invites = useShareInvites(ids[0], adapter);
	const setters = useRef({
		setUrl,
		setShareStatus,
		setInvitedEmails: invites.setInvitedEmails,
		setPhase,
		setCopied
	}).current;
	useEffect(() => {
		const currentIds = stableIds.current;
		if (!open || !currentIds.length) return;
		loadShareStatus(currentIds, adapter, beginOperation(), setters);
	}, [
		open,
		idsKey,
		adapter,
		beginOperation,
		setters
	]);
	const handleCreateOrUpdateLink = useCallback((visibility = "public") => {
		const currentIds = stableIds.current;
		if (!currentIds.length) return;
		createShareLink(currentIds, visibility, adapter, beginOperation(), setters);
	}, [
		adapter,
		beginOperation,
		setters
	]);
	const handleRevoke = useCallback(() => {
		const currentIds = stableIds.current;
		if (!currentIds.length) return;
		revokeShareLink(currentIds, adapter, beginOperation(), setters);
	}, [
		adapter,
		beginOperation,
		setters
	]);
	return {
		url,
		shareStatus,
		phase,
		copied,
		handleCreateOrUpdateLink,
		handleCopyLink: useCallback(() => {
			if (url) copyShareLink(url, adapter, setCopied);
		}, [url, adapter]),
		handleRevoke,
		...invites
	};
}
function useShareLink(options) {
	const { ids, adapter, supportsTeam, canShareBeyondPrivate, hasTeam } = options;
	const { url, shareStatus, invitedEmails, phase, copied, handleCreateOrUpdateLink, handleCopyLink, handleRevoke, handleAddInvite, handleRemoveInvite } = useShareController(options);
	return {
		url,
		shareStatus,
		isCheckingStatus: phase.kind === "checking",
		isCreating: phase.kind === "creating",
		creatingVisibility: phase.kind === "creating" ? phase.visibility : null,
		isRevoking: phase.kind === "revoking",
		isLoading: phase.kind !== "idle",
		copied,
		selectedVisibility: shareStatus?.visibility ?? "private",
		showTeam: canShareBeyondPrivate && supportsTeam && hasTeam,
		showPublic: canShareBeyondPrivate,
		showPeople: canShareBeyondPrivate && !!adapter.invites && ids.length === 1,
		invitedEmails,
		handleCreateOrUpdateLink,
		handleCopyLink,
		handleRevoke,
		handleAddInvite,
		handleRemoveInvite
	};
}
//#endregion
//#region src/projects/capabilities.ts
const EMPTY_PROJECT_CAPABILITIES = {
	canEditContent: false,
	canInvite: false,
	canManageMembers: false,
	canManageLink: false,
	canRename: false,
	canDelete: false,
	canLeave: false
};
//#endregion
//#region src/projects/invite-suggestions.ts
const DEFAULT_MAX_SUGGESTIONS = 6;
const EMAIL_ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const lowercased = (emails) => emails.map((email) => email.toLowerCase());
const emailSet = (holders) => new Set(holders.map((holder) => holder.email?.toLowerCase()).filter((email) => Boolean(email)));
const matchesQuery = (needle, ...fields) => !needle || fields.some((field) => field?.toLowerCase().includes(needle) ?? false);
/** The people who are neither on the team nor in the project: the wiki people that match the query, and the address
* itself when the query is a whole address that no one holds. A person who only asked to connect is not one that the
* user knows, so their address brings up their page, with the request on it, and no name does. */
function suggestOutsiders(people, needle, teamOrProjectEmails) {
	if (!needle) return [];
	const wikiPeople = people.filter(({ name, emails, isRequestOnly }) => {
		const addresses = lowercased(emails);
		return addresses.length > 0 && !addresses.some((address) => teamOrProjectEmails.has(address)) && matchesQuery(needle, name, ...emails) && (!isRequestOnly || addresses.includes(needle));
	}).map((person) => ({
		key: `person:${person.id}`,
		name: person.name,
		detail: person.emails.find((email) => email.toLowerCase() === needle) ?? person.emails.find((email) => email.toLowerCase().includes(needle)) ?? person.emails[0],
		isTeam: false,
		personId: person.id
	}));
	const isHeldAddress = teamOrProjectEmails.has(needle) || people.some(({ emails }) => lowercased(emails).includes(needle));
	if (!EMAIL_ADDRESS.test(needle) || isHeldAddress) return wikiPeople;
	const typedAddress = {
		key: `typed:${needle}`,
		name: needle,
		detail: needle,
		isTeam: false,
		typedEmail: true
	};
	return [...wikiPeople, typedAddress];
}
/** The rows for the team: "Everyone at {team}", and the teammates who match the query and are not in the project */
function suggestTeam(team, members, needle, takenEmails) {
	const teamAlreadyGranted = members.some((member) => member.teamId === team.team.id);
	const teammates = !needle ? [] : team.members.filter((member) => !takenEmails.has(member.email.toLowerCase()) && matchesQuery(needle, member.email, member.name)).map((member) => ({
		key: member.email,
		name: member.name ?? member.email,
		detail: member.email,
		isTeam: false
	}));
	return [...!teamAlreadyGranted && matchesQuery(needle, team.team.name, "everyone") ? [{
		key: "whole-team",
		name: `Everyone at ${team.team.name}`,
		detail: `${team.members.length} people`,
		isTeam: true
	}] : [], ...teammates];
}
function buildInviteSuggestions({ team, members, people = null, query, max = DEFAULT_MAX_SUGGESTIONS }) {
	const needle = query.trim().toLowerCase();
	const takenEmails = emailSet(members ?? []);
	const teamRows = team ? suggestTeam(team, members ?? [], needle, takenEmails) : [];
	const teamOrProjectEmails = /* @__PURE__ */ new Set([...takenEmails, ...emailSet(team?.members ?? [])]);
	const outsiders = people ? suggestOutsiders(people, needle, teamOrProjectEmails) : [];
	const rows = [...teamRows, ...outsiders];
	const typedRow = rows.find((row) => row.typedEmail);
	if (!typedRow) return rows.slice(0, max);
	return [...rows.filter((row) => row !== typedRow).slice(0, Math.max(max - 1, 0)), typedRow].slice(0, max);
}
//#endregion
//#region src/projects/project-activity.ts
const PROJECT_ACTIVITY_FILTERS = [
	{
		label: "All",
		value: "all"
	},
	{
		label: "Chats",
		value: "chats"
	},
	{
		label: "Notes",
		value: "meetings"
	}
];
const MATCHES = {
	all: () => true,
	chats: (item) => item.type === "chat",
	meetings: (item) => item.type === "meeting"
};
function filterProjectActivity({ items, filter, search }) {
	const needle = search.trim().toLowerCase();
	return items.filter(MATCHES[filter]).filter((item) => !needle || item.title.toLowerCase().includes(needle) || (item.preview ?? "").toLowerCase().includes(needle)).sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}
//#endregion
//#region src/projects/project-files.ts
const PROJECT_FILE_EXTENSIONS = [
	"pdf",
	"docx",
	"doc",
	"pptx",
	"ppt",
	"xlsx",
	"xls",
	"txt",
	"csv",
	"md"
];
const PROJECT_FILE_MIME_TYPES = [
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.presentationml.presentation",
	"application/vnd.ms-powerpoint",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/vnd.ms-excel",
	"text/plain",
	"text/csv",
	"text/markdown"
];
const MAX_PROJECT_FILES = 20;
const MAX_PROJECT_FILE_SIZE_MB = 30;
const MAX_PROJECT_FILE_SIZE_BYTES = 31457280;
/** `null` means the file reached a good terminal state; show its summary instead. */
function getProjectFileStatus(file) {
	if (file.status === "failed") return {
		label: "Couldn’t upload this file",
		tone: "danger",
		spinning: false
	};
	switch (file.ingestionStatus) {
		case "ready": return null;
		case "processing": return {
			label: "Processing file…",
			tone: "muted",
			spinning: true
		};
		case "failed": return {
			label: "Couldn’t process this file",
			tone: "danger",
			spinning: false
		};
		case "unsupported": return {
			label: "No readable text found",
			tone: "warning",
			spinning: false
		};
		default: return {
			label: "Queued…",
			tone: "muted",
			spinning: true
		};
	}
}
function validateProjectFileSelection({ selected, existingCount }) {
	if (selected.length > 20 - existingCount) return {
		accepted: [],
		errors: [`You can only upload up to 20 files per project.`],
		limitReached: true
	};
	const accepted = [];
	const errors = [];
	for (const file of selected) {
		if (file.size > 31457280) {
			errors.push(`${file.name} exceeds the 30MB limit.`);
			continue;
		}
		accepted.push(file);
	}
	return {
		accepted,
		errors,
		limitReached: false
	};
}
//#endregion
//#region src/projects/share-copy.ts
const PROJECT_MEMBER_ROLE_LABELS = {
	owner: "Owner",
	admin: "Admin",
	member: "Member"
};
/** What each role can do, shown under the role name in role pickers. */
const PROJECT_MEMBER_ROLE_DESCRIPTIONS = {
	owner: "Can also delete the project",
	admin: "Can also invite and remove people",
	member: "Can add files and edit the project"
};
function mapShareError(error) {
	if (error instanceof Error && error.message && !/^HTTP \d/.test(error.message)) return error.message;
	return "Something went wrong. Please try again.";
}
function projectMemberRowCopy(member, currentEmail) {
	return {
		name: member.displayName ?? member.email ?? "Unknown",
		isYou: !!member.email && !!currentEmail && member.email.toLowerCase() === currentEmail.toLowerCase(),
		detail: member.teamId ? "Whole team" : member.userId === null ? "Invited" : member.displayName && member.email ? member.email : null
	};
}
function mapJoinProjectError(error) {
	const status = error?.status;
	if (status === 404) return "This link is no longer valid";
	if (status === 403) return "This link is only for people in the owner's team";
	return "Something went wrong. Please try again.";
}
function invitedByLine(inviterName) {
	return inviterName ? `${inviterName} invited you to join the project` : "You have been invited to join the project";
}
function sharedDetailUnavailableMessage(kind) {
	return `This ${kind} is no longer available.`;
}
//#endregion
//#region src/projects/shared-content.ts
function sortByRecency(items) {
	return [...items].sort((left, right) => {
		const diff = new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
		if (diff !== 0) return diff;
		if (left.id === right.id) return 0;
		return left.id > right.id ? -1 : 1;
	});
}
function mergeSharedContent({ ownItems, remoteItems, projectId, localProjectId }) {
	const ownIds = new Set(ownItems.map((item) => item.id));
	const otherItems = remoteItems.filter((item) => {
		if (ownIds.has(item.id)) return false;
		const localId = localProjectId(item.id);
		return localId === void 0 || localId === projectId;
	});
	return sortByRecency([...ownItems, ...otherItems]);
}
function isOwnSharedContentItem(item, userId) {
	return item.authorUserId === userId;
}
//#endregion
//#region src/projects/shared-item-author.ts
function sharedItemAuthorLabel(item) {
	return item.authorName?.trim() || item.authorEmail?.trim() || "Unknown";
}
function shouldShowSharedItemAuthors({ hasShareLink, memberCount, items, currentUserId }) {
	if (hasShareLink) return true;
	if (memberCount !== null && memberCount > 1) return true;
	if (currentUserId === null) return false;
	return items.some((item) => item.authorUserId !== currentUserId);
}
//#endregion
//#region src/projects/use-join-project.ts
function redirectExistingMember(latest, projectId, isCancelled) {
	latest.current.refreshCatalog().catch(() => void 0).then(() => {
		if (!isCancelled()) latest.current.navigateToProject(projectId);
	});
}
function useJoinProject({ token, fetchPreview, redeem, refreshCatalog, navigateToProject }) {
	const [preview, setPreview] = useState(null);
	const [error, setError] = useState(null);
	const [isJoining, setIsJoining] = useState(false);
	const [isLoadingPreview, setIsLoadingPreview] = useState(false);
	const latest = useRef({
		fetchPreview,
		refreshCatalog,
		navigateToProject
	});
	latest.current = {
		fetchPreview,
		refreshCatalog,
		navigateToProject
	};
	useEffect(() => {
		setPreview(null);
		setError(token ? null : "Invalid invite link: missing token");
		if (!token) return;
		let cancelled = false;
		setIsLoadingPreview(true);
		latest.current.fetchPreview(token).then((next) => {
			if (cancelled) return;
			if (next.isMember) {
				redirectExistingMember(latest, next.projectId, () => cancelled);
				return;
			}
			setPreview(next);
		}).catch((previewError) => {
			if (!cancelled) setError(mapJoinProjectError(previewError));
		}).finally(() => {
			if (!cancelled) setIsLoadingPreview(false);
		});
		return () => {
			cancelled = true;
		};
	}, [token]);
	const join = async () => {
		if (!token) return;
		setIsJoining(true);
		try {
			const member = await redeem(token);
			if (!member.projectId) {
				setError("Something went wrong. Please try again.");
				return;
			}
			await latest.current.refreshCatalog();
			latest.current.navigateToProject(member.projectId);
		} catch (joinError) {
			setError(mapJoinProjectError(joinError));
		} finally {
			setIsJoining(false);
		}
	};
	return {
		preview,
		error,
		isJoining,
		isLoadingPreview,
		join
	};
}
//#endregion
//#region src/projects/use-project-share-link.ts
function useProjectShareLink({ isOpen, canManageLink, adapter }) {
	const [token, setToken] = useState(null);
	const [isMinting, setIsMinting] = useState(false);
	const [isRevoking, setIsRevoking] = useState(false);
	const beginOperation = useLatestOperation();
	useEffect(() => {
		if (!isOpen || !canManageLink) return;
		let cancelled = false;
		const isLatest = beginOperation();
		adapter.fetchToken().then((existingToken) => {
			if (!cancelled && isLatest()) setToken(existingToken);
		}).catch((error) => {
			if (!cancelled && isLatest()) adapter.onError?.(error, "fetch");
		});
		return () => {
			cancelled = true;
		};
	}, [
		adapter,
		beginOperation,
		canManageLink,
		isOpen
	]);
	return {
		token,
		mint: useCallback(async () => {
			setIsMinting(true);
			const isLatest = beginOperation();
			try {
				const newToken = await adapter.mintToken();
				if (isLatest()) setToken(newToken);
				await adapter.refreshCatalog?.();
				return newToken;
			} catch (error) {
				adapter.onError?.(error, "mint");
				return;
			} finally {
				setIsMinting(false);
			}
		}, [adapter, beginOperation]),
		revoke: useCallback(async () => {
			setIsRevoking(true);
			const isLatest = beginOperation();
			try {
				await adapter.revokeToken();
				if (isLatest()) setToken(null);
				await adapter.refreshCatalog?.();
			} catch (error) {
				adapter.onError?.(error, "revoke");
			} finally {
				setIsRevoking(false);
			}
		}, [adapter, beginOperation]),
		isMinting,
		isRevoking
	};
}
//#endregion
//#region src/projects/use-shared-detail.ts
function useSharedProjectDetail({ fetchDetail, kind, projectId, contentId, onError }) {
	const [detail, setDetail] = useState(null);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	useEffect(() => {
		let cancelled = false;
		setIsLoading(true);
		setError(null);
		setDetail(null);
		fetchDetail().then((next) => {
			if (!cancelled) setDetail(next);
		}).catch((err) => {
			onError?.(err);
			if (!cancelled) setError(sharedDetailUnavailableMessage(kind));
		}).finally(() => {
			if (!cancelled) setIsLoading(false);
		});
		return () => {
			cancelled = true;
		};
	}, [
		contentId,
		fetchDetail,
		kind,
		onError,
		projectId
	]);
	return {
		detail,
		error,
		isLoading
	};
}
//#endregion
//#region src/use-paged-load.ts
function usePagedLoad({ enabled, canLoadMore, loadPage, onError }) {
	const loadingRef = useRef(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	return {
		runLoadMore: useCallback(async () => {
			if (!enabled || !canLoadMore || loadingRef.current) return;
			loadingRef.current = true;
			setIsLoadingMore(true);
			try {
				await loadPage();
			} catch (error) {
				if (onError) onError(error);
				else console.error("Failed to load page", error);
			} finally {
				loadingRef.current = false;
				setIsLoadingMore(false);
			}
		}, [
			enabled,
			canLoadMore,
			loadPage,
			onError
		]),
		isLoadingMore
	};
}
//#endregion
export { ADVANCED_MONTH_DAYS, AnalyticsEvents, DEFAULT_ADVANCED_SCHEDULE, DEFAULT_ROUTINE_INTERVAL_COUNT, DEFAULT_ROUTINE_INTERVAL_UNIT, DEFAULT_ROUTINE_SCHEDULE, DEFAULT_SHARE_STATUS, EDIT_TOOL_TYPES, EMPTY_PROJECT_CAPABILITIES, EVERY_MONTH, EmailDraftSchema, FEEDBACK_CONSENT_LABEL_DOWNVOTE, FEEDBACK_CONSENT_LABEL_UPVOTE, FREE_TIER_INTEGRATION_TOOLKITS, FeedbackDraftSchema, GREETING_LINE_COUNT, IMPROVE_ROUTINE_PROMPT, ImageGenerationEvents, MAX_PROJECT_FILES, MAX_PROJECT_FILE_SIZE_BYTES, MAX_PROJECT_FILE_SIZE_MB, MESSAGING_DELIVERY_OFF, MIN_ROUTINE_INTERVAL_MINUTES, MONTH_DAYS, MONTH_LABELS, PROJECT_ACTIVITY_FILTERS, PROJECT_FILE_EXTENSIONS, PROJECT_FILE_MIME_TYPES, PROJECT_MEMBER_ROLE_DESCRIPTIONS, PROJECT_MEMBER_ROLE_LABELS, PeriodicCron, REPORTS_GENERATING_REFRESH_INTERVAL_MS, REPORTS_REFRESH_INTERVAL_MS, REPORT_TEXT_GENERATING, REPORT_TEXT_NO_DATA, ROUTINES_REFRESH_INTERVAL_MS, ROUTINE_AUTO_PAUSE_RESUME_CONFIRMATION, ROUTINE_FREQUENCY_OPTIONS, ROUTINE_INTERVAL_COUNTS, ROUTINE_INTERVAL_UNITS, ROUTINE_MESSAGING_CHANNELS, ROUTINE_MESSAGING_CHANNEL_LABELS, ROUTINE_REMOVE_TEAM_TEMPLATE_CONFIRMATION, ROUTINE_SOURCE_DEFAULTS, ReasoningStatus, ReasoningStepType, WEEKDAYS_FREQUENCY, WEEKDAY_LABELS, WEEKDAY_ONLY_CODES, WEEK_DAYS, WEEK_DAY_CODES, WORKING_WORDS, adaptReportToRoutineRun, adaptRoutineToViewModel, backendLabelOf, buildAdvancedRecurrenceRule, buildConsumedToolCallLookup, buildInviteLinkCopyText, buildInviteSuggestions, buildLittlebirdDraftPrompt, buildMailtoUrlFromDraft, buildMeetingCircles, buildMeetingNotesSendContext, buildMeetingPrepEventPayload, buildProjectJoinUrl, buildReasoningDisplayItems, buildReferralShareUrl, buildRoutineDeliveryOptions, buildRoutineFormRecurrenceRule, buildRoutineRecurrenceRule, buildRoutineRunChatPrompt, buildRoutineUpsertInput, buildSendDraftFromLine, buildSharedArenaUrl, buildSharedChatUrl, buildSharedMeetingUrl, buildSharedSiteUrl, buildToolCallByStepIdMap, circleMemberEmails, classifyEmailDraftError, collectUpcomingMeetingIds, computeLastInResponseSequenceIds, copyShareLink, correlateToolCalls, dedupeRoutineSuggestions, deriveLoaderActivity, deviceCount, deviceWord, devicesWord, displayReferralShareUrl, draftToEditForm, editFormToUpdatePayload, enrichReasoningStepsWithToolCalls, extractLatestTodoWriteItems, extractToolName, filterDisplayableModalities, filterProjectActivity, filterRoutineViewModels, findLatestCompletedRoutineRun, findLinkedActionCardIds, formatCallIntegrationLabel, formatEmailDraftContent, formatGreeting, formatImageGenQuotaResetCountdown, formatMonthDayOrdinal, formatPaywallSubtitle, formatRelativeDate, formatRoutineInterval, formatRoutineIntervalEnd, formatRoutineScheduleSummary, formatRoutineWeekDays, formatTimezone, formatUserWaitTitle, fromRoutineDateTimeValue, getDomain, getEditToolChips, getIntegrationDisplayName, getIntegrationToolName, getMeetingBucketDate, getMeetingGroupLabel, getMeetingSortDate, getModalityDescription, getModalitySemanticIdentity, getModalityTitle, getProjectFileStatus, getReasoningStatus, getResumeConfirmation, getRoutineActionState, getRunUserWait, getStaticTodoWriteItems, getStepTitle, getTensed, getTodoWriteSummary, getTurnActivityLabel, getUserContextChipGroups, getWebSearchQueries, getWorkingWord, groupMeetingsByDate, hasRecipient, hashSendContext, hashString, humanizeMcpToolName, integrationNameFromSlug, integrationStatusPriority, invitedByLine, isCanonicalRecurrenceRule, isContentOldEnough, isCopyableEmailBlock, isFreeTierIntegrationToolkit, isInCircle, isInternalEmail, isMcpIdentifier, isMeaningfulThought, isMoreFrequentThanDaily, isNativeSchemeUrl, isOpenableUrl, isOwnSharedContentItem, isPartialConnectionFlowRef, isRoutineDueOn, isRoutineRunUnread, isRoutineScheduleDueOn, isRunWaiting, isSelfToolExecute, isTrustedUrl, isValidRecipient, isValidRoutineTime, mapJoinProjectError, mapShareError, mapToolNameToLabel, mergeRecipients, mergeSharedContent, minOccurrenceGapMs, modalityHasExpandableContent, nextGreetingRotationAt, nextRoutineOccurrence, normalizeIntegrationName, normalizeRecipient, normalizeRoutineTitle, normalizeToolkitKey, opensTurn, otherAttendeeEmails, parseActionCardUrl, parseAdvancedRecurrenceRule, parseBackendName, parseConnectionCardUrl, parseEmailDraft, parseFeedbackDraftUrl, parseFileLinkUrl, parseGmailQuery, parseMcpSlug, parseRecipients, parseRoutineMessagingChannels, parseRoutineRecurrenceRule, parseRoutineSchedule, parseSendDraftUrl, parseWebhookEndpointSetupUrl, pickReferralShareEmail, pickReferralShareMessage, platformName, projectMemberRowCopy, rebuildEditedDraft, replaceSubjectValue, resolveGreetingIndex, routineIntervalCounts, routineIntervalMinutes, selectReasoningDisplayEntries, selectReasoningDisplaySteps, selectRoutineRun, selectRunIdAfterDeletion, sharedDetailUnavailableMessage, sharedItemAuthorLabel, shouldGateIntegrationConnect, shouldGateMCPConnect, shouldShowSharedItemAuthors, sortByRecency, stripIntegrationPrefix, suggestionToRoutineTemplate, summarizeApplyPatch, syncAdvancedTime, toRoutineDateTimeValue, toRoutineIsoString, toRoutineScheduleParts, toSendFeedbackDraftBody, toolMatchKeyForCorrelatableModality, trimBoundaryNewlines, useInitialRoutineDraft, useJoinProject, useLatestOperation, useMaxModeFallback, usePagedLoad, useProjectShareLink, useRollingEntry, useRoutineFormDraft, useRoutineRefresh, useShareLink, useSharedProjectDetail, validateProjectFileSelection, validateRecurrenceRule, withCircleHidden, withoutHiddenCircles };

//# sourceMappingURL=index.js.map